import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Beef as BeefIcon,
  Drumstick,
  Egg,
  Fish,
  Flame,
  Leaf,
  LeafyGreen,
  Milk,
  Nut,
  PiggyBank,
  Star,
  Wheat,
  WheatOff,
} from 'lucide-react';
import QRCode from 'qrcode';
import GordonMenuTools from './GordonMenuTools';
import { isPdfMenuSource, uploadMenuSourceFile } from './gordonMenuSources';
import PhotoAssetBrowser from '../PhotoAssetBrowser';
import SelectedAssetPreview from '../SelectedAssetPreview';
import { isImageAsset, resolveThumbKind } from '../assetUtils';
import { useUploadQueue } from '../../../context/UploadQueueContext';
import { getUserRoleFromToken } from '../../../utils/jwt';
import { getMenuTagI18nPack } from './menuTagI18n';
import { groupSectionsByHeading, moveSectionWithinHeading, dropSectionWithinHeading, resolveUsefulDropPosition } from './sectionOrdering';

const makeDraftId = () => `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const CATALOG_MOVE_TYPES = [
  { value: 'menu', label: 'Menu' },
  { value: 'products', label: 'Products' },
  { value: 'services', label: 'Services' },
];

const getCatalogConfig = (catalogType) => {
  const type = String(catalogType || '').toLowerCase();
  if (type === 'products') {
    return {
      catalogType: 'products',
      displayName: 'Products',
      shortName: 'Products',
      description: 'Create a per-area products catalog with flexible sections and item photos.',
      apiBase: '/api/catalogs/products',
      landingPath: '/lp/pr/',
      unitLabel: 'Location',
      unitPlural: 'Locations',
      unitParam: 'loc',
      unitLabelParam: 'llabel',
    };
  }
  if (type === 'services') {
    return {
      catalogType: 'services',
      displayName: 'Services',
      shortName: 'Services',
      description: 'Create a per-area services catalog with flexible sections and item photos.',
      apiBase: '/api/catalogs/services',
      landingPath: '/lp/s/',
      unitLabel: 'Location',
      unitPlural: 'Locations',
      unitParam: 'loc',
      unitLabelParam: 'llabel',
    };
  }
  return {
    catalogType: 'menu',
    displayName: 'Menu Items',
    shortName: 'Menu',
    description: 'Create a per-area menu with flexible sections and item photos.',
    apiBase: '/api/catalogs/menu',
    landingPath: '/lp/m/',
    unitLabel: 'Table',
    unitPlural: 'Tables',
    unitParam: 'table',
    unitLabelParam: 'tlabel',
  };
};

const normalizeId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
};

const MONETISATION_MODE_OPTIONS = [
  { value: 'off', label: 'Off', description: 'No automatic landing page monetisation.' },
  { value: 'subtle', label: 'Subtle', description: 'Light inline monetisation and low-density Network Ads.' },
  { value: 'standard', label: 'Standard', description: 'Inline links, activity blocks, and Network Ads.' },
  { value: 'full', label: 'Full', description: 'Apply the complete monetisation mix.' },
  { value: 'custom', label: 'Custom', description: 'Pick the exact monetisation types for this landing page.' },
];
const CATALOG_MONETISATION_TYPE_OPTIONS = [
  {
    value: 'landing_inline_links',
    label: 'Inline Links',
    description: 'Short inline recommendations inserted into the page content.',
  },
  {
    value: 'landing_activity_blocks',
    label: 'Activity Blocks',
    description: 'Promotional activity cards and inserted promo blocks.',
  },
  {
    value: 'landing_city_widgets',
    label: 'City Widgets',
    description: 'Nearby destination widgets and the full travel partner layer.',
  },
  {
    value: 'landing_network_ads',
    label: 'Network Ads',
    description: 'Administrator-approved advertiser campaigns placed in platform inventory.',
  },
];
const CATALOG_MONETISATION_TYPE_ID_SET = new Set(
  CATALOG_MONETISATION_TYPE_OPTIONS.map((option) => option.value)
);
const CATALOG_MONETISATION_TYPES_BY_MODE = {
  off: [],
  subtle: ['landing_inline_links', 'landing_network_ads'],
  standard: ['landing_inline_links', 'landing_activity_blocks', 'landing_network_ads'],
  full: [...CATALOG_MONETISATION_TYPE_ID_SET],
};
const CATALOG_PROMOTION_LABELS = Object.freeze({
  landing_inline_links: 'Inline Recommendations',
  landing_activity_blocks: 'Activity Hub Promotions',
  landing_city_widgets: 'Partner City Widgets',
  landing_network_ads: 'Network Ads',
});
const CATALOG_PROMOTION_STYLE_DEFAULTS = Object.freeze({
  landing_inline_links: { emphasis: 'standard', accentColor: '#14b8a6' },
  landing_activity_blocks: { emphasis: 'standard', accentColor: '#a855f7' },
  landing_city_widgets: { emphasis: 'standard', accentColor: '#3b82f6' },
  landing_network_ads: { emphasis: 'standard', accentColor: '#f59e0b' },
  socials: { emphasis: 'standard', accentColor: '#06b6d4' },
});
const CATALOG_PROMOTION_EMPHASIS_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'standard', label: 'Standard' },
  { value: 'prominent', label: 'Prominent' },
];
const CATALOG_LOGO_SIZE_OPTIONS = [
  { value: 'normal', label: 'Normal', description: 'Current default logo size.' },
  { value: 'large', label: 'Large', description: 'Gives the logo more presence without taking over the page.' },
  { value: 'full', label: 'Full Width', description: 'Expands the logo to the full content width, like the photos below.' },
];
const CATALOG_PHOTO_DISPLAY_OPTIONS = [
  { value: 'fill', label: 'Fill Frame', description: 'Default. Fills the fixed-height frame and may crop the image.' },
  { value: 'double', label: 'Double Height', description: 'Uses the same cropped fill style, but doubles the photo height for more impact.' },
  { value: 'fit', label: 'Fit Whole Photo', description: 'Keeps the same height, shows the full image, and adds a soft backdrop behind it.' },
];
const DEFAULT_CATALOG_BACKGROUND_COLOR = '#0b1220';
const DEFAULT_CATALOG_SURFACE_COLOR = '#0f172a';
const DEFAULT_CATALOG_TEXT_COLOR = '#e2e8f0';
const DEFAULT_CATALOG_SECONDARY_SURFACE_COLOR = DEFAULT_CATALOG_SURFACE_COLOR;
const DEFAULT_CATALOG_TERTIARY_SURFACE_COLOR = DEFAULT_CATALOG_SURFACE_COLOR;
const DEFAULT_CATALOG_HEADING_COLOR = DEFAULT_CATALOG_TEXT_COLOR;
const DEFAULT_CATALOG_LANDING_HEADER_TEXT = 'Digital Menu';
const DEFAULT_CATALOG_CAMPAIGN_SECTION_KICKER_LABEL = 'Advertisement';
const DEFAULT_CATALOG_SOCIAL_SECTION_TITLE = 'Find Us Online';
const DEFAULT_CATALOG_SOCIAL_SECTION_DESCRIPTION = 'Choose your favourite way to keep up with new posts, directions, playlists, questions, or updates.';
const CATALOG_SOCIAL_SECTION_KEY = 'catalog-socials-section';
const DEFAULT_LANGUAGE_DISPLAY_ORDER = Object.freeze(['en', 'vi', 'ko', 'zh', 'ru', 'fr', 'es']);
const DEFAULT_SOCIAL_SUGGESTED_LANGUAGE_CODES = Object.freeze(['vi', 'ru', 'ko']);
const MENU_ITEM_TAG_OPTIONS = Object.freeze([
  { key: 'beef', label: 'Beef', chipClass: 'border-amber-700/70 bg-amber-950/40 text-amber-100' },
  { key: 'pork', label: 'Pork', chipClass: 'border-rose-700/70 bg-rose-950/40 text-rose-100' },
  { key: 'chicken', label: 'Chicken', chipClass: 'border-orange-700/70 bg-orange-950/40 text-orange-100' },
  { key: 'duck', label: 'Duck', chipClass: 'border-orange-800/70 bg-orange-950/50 text-orange-100' },
  { key: 'poultry', label: 'Poultry', chipClass: 'border-orange-700/70 bg-orange-950/40 text-orange-100' },
  { key: 'fish', label: 'Fish', chipClass: 'border-cyan-700/70 bg-cyan-950/40 text-cyan-100' },
  { key: 'shrimp', label: 'Shrimp', chipClass: 'border-teal-700/70 bg-teal-950/40 text-teal-100' },
  { key: 'squid', label: 'Squid', chipClass: 'border-sky-700/70 bg-sky-950/40 text-sky-100' },
  { key: 'crab', label: 'Crab', chipClass: 'border-blue-700/70 bg-blue-950/40 text-blue-100' },
  { key: 'seafood', label: 'Seafood', chipClass: 'border-cyan-700/70 bg-cyan-950/40 text-cyan-100' },
  { key: 'vegetarian', label: 'Vegetarian', chipClass: 'border-lime-700/70 bg-lime-950/40 text-lime-100' },
  { key: 'vegan', label: 'Vegan', chipClass: 'border-emerald-700/70 bg-emerald-950/40 text-emerald-100' },
  { key: 'bestseller', label: 'Best Seller', chipClass: 'border-amber-500/70 bg-amber-950/50 text-amber-100' },
  { key: 'nuts', label: 'Nuts', chipClass: 'border-yellow-700/70 bg-yellow-950/40 text-yellow-100' },
  { key: 'dairy', label: 'Dairy', chipClass: 'border-sky-700/70 bg-sky-950/40 text-sky-100' },
  { key: 'eggs', label: 'Eggs', chipClass: 'border-stone-700/70 bg-stone-950/40 text-stone-100' },
  { key: 'gluten', label: 'Contains Gluten', chipClass: 'border-violet-700/70 bg-violet-950/40 text-violet-100' },
  { key: 'gluten_free', label: 'Gluten Free', chipClass: 'border-emerald-700/70 bg-emerald-950/40 text-emerald-100' },
]);
const MENU_ITEM_TAG_OPTION_MAP = new Map(MENU_ITEM_TAG_OPTIONS.map((option) => [option.key, option]));
const MENU_ITEM_TAG_KEYS = Object.freeze(MENU_ITEM_TAG_OPTIONS.map((option) => option.key));
const DEFAULT_MENU_TAG_ICON_SET = 'emoji';
const MENU_TAG_ICON_SET_OPTIONS = Object.freeze([
  {
    key: 'emoji',
    label: 'Emoji',
    description: 'The current badge style using emoji icons.',
  },
  {
    key: 'outline',
    label: 'Outline',
    description: 'Line icons inside round badges.',
  },
  {
    key: 'tile',
    label: 'Tile',
    description: 'Line icons inside rounded-square badges.',
  },
  {
    key: 'mono',
    label: 'Initials',
    description: 'Compact text monograms for a cleaner look.',
  },
  {
    key: 'custom',
    label: 'Custom',
    description: 'Upload your own icon artwork for each menu badge.',
  },
]);
const MENU_ITEM_TAG_ALIASES = Object.freeze({
  best_seller: 'bestseller',
  bestseller: 'bestseller',
  glutenfree: 'gluten_free',
});
const MENU_ITEM_TAG_ICON_META = Object.freeze({
  beef: { emoji: '🐄', shortLabel: 'BF', Icon: BeefIcon },
  pork: { emoji: '🐖', shortLabel: 'PK', Icon: PiggyBank },
  chicken: { emoji: '🍗', shortLabel: 'CHK', Icon: Drumstick },
  duck: { emoji: '🦆', shortLabel: 'DK', Icon: Drumstick },
  poultry: { emoji: '🐓', shortLabel: 'PT', Icon: Drumstick },
  fish: { emoji: '🐟', shortLabel: 'FSH', Icon: Fish },
  shrimp: { emoji: '🦐', shortLabel: 'SHP', Icon: Fish },
  squid: { emoji: '🦑', shortLabel: 'SQD', Icon: Fish },
  crab: { emoji: '🦀', shortLabel: 'CRB', Icon: Fish },
  seafood: { emoji: '🐟', shortLabel: 'SF', Icon: Fish },
  vegetarian: { emoji: '🥬', shortLabel: 'VEG', Icon: Leaf },
  vegan: { emoji: '🌿', shortLabel: 'VGN', Icon: LeafyGreen },
  bestseller: { emoji: '⭐', shortLabel: 'TOP', Icon: Star },
  nuts: { emoji: '🥜', shortLabel: 'NT', Icon: Nut },
  dairy: { emoji: '🥛', shortLabel: 'DY', Icon: Milk },
  eggs: { emoji: '🥚', shortLabel: 'EG', Icon: Egg },
  gluten: { emoji: '🌾', shortLabel: 'GL', Icon: Wheat },
  gluten_free: { emoji: 'GF', shortLabel: 'GF', Icon: WheatOff },
});
const MENU_ITEM_SPICINESS_OPTIONS = Object.freeze([
  { key: '', label: 'Not Set', chipClass: 'border-gray-700 bg-gray-900/60 text-gray-300' },
  { key: 'mild', label: 'Mild', chipClass: 'border-amber-700/70 bg-amber-950/40 text-amber-100' },
  { key: 'medium', label: 'Medium', chipClass: 'border-orange-700/70 bg-orange-950/40 text-orange-100' },
  { key: 'hot', label: 'Hot', chipClass: 'border-red-700/70 bg-red-950/40 text-red-100' },
]);
const MENU_ITEM_SPICINESS_OPTION_MAP = new Map(MENU_ITEM_SPICINESS_OPTIONS.map((option) => [option.key, option]));
const MENU_ITEM_SPICINESS_ICON_META = Object.freeze({
  mild: { emoji: '🌶', shortLabel: 'M', Icon: Flame },
  medium: { emoji: '🌶🌶', shortLabel: 'MED', Icon: Flame },
  hot: { emoji: '🌶🌶🌶', shortLabel: 'HOT', Icon: Flame },
});
const MENU_NEGATIVE_FILTER_LABELS = Object.freeze({
  beef: 'No Beef',
  pork: 'No Pork',
  chicken: 'No Chicken',
  duck: 'No Duck',
  poultry: 'No Poultry',
  fish: 'No Fish',
  shrimp: 'No Shrimp',
  squid: 'No Squid',
  crab: 'No Crab',
  seafood: 'No Seafood',
  vegetarian: 'No Vegetarian',
  vegan: 'No Vegan',
  bestseller: 'Not Best Seller',
  nuts: 'No Nuts',
  dairy: 'No Dairy',
  eggs: 'No Eggs',
  gluten: 'No Gluten',
  gluten_free: 'Not Gluten Free',
});
const MENU_NEGATIVE_ONLY_FILTER_TAGS = new Set([
  'nuts',
  'dairy',
  'eggs',
  'gluten',
  'chicken',
  'duck',
  'fish',
  'shrimp',
  'squid',
  'crab',
  'seafood',
]);
const MENU_AGGREGATE_FILTER_TAG_SOURCES = Object.freeze({
  poultry: Object.freeze(['poultry', 'chicken', 'duck']),
  seafood: Object.freeze(['seafood', 'fish', 'shrimp', 'squid', 'crab']),
});
const MENU_FILTER_TAG_PREFERENCE = new Map([
  'bestseller',
  'vegetarian',
  'vegan',
  'gluten_free',
  'beef',
  'pork',
  'chicken',
  'duck',
  'poultry',
  'fish',
  'shrimp',
  'squid',
  'crab',
  'seafood',
  'nuts',
  'dairy',
  'eggs',
  'gluten',
].map((key, index) => [key, index]));
const MENU_FILTER_SPICINESS_ORDER = ['mild', 'medium', 'hot'];
const MENU_FILTER_VARIANTS = Object.freeze([
  ...MENU_ITEM_TAG_OPTIONS.flatMap((option) => ([
    {
      key: option.key,
      label: option.label,
      tag: option.key,
      variant: 'positive',
      groupKey: option.key,
    },
    {
      key: `hide-${option.key}`,
      label: MENU_NEGATIVE_FILTER_LABELS[option.key] || `No ${option.label}`,
      tag: option.key,
      variant: 'negative',
      groupKey: option.key,
    },
  ])),
  ...MENU_ITEM_SPICINESS_OPTIONS
    .filter((option) => option.key)
    .map((option) => ({
      key: `spiciness-${option.key}`,
      label: option.label,
      spiciness: option.key,
      variant: 'spiciness',
      groupKey: `spiciness:${option.key}`,
    })),
]);
const MENU_FILTER_VARIANT_BY_KEY = new Map(MENU_FILTER_VARIANTS.map((option) => [option.key, option]));
const compareMenuFilterVariantOptions = (optionA, optionB) => {
  const safeA = optionA || {};
  const safeB = optionB || {};
  if (safeA.variant === 'spiciness' || safeB.variant === 'spiciness') {
    if (safeA.variant !== safeB.variant) return safeA.variant === 'spiciness' ? 1 : -1;
    const spiceOrderA = MENU_FILTER_SPICINESS_ORDER.indexOf(safeA.spiciness);
    const spiceOrderB = MENU_FILTER_SPICINESS_ORDER.indexOf(safeB.spiciness);
    if (spiceOrderA !== spiceOrderB) return spiceOrderA - spiceOrderB;
  } else {
    const tagOrderA = MENU_FILTER_TAG_PREFERENCE.has(safeA.tag) ? MENU_FILTER_TAG_PREFERENCE.get(safeA.tag) : Number.MAX_SAFE_INTEGER;
    const tagOrderB = MENU_FILTER_TAG_PREFERENCE.has(safeB.tag) ? MENU_FILTER_TAG_PREFERENCE.get(safeB.tag) : Number.MAX_SAFE_INTEGER;
    if (tagOrderA !== tagOrderB) return tagOrderA - tagOrderB;
  }
  const variantOrderA = safeA.variant === 'positive' ? 0 : safeA.variant === 'negative' ? 1 : 2;
  const variantOrderB = safeB.variant === 'positive' ? 0 : safeB.variant === 'negative' ? 1 : 2;
  if (variantOrderA !== variantOrderB) return variantOrderA - variantOrderB;
  return String(safeA.label || safeA.key || '').localeCompare(String(safeB.label || safeB.key || ''));
};
const MENU_CUSTOM_ICON_MAX_BYTES = 64 * 1024;
const MENU_CUSTOM_ICON_ALLOWED_PREFIXES = ['data:image/', 'https://', 'http://', '/'];
const MENU_CUSTOM_ICON_TARGETS = Object.freeze([
  ...MENU_ITEM_TAG_OPTIONS.map((option) => ({
    key: option.key,
    label: option.label,
    chipClass: option.chipClass,
    tag: option.key,
    spiciness: '',
  })),
  ...MENU_ITEM_SPICINESS_OPTIONS
    .filter((option) => option.key)
    .map((option) => ({
      key: `spiciness:${option.key}`,
      label: option.label,
      chipClass: option.chipClass,
      tag: '',
      spiciness: option.key,
    })),
]);
const MENU_CUSTOM_ICON_TARGET_KEY_SET = new Set(MENU_CUSTOM_ICON_TARGETS.map((target) => target.key));
const MENU_IMPORT_TEXT_MIME_TYPES = new Set([
  'application/csv',
  'application/json',
  'application/vnd.ms-excel',
  'application/xml',
  'text/csv',
  'text/html',
  'text/markdown',
  'text/plain',
  'text/tab-separated-values',
  'text/xml',
]);
const MENU_IMPORT_TEXT_FILE_EXTENSIONS = new Set([
  '.csv',
  '.htm',
  '.html',
  '.json',
  '.md',
  '.txt',
  '.tsv',
  '.xml',
  '.yaml',
  '.yml',
]);

const getLocalizedMenuTagLabel = (tagKey, language = 'en') => {
  const normalizedTagKey = normalizeMenuItemTag(tagKey);
  if (!normalizedTagKey) return String(tagKey || '').trim();
  const pack = getMenuTagI18nPack(language);
  return pack.tags?.[normalizedTagKey] || MENU_ITEM_TAG_OPTION_MAP.get(normalizedTagKey)?.label || normalizedTagKey;
};

const getLocalizedMenuNegativeFilterLabel = (tagKey, language = 'en') => {
  const normalizedTagKey = normalizeMenuItemTag(tagKey);
  if (!normalizedTagKey) return String(tagKey || '').trim();
  const pack = getMenuTagI18nPack(language);
  return pack.negativeFilters?.[normalizedTagKey]
    || MENU_NEGATIVE_FILTER_LABELS[normalizedTagKey]
    || `No ${getLocalizedMenuTagLabel(normalizedTagKey, language)}`;
};

const getLocalizedMenuSpicinessLabel = (spicinessKey, language = 'en') => {
  const normalizedSpiciness = normalizeMenuItemSpiciness(spicinessKey);
  if (!normalizedSpiciness) return String(spicinessKey || '').trim();
  const pack = getMenuTagI18nPack(language);
  return pack.spiciness?.[normalizedSpiciness]
    || MENU_ITEM_SPICINESS_OPTION_MAP.get(normalizedSpiciness)?.label
    || normalizedSpiciness;
};

const buildLocalizedMenuTagOptions = (language = 'en') => (
  MENU_ITEM_TAG_OPTIONS.map((option) => ({
    ...option,
    label: getLocalizedMenuTagLabel(option.key, language),
  }))
);

const buildLocalizedMenuSpicinessOptions = (language = 'en') => (
  MENU_ITEM_SPICINESS_OPTIONS.map((option) => ({
    ...option,
    label: option.key ? getLocalizedMenuSpicinessLabel(option.key, language) : option.label,
  }))
);

const buildLocalizedMenuFilterVariants = (language = 'en') => ([
  ...buildLocalizedMenuTagOptions(language).flatMap((option) => ([
    {
      key: option.key,
      label: option.label,
      tag: option.key,
      variant: 'positive',
      groupKey: option.key,
    },
    {
      key: `hide-${option.key}`,
      label: getLocalizedMenuNegativeFilterLabel(option.key, language),
      tag: option.key,
      variant: 'negative',
      groupKey: option.key,
    },
  ])),
  ...buildLocalizedMenuSpicinessOptions(language)
    .filter((option) => option.key)
    .map((option) => ({
      key: `spiciness-${option.key}`,
      label: option.label,
      spiciness: option.key,
      variant: 'spiciness',
      groupKey: `spiciness:${option.key}`,
    })),
]);

const buildLocalizedMenuCustomIconTargets = (language = 'en') => ([
  ...buildLocalizedMenuTagOptions(language).map((option) => ({
    key: option.key,
    label: option.label,
    chipClass: option.chipClass,
    tag: option.key,
    spiciness: '',
  })),
  ...buildLocalizedMenuSpicinessOptions(language)
    .filter((option) => option.key)
    .map((option) => ({
      key: `spiciness:${option.key}`,
      label: option.label,
      chipClass: option.chipClass,
      tag: '',
      spiciness: option.key,
    })),
]);
const MENU_IMPORT_FILE_ACCEPT = [
  'application/pdf',
  '.pdf',
  'image/*',
  '.csv',
  '.htm',
  '.html',
  '.json',
  '.md',
  '.txt',
  '.tsv',
  '.xml',
  '.yaml',
  '.yml',
  'text/plain',
  'text/csv',
  'text/tab-separated-values',
  'text/html',
  'application/json',
  'application/xml',
].join(',');
const CATALOG_SOCIAL_LINK_OPTIONS = Object.freeze([
  {
    key: 'google_maps',
    label: 'Google Maps',
    fields: ['google_url', 'googleMapsUrl'],
    ctaLabel: 'Find us on Google Maps',
    accentClass: 'from-emerald-400/25 via-cyan-400/10 to-sky-400/20',
    iconLabel: 'GM',
  },
  {
    key: 'email',
    label: 'Email',
    fields: ['contact_email', 'contactEmail'],
    ctaLabel: 'Email us',
    accentClass: 'from-teal-400/25 via-cyan-400/10 to-sky-400/20',
    iconLabel: '@',
    valueType: 'email',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    fields: ['facebook_url', 'facebookUrl'],
    ctaLabel: 'Like us on Facebook',
    accentClass: 'from-blue-500/25 via-indigo-500/10 to-sky-400/20',
    iconLabel: 'f',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    fields: ['instagram_url', 'instagramUrl'],
    ctaLabel: 'Follow us on Instagram',
    accentClass: 'from-fuchsia-500/25 via-rose-500/10 to-orange-400/20',
    iconLabel: 'IG',
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    fields: ['tiktok_url', 'tiktokUrl'],
    ctaLabel: 'Follow us on TikTok',
    accentClass: 'from-slate-200/10 via-cyan-400/10 to-pink-500/20',
    iconLabel: 'TT',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    fields: ['youtube_url', 'youtubeUrl'],
    ctaLabel: 'Watch us on YouTube',
    accentClass: 'from-red-500/25 via-rose-500/10 to-orange-400/20',
    iconLabel: 'YT',
  },
  {
    key: 'twitter',
    label: 'X',
    fields: ['twitter_url', 'twitterUrl'],
    ctaLabel: 'Follow us on X',
    accentClass: 'from-slate-300/15 via-slate-400/5 to-slate-500/20',
    iconLabel: 'X',
  },
  {
    key: 'tripadvisor',
    label: 'TripAdvisor',
    fields: ['tripadvisor_url', 'tripadvisorUrl'],
    ctaLabel: 'Find us on TripAdvisor',
    accentClass: 'from-emerald-400/25 via-lime-400/10 to-teal-400/20',
    iconLabel: 'TA',
  },
  {
    key: 'pinterest',
    label: 'Pinterest',
    fields: ['pinterest_url', 'pinterestUrl'],
    ctaLabel: 'Browse us on Pinterest',
    accentClass: 'from-red-500/25 via-rose-500/10 to-pink-400/20',
    iconLabel: 'P',
  },
  {
    key: 'spotify',
    label: 'Spotify',
    fields: ['spotify_url', 'spotifyUrl'],
    ctaLabel: 'Listen on Spotify',
    accentClass: 'from-emerald-400/25 via-green-400/10 to-lime-400/20',
    iconLabel: 'S',
  },
  {
    key: 'soundcloud',
    label: 'SoundCloud',
    fields: ['soundcloud_url', 'soundcloudUrl'],
    ctaLabel: 'Listen on SoundCloud',
    accentClass: 'from-orange-400/25 via-amber-400/10 to-red-400/20',
    iconLabel: 'SC',
  },
  {
    key: 'mixcloud',
    label: 'Mixcloud',
    fields: ['mixcloud_url', 'mixcloudUrl'],
    ctaLabel: 'Listen on Mixcloud',
    accentClass: 'from-violet-500/25 via-indigo-500/10 to-sky-400/20',
    iconLabel: 'MC',
  },
  {
    key: 'apple_music',
    label: 'Apple Music',
    fields: ['apple_music_url', 'appleMusicUrl'],
    ctaLabel: 'Listen on Apple Music',
    accentClass: 'from-rose-500/25 via-pink-500/10 to-red-400/20',
    iconLabel: 'AM',
  },
]);
const CATALOG_SOCIAL_LINK_OPTION_BY_KEY = new Map(
  CATALOG_SOCIAL_LINK_OPTIONS.map((option) => [option.key, option])
);
const CATALOG_SOCIAL_HEADER_POSITION_OPTIONS = Object.freeze([
  {
    value: 'centered',
    label: 'Centered',
    description: 'Center the socials and language controls together, with language on the right.',
  },
  {
    value: 'right_left',
    label: 'Right / Left',
    description: 'Keep language on the right and socials on the left.',
  },
  {
    value: 'left_right',
    label: 'Left / Right',
    description: 'Keep language on the left and socials on the right.',
  },
]);

const normalizeMonetisationMode = (value, fallback = 'off') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (MONETISATION_MODE_OPTIONS.some((option) => option.value === normalized)) return normalized;
  return fallback;
};

const normalizeMonetisationTypeIds = (value) => {
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? (() => {
        const trimmed = value.trim();
        if (!trimmed) return [];
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : [trimmed];
        } catch (err) {
          return [trimmed];
        }
      })()
      : [];

  return [...new Set(
    source
      .map((entry) => String(entry || '').trim().toLowerCase())
      .filter((entry) => CATALOG_MONETISATION_TYPE_ID_SET.has(entry))
  )];
};

const getDefaultMonetisationTypeIdsForMode = (mode) => {
  const normalizedMode = normalizeMonetisationMode(mode, 'off');
  if (normalizedMode === 'custom') return [];
  return normalizeMonetisationTypeIds(CATALOG_MONETISATION_TYPES_BY_MODE[normalizedMode] || []);
};

const normalizeCatalogLogoSize = (value, fallback = 'normal') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (CATALOG_LOGO_SIZE_OPTIONS.some((option) => option.value === normalized)) return normalized;
  return fallback;
};

const normalizeCatalogPhotoDisplay = (value, fallback = 'fill') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (CATALOG_PHOTO_DISPLAY_OPTIONS.some((option) => option.value === normalized)) return normalized;
  return fallback;
};

const normalizeHexColor = (value, fallback = DEFAULT_CATALOG_BACKGROUND_COLOR) => {
  const normalized = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(normalized)) return normalized.toLowerCase();
  return fallback;
};

const normalizeCatalogContentMode = (value) => (
  String(value || '').trim().toLowerCase() === 'shared' ? 'shared' : 'native'
);

const normalizeSectionNameKey = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
);

const normalizeCatalogSocialLinkKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return CATALOG_SOCIAL_LINK_OPTIONS.some((option) => option.key === normalized) ? normalized : '';
};

const normalizeCatalogSocialHeaderPosition = (value, fallback = 'right_left') => {
  const normalized = String(value || '').trim().toLowerCase();
  return CATALOG_SOCIAL_HEADER_POSITION_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : fallback;
};

const normalizeCatalogSocialSectionText = (
  value,
  {
    fallback = '',
    maxLength = 160,
    allowEmpty = false,
  } = {}
) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return allowEmpty ? '' : fallback;
  return trimmed.slice(0, maxLength);
};

const normalizeCatalogSocialDraftText = (
  value,
  {
    fallback = '',
    maxLength = 160,
  } = {}
) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value !== 'string') return fallback;
  return value.slice(0, maxLength);
};

const getCatalogSocialDefaultCtaLabel = (key, fallback = 'Open link') => {
  const normalizedKey = normalizeCatalogSocialLinkKey(key);
  return CATALOG_SOCIAL_LINK_OPTION_BY_KEY.get(normalizedKey)?.ctaLabel || fallback;
};

const normalizeCatalogSocialCtaLabels = (value, { allowEmpty = false } = {}) => {
  const raw = value && typeof value === 'object' ? value : {};
  const normalized = {};
  CATALOG_SOCIAL_LINK_OPTIONS.forEach((option) => {
    normalized[option.key] = normalizeCatalogSocialSectionText(raw[option.key], {
      fallback: option.ctaLabel,
      maxLength: 120,
      allowEmpty,
    });
  });
  return normalized;
};

const normalizeCatalogSocialCtaLabelsDraft = (value, { allowEmpty = false } = {}) => {
  const raw = value && typeof value === 'object' ? value : {};
  const normalized = {};
  CATALOG_SOCIAL_LINK_OPTIONS.forEach((option) => {
    normalized[option.key] = normalizeCatalogSocialDraftText(raw[option.key], {
      fallback: allowEmpty ? '' : option.ctaLabel,
      maxLength: 120,
    });
  });
  return normalized;
};

const normalizeCatalogLanguageCode = (value, fallback = '') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const getCatalogLanguageKey = (value) => normalizeCatalogLanguageCode(value).toLowerCase();

const sameCatalogLanguageCode = (left, right) => {
  const normalizedLeft = getCatalogLanguageKey(left);
  const normalizedRight = getCatalogLanguageKey(right);
  return Boolean(normalizedLeft) && normalizedLeft === normalizedRight;
};

const normalizeCatalogSocialTranslationRowDraft = (value, sourceLanguageCode = 'en') => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const languageCode = normalizeCatalogLanguageCode(value.languageCode || value.language_code);
  if (!languageCode || sameCatalogLanguageCode(languageCode, sourceLanguageCode)) return null;
  const ctaLabelsRaw = value.ctaLabels && typeof value.ctaLabels === 'object'
    ? value.ctaLabels
    : (value.cta_labels && typeof value.cta_labels === 'object' ? value.cta_labels : {});
  return {
    languageCode,
    title: normalizeCatalogSocialDraftText(value.title, {
      fallback: '',
      maxLength: 120,
    }),
    description: normalizeCatalogSocialDraftText(value.description, {
      fallback: '',
      maxLength: 240,
    }),
    ctaLabels: normalizeCatalogSocialCtaLabelsDraft(ctaLabelsRaw, { allowEmpty: true }),
  };
};

const normalizeCatalogSocialTranslationsDraft = (value, sourceLanguageCode = 'en') => {
  const rows = Array.isArray(value) ? value : [];
  const seen = new Set();
  return rows.map((row) => {
    const normalizedRow = normalizeCatalogSocialTranslationRowDraft(row, sourceLanguageCode);
    if (!normalizedRow) return null;
    const languageKey = getCatalogLanguageKey(normalizedRow.languageCode);
    if (!languageKey || seen.has(languageKey)) return null;
    seen.add(languageKey);
    return normalizedRow;
  }).filter(Boolean).sort((left, right) => compareLanguageCodesForDisplay(left.languageCode, right.languageCode));
};

const catalogSocialDraftFieldNeedsTranslation = (sourceValue, translatedValue, { maxLength = 160 } = {}) => {
  const normalizedSource = normalizeCatalogSocialDraftText(sourceValue, {
    fallback: '',
    maxLength,
  }).trim();
  if (!normalizedSource) return false;
  const normalizedTranslated = normalizeCatalogSocialDraftText(translatedValue, {
    fallback: '',
    maxLength,
  }).trim();
  if (!normalizedTranslated) return true;
  return normalizedTranslated === normalizedSource;
};

const resolveCatalogSocialEntryTitle = (entry, socialSection = null) => {
  const normalizedKey = normalizeCatalogSocialLinkKey(entry?.key);
  return normalizeCatalogSocialSectionText(socialSection?.ctaLabels?.[normalizedKey], {
    fallback: getCatalogSocialDefaultCtaLabel(
      normalizedKey,
      String(entry?.ctaLabel || entry?.label || 'Open link')
    ),
    maxLength: 120,
    allowEmpty: false,
  });
};

const normalizeMenuItemKey = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
);

const normalizeMenuItemTag = (value) => {
  const normalized = normalizeMenuItemKey(value);
  if (MENU_ITEM_TAG_OPTION_MAP.has(normalized)) return normalized;
  if (Object.prototype.hasOwnProperty.call(MENU_ITEM_TAG_ALIASES, normalized)) {
    return MENU_ITEM_TAG_ALIASES[normalized];
  }
  const compact = normalized.replace(/_/g, '');
  return Object.prototype.hasOwnProperty.call(MENU_ITEM_TAG_ALIASES, compact)
    ? MENU_ITEM_TAG_ALIASES[compact]
    : '';
};

const normalizeMenuItemTags = (value) => {
  const safe = Array.isArray(value) ? value : [];
  return Array.from(new Set(safe.map((entry) => normalizeMenuItemTag(entry)).filter(Boolean)));
};

const normalizeMenuEnabledTagKeys = (value, fallback = MENU_ITEM_TAG_KEYS) => {
  if (!Array.isArray(value)) return Array.isArray(fallback) ? [...fallback] : [...MENU_ITEM_TAG_KEYS];
  return Array.from(new Set(value.map((entry) => normalizeMenuItemTag(entry)).filter(Boolean)));
};

const resolveMenuEnabledTagSet = (enabledTagKeys = MENU_ITEM_TAG_KEYS) => (
  enabledTagKeys instanceof Set
    ? enabledTagKeys
    : new Set(normalizeMenuEnabledTagKeys(enabledTagKeys, MENU_ITEM_TAG_KEYS))
);

const getMenuAggregateFilterSourceTags = (tagKey = '') => {
  const normalizedTagKey = normalizeMenuItemTag(tagKey);
  if (!normalizedTagKey) return [];
  return Array.isArray(MENU_AGGREGATE_FILTER_TAG_SOURCES[normalizedTagKey])
    ? [...MENU_AGGREGATE_FILTER_TAG_SOURCES[normalizedTagKey]]
    : [normalizedTagKey];
};

const isMenuFilterTagAvailable = (tagKey = '', enabledTagKeys = MENU_ITEM_TAG_KEYS) => {
  const normalizedTagKey = normalizeMenuItemTag(tagKey);
  if (!normalizedTagKey) return false;
  if (Object.prototype.hasOwnProperty.call(MENU_AGGREGATE_FILTER_TAG_SOURCES, normalizedTagKey)) return true;
  return resolveMenuEnabledTagSet(enabledTagKeys).has(normalizedTagKey);
};

const menuItemMatchesTagKey = (tagKey = '', tags = []) => {
  const normalizedTags = normalizeMenuItemTags(tags);
  const sourceTags = getMenuAggregateFilterSourceTags(tagKey);
  return sourceTags.some((sourceTag) => normalizedTags.includes(sourceTag));
};

const filterMenuItemTagsByEnabledTags = (tags = [], enabledTagKeys = MENU_ITEM_TAG_KEYS) => {
  const enabledTagSet = resolveMenuEnabledTagSet(enabledTagKeys);
  return normalizeMenuItemTags(tags).filter((tag) => enabledTagSet.has(tag));
};

const normalizeMenuItemSpiciness = (value) => {
  const normalized = normalizeMenuItemKey(value);
  if (!normalized) return '';
  if (normalized === 'mild' || normalized === 'medium' || normalized === 'hot') return normalized;
  if (
    normalized === 'spicy'
    || normalized === 'extra_hot'
    || normalized === 'very_hot'
    || normalized === 'extra_spicy'
    || normalized === 'very_spicy'
  ) return 'hot';
  return '';
};

const getFileExtension = (filename) => {
  const raw = String(filename || '').trim().toLowerCase();
  const index = raw.lastIndexOf('.');
  if (index < 0) return '';
  return raw.slice(index);
};

const isMenuImportImageFile = (file) => String(file?.type || '').toLowerCase().startsWith('image/');

const isMenuImportTextFile = (file) => {
  const mimeType = String(file?.type || '').trim().toLowerCase();
  if (mimeType.startsWith('text/')) return true;
  if (MENU_IMPORT_TEXT_MIME_TYPES.has(mimeType)) return true;
  return MENU_IMPORT_TEXT_FILE_EXTENSIONS.has(getFileExtension(file?.name || ''));
};

const buildMenuImportTextEntry = ({ text, title = '', filename = '', mimeType = '' } = {}) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return null;
  const normalizedTitle = String(title || filename || 'Text import').trim();
  return {
    title: normalizedTitle || 'Text import',
    filename: String(filename || '').trim(),
    mimeType: String(mimeType || '').trim(),
    text: normalizedText,
  };
};

const createCanvasForImage = ({ width, height }) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(Number(width) || 1));
  canvas.height = Math.max(1, Math.round(Number(height) || 1));
  return canvas;
};

const canvasToWebpBlob = (canvas, quality = 0.9) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
      return;
    }
    reject(new Error('The browser could not convert the clipboard image to WebP.'));
  }, 'image/webp', quality);
});

const convertImageBlobToWebpFile = async (blob, filename = '') => {
  const safeFilename = String(filename || '').trim() || `pasted-photo-${Date.now()}.webp`;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Clipboard image conversion is not available in this environment.');
  }

  if (typeof window.createImageBitmap === 'function') {
    const bitmap = await window.createImageBitmap(blob);
    try {
      const canvas = createCanvasForImage({ width: bitmap.width, height: bitmap.height });
      const context = canvas.getContext('2d');
      if (!context) throw new Error('The browser could not prepare the pasted image for upload.');
      context.drawImage(bitmap, 0, 0);
      const webpBlob = await canvasToWebpBlob(canvas);
      return new File([webpBlob], safeFilename, { type: 'image/webp' });
    } finally {
      if (typeof bitmap.close === 'function') bitmap.close();
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error('The pasted image could not be read.'));
      nextImage.src = objectUrl;
    });
    const canvas = createCanvasForImage({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
    const context = canvas.getContext('2d');
    if (!context) throw new Error('The browser could not prepare the pasted image for upload.');
    context.drawImage(image, 0, 0);
    const webpBlob = await canvasToWebpBlob(canvas);
    return new File([webpBlob], safeFilename, { type: 'image/webp' });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const normalizeImmediateUploadAsset = (asset) => {
  if (!asset?.id) return null;
  const scd = asset?.scd && typeof asset.scd === 'object' ? asset.scd : {};
  const files = Array.isArray(scd.files) ? scd.files : [];
  return {
    id: asset.id,
    type: asset.type || 'upload',
    title: scd.title || `Asset ${asset.id}`,
    files,
  };
};

const uploadImageAssetFile = async ({ siteId, file, title = '', folder = '' }) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Please log in again.');
  if (!siteId) throw new Error('Select a site first.');
  if (!(file instanceof File)) throw new Error('Missing image file.');

  const presignRes = await fetch(
    `/api/uploads/presign?kind=image&filename=${encodeURIComponent(file.name)}&siteId=${encodeURIComponent(siteId)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!presignRes.ok) {
    const body = await presignRes.text().catch(() => '');
    throw new Error(body || `Failed to get upload URL (${presignRes.status})`);
  }
  const { url, key } = await presignRes.json().catch(() => ({}));
  if (!url || !key) throw new Error('Invalid upload URL response.');

  const putRes = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) {
    const body = await putRes.text().catch(() => '');
    throw new Error(body || `Upload failed (${putRes.status})`);
  }

  const finalizeRes = await fetch('/api/uploads/finalize', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      filename: file.name,
      fileType: file.type,
      key,
      siteId,
      title,
      folder: folder || null,
    }),
  });
  if (!finalizeRes.ok) {
    const body = await finalizeRes.text().catch(() => '');
    throw new Error(body || `Finalize failed (${finalizeRes.status})`);
  }

  const payload = await finalizeRes.json().catch(() => ({}));
  const asset = normalizeImmediateUploadAsset(payload?.asset || null);
  if (!asset) throw new Error('Upload completed but no asset was returned.');
  return asset;
};

const normalizeCatalogSocialSection = (value) => {
  const raw = value && typeof value === 'object' ? value : {};
  const linkKeys = Array.from(new Set(
    (Array.isArray(raw.linkKeys) ? raw.linkKeys : [])
      .map((entry) => normalizeCatalogSocialLinkKey(entry))
      .filter(Boolean)
  ));
  const hasExplicitHeaderLinkKeys = (
    Object.prototype.hasOwnProperty.call(raw, 'headerLinkKeys')
    || Object.prototype.hasOwnProperty.call(raw, 'header_link_keys')
  );
  const headerLinkSource = hasExplicitHeaderLinkKeys
    ? (Array.isArray(raw.headerLinkKeys)
      ? raw.headerLinkKeys
      : (Array.isArray(raw.header_link_keys) ? raw.header_link_keys : []))
    : (Boolean(raw.showInHeader ?? raw.show_in_header) ? linkKeys : []);
  const headerLinkKeys = Array.from(new Set(
    headerLinkSource
      .map((entry) => normalizeCatalogSocialLinkKey(entry))
      .filter(Boolean)
  ));
  const ctaLabelsRaw = raw.ctaLabels && typeof raw.ctaLabels === 'object'
    ? raw.ctaLabels
    : (raw.cta_labels && typeof raw.cta_labels === 'object' ? raw.cta_labels : {});
  const sourceLanguageCode = normalizeCatalogLanguageCode(
    raw.sourceLanguageCode || raw.source_language_code,
    'en'
  );
  const parsedSortOrder = Number(raw.sortOrder ?? raw.sort_order);
  return {
    enabled: Boolean(raw.enabled),
    title: normalizeCatalogSocialSectionText(raw.title, {
      fallback: DEFAULT_CATALOG_SOCIAL_SECTION_TITLE,
      maxLength: 120,
      allowEmpty: true,
    }),
    description: normalizeCatalogSocialSectionText(raw.description, {
      fallback: DEFAULT_CATALOG_SOCIAL_SECTION_DESCRIPTION,
      maxLength: 240,
      allowEmpty: true,
    }),
    ctaLabels: normalizeCatalogSocialCtaLabels(ctaLabelsRaw, { allowEmpty: true }),
    linkKeys,
    headerLinkKeys,
    showInHeader: Boolean(raw.showInHeader ?? raw.show_in_header),
    headerPosition: normalizeCatalogSocialHeaderPosition(raw.headerPosition ?? raw.header_position),
    sortOrder: Number.isFinite(parsedSortOrder) && parsedSortOrder > 0 ? Math.trunc(parsedSortOrder) : null,
    sourceLanguageCode,
    translations: normalizeCatalogSocialTranslationsDraft(
      raw.translations || raw.languageVersions || raw.language_versions,
      sourceLanguageCode
    ),
  };
};

const normalizeCatalogSocialSectionDraft = (value) => {
  const raw = value && typeof value === 'object' ? value : {};
  const linkKeys = Array.from(new Set(
    (Array.isArray(raw.linkKeys) ? raw.linkKeys : [])
      .map((entry) => normalizeCatalogSocialLinkKey(entry))
      .filter(Boolean)
  ));
  const hasExplicitHeaderLinkKeys = (
    Object.prototype.hasOwnProperty.call(raw, 'headerLinkKeys')
    || Object.prototype.hasOwnProperty.call(raw, 'header_link_keys')
  );
  const headerLinkSource = hasExplicitHeaderLinkKeys
    ? (Array.isArray(raw.headerLinkKeys)
      ? raw.headerLinkKeys
      : (Array.isArray(raw.header_link_keys) ? raw.header_link_keys : []))
    : (Boolean(raw.showInHeader ?? raw.show_in_header) ? linkKeys : []);
  const headerLinkKeys = Array.from(new Set(
    headerLinkSource
      .map((entry) => normalizeCatalogSocialLinkKey(entry))
      .filter(Boolean)
  ));
  const ctaLabelsRaw = raw.ctaLabels && typeof raw.ctaLabels === 'object'
    ? raw.ctaLabels
    : (raw.cta_labels && typeof raw.cta_labels === 'object' ? raw.cta_labels : {});
  const sourceLanguageCode = normalizeCatalogLanguageCode(
    raw.sourceLanguageCode || raw.source_language_code,
    'en'
  );
  const parsedSortOrder = Number(raw.sortOrder ?? raw.sort_order);
  return {
    enabled: Boolean(raw.enabled),
    title: normalizeCatalogSocialDraftText(raw.title, {
      fallback: DEFAULT_CATALOG_SOCIAL_SECTION_TITLE,
      maxLength: 120,
    }),
    description: normalizeCatalogSocialDraftText(raw.description, {
      fallback: DEFAULT_CATALOG_SOCIAL_SECTION_DESCRIPTION,
      maxLength: 240,
    }),
    ctaLabels: normalizeCatalogSocialCtaLabelsDraft(ctaLabelsRaw),
    linkKeys,
    headerLinkKeys,
    showInHeader: Boolean(raw.showInHeader ?? raw.show_in_header),
    headerPosition: normalizeCatalogSocialHeaderPosition(raw.headerPosition ?? raw.header_position),
    sortOrder: Number.isFinite(parsedSortOrder) && parsedSortOrder > 0 ? Math.trunc(parsedSortOrder) : null,
    sourceLanguageCode,
    translations: normalizeCatalogSocialTranslationsDraft(
      raw.translations || raw.languageVersions || raw.language_versions,
      sourceLanguageCode
    ),
  };
};

const resolveCatalogSocialSortOrder = (socialSection, sectionCount = 0) => {
  const maxPosition = Math.max(1, Number(sectionCount || 0) + 1);
  const requested = Number(socialSection?.sortOrder ?? socialSection?.sort_order);
  if (!Number.isFinite(requested) || requested <= 0) return maxPosition;
  return Math.max(1, Math.min(maxPosition, Math.trunc(requested)));
};

const readSiteField = (site, fields = []) => {
  const safeFields = Array.isArray(fields) ? fields : [];
  for (const field of safeFields) {
    const value = String(site?.[field] || '').trim();
    if (value) return value;
  }
  return '';
};

const formatExternalHost = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^mailto:/i.test(raw)) return raw.replace(/^mailto:/i, '').trim();
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return raw;
  try {
    return new URL(raw).hostname.replace(/^www\./i, '');
  } catch (err) {
    try {
      return new URL(`https://${raw.replace(/^\/+/, '')}`).hostname.replace(/^www\./i, '');
    } catch (_) {
      return raw;
    }
  }
};

const normalizeCatalogSocialHref = (option, value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (String(option?.valueType || '').trim().toLowerCase() === 'email') {
    const email = raw.replace(/^mailto:/i, '').trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? `mailto:${email}` : '';
  }
  return raw;
};

const buildAvailableCatalogSocialEntries = (site) => (
  CATALOG_SOCIAL_LINK_OPTIONS
    .map((option) => {
      const href = normalizeCatalogSocialHref(option, readSiteField(site, option.fields));
      if (!href) return null;
      return {
        ...option,
        href,
        hostLabel: formatExternalHost(href),
      };
    })
    .filter(Boolean)
);

const resolveSiteLabel = (site) => String(site?.name || site?.siteName || site?.site_name || '').trim();
const resolveAreaLabel = (area) => String(area?.name || area?.areaName || area?.area_name || '').trim();

const resolveCatalogTitleFallback = () => DEFAULT_CATALOG_LANDING_HEADER_TEXT;

const resolveCatalogSubtitleFallback = () => DEFAULT_CATALOG_LANDING_HEADER_TEXT;

const buildSourceCatalogOptionLabel = (sourceCatalog) => {
  const siteName = resolveSiteLabel(sourceCatalog);
  const areaName = resolveAreaLabel(sourceCatalog);
  const title = String(sourceCatalog?.title || '').trim();
  const parts = [siteName, areaName];
  if (title && title !== siteName) parts.push(title);
  return parts.filter(Boolean).join(' • ');
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const normalizeMenuTagIconSet = (value, fallback = DEFAULT_MENU_TAG_ICON_SET) => {
  const normalized = String(value || '').trim().toLowerCase();
  return MENU_TAG_ICON_SET_OPTIONS.some((option) => option.key === normalized) ? normalized : fallback;
};

const getMenuCustomIconKey = ({ tag = '', spiciness = '' } = {}) => {
  const normalizedTag = normalizeMenuItemTag(tag);
  if (normalizedTag) return normalizedTag;
  const normalizedSpiciness = normalizeMenuItemSpiciness(spiciness);
  return normalizedSpiciness ? `spiciness:${normalizedSpiciness}` : '';
};

const normalizeMenuCustomIconUrl = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 800000) return '';
  return MENU_CUSTOM_ICON_ALLOWED_PREFIXES.some((prefix) => trimmed.startsWith(prefix)) ? trimmed : '';
};

const normalizeMenuCustomIconMap = (value) => {
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return Object.fromEntries(
    Object.entries(raw)
      .map(([key, iconUrl]) => [String(key || '').trim(), normalizeMenuCustomIconUrl(iconUrl)])
      .filter(([key, iconUrl]) => MENU_CUSTOM_ICON_TARGET_KEY_SET.has(key) && iconUrl)
  );
};

const normalizeMenuFilterKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return MENU_FILTER_VARIANT_BY_KEY.has(normalized) ? normalized : '';
};

const normalizeMenuFilterKeys = (value) => {
  const source = Array.isArray(value) ? value : [];
  return Array.from(new Set(source.map((entry) => normalizeMenuFilterKey(entry)).filter(Boolean)));
};

const filterMenuFilterKeysByEnabledTags = (value, enabledTagKeys = MENU_ITEM_TAG_KEYS) => {
  const enabledTagSet = resolveMenuEnabledTagSet(enabledTagKeys);
  return normalizeMenuFilterKeys(value).filter((filterKey) => {
    const filterOption = MENU_FILTER_VARIANT_BY_KEY.get(filterKey);
    if (!filterOption) return false;
    if (filterOption.variant === 'spiciness') return true;
    return isMenuFilterTagAvailable(filterOption.tag, enabledTagSet);
  });
};

const hasExplicitMenuFilterKeys = (menuFeaturesRaw = {}) => (
  hasOwn(menuFeaturesRaw, 'enabledFilterKeys') || hasOwn(menuFeaturesRaw, 'enabled_filter_keys')
);

const hasExplicitMenuEnabledTagKeys = (menuFeaturesRaw = {}) => (
  hasOwn(menuFeaturesRaw, 'enabledTagKeys') || hasOwn(menuFeaturesRaw, 'enabled_tag_keys')
);

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

const resolveMenuEnabledTagKeys = (menuFeaturesRaw = {}) => {
  if (hasExplicitMenuEnabledTagKeys(menuFeaturesRaw)) {
    return normalizeMenuEnabledTagKeys(menuFeaturesRaw.enabledTagKeys ?? menuFeaturesRaw.enabled_tag_keys, []);
  }
  return [...MENU_ITEM_TAG_KEYS];
};

const catalogHasConfiguredMenuBadges = (items = [], enabledTagKeys = MENU_ITEM_TAG_KEYS) => (
  (Array.isArray(items) ? items : []).some((item) => (
    filterMenuItemTagsByEnabledTags(item?.tags, enabledTagKeys).length
      || Boolean(String(item?.spiciness || '').trim())
  ))
);

const resolveMenuShowItemTags = (menuFeaturesRaw = {}, items = [], enabledTagKeys = MENU_ITEM_TAG_KEYS) => {
  if (hasOwn(menuFeaturesRaw, 'showItemTags') || hasOwn(menuFeaturesRaw, 'show_item_tags')) {
    return Boolean(menuFeaturesRaw.showItemTags ?? menuFeaturesRaw.show_item_tags);
  }
  return catalogHasConfiguredMenuBadges(items, enabledTagKeys);
};

const menuFilterMatchesItem = (filterKey, { tags = [], spiciness = '' } = {}) => {
  const option = MENU_FILTER_VARIANT_BY_KEY.get(String(filterKey || '').trim().toLowerCase());
  if (!option) return false;
  const safeTags = normalizeMenuItemTags(tags);
  const normalizedSpiciness = normalizeMenuItemSpiciness(spiciness);
  if (option.variant === 'spiciness') {
    return normalizedSpiciness === option.spiciness;
  }
  if (option.variant === 'positive') {
    if (option.tag === 'vegetarian') {
      return safeTags.includes('vegetarian') || safeTags.includes('vegan');
    }
    return menuItemMatchesTagKey(option.tag, safeTags);
  }
  if (option.tag === 'vegetarian') {
    return !safeTags.includes('vegetarian') && !safeTags.includes('vegan');
  }
  return !menuItemMatchesTagKey(option.tag, safeTags);
};

const buildDefaultMenuFilterKeys = (items = [], enabledTagKeys = MENU_ITEM_TAG_KEYS) => {
  const enabledTagSet = resolveMenuEnabledTagSet(enabledTagKeys);
  const tagCounts = new Map();
  const spicinessCounts = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const normalizedItemTags = filterMenuItemTagsByEnabledTags(item?.tags, enabledTagKeys);
    normalizedItemTags.forEach((tag) => {
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
    });
    Object.keys(MENU_AGGREGATE_FILTER_TAG_SOURCES).forEach((aggregateTag) => {
      if (!normalizedItemTags.includes(aggregateTag) && menuItemMatchesTagKey(aggregateTag, item?.tags)) {
        tagCounts.set(aggregateTag, (tagCounts.get(aggregateTag) || 0) + 1);
      }
    });
    const normalizedSpiciness = normalizeMenuItemSpiciness(item?.spiciness);
    if (normalizedSpiciness) {
      spicinessCounts.set(normalizedSpiciness, (spicinessCounts.get(normalizedSpiciness) || 0) + 1);
    }
  });

  const positiveKeys = Array.from(tagCounts.keys())
    .filter((tag) => !MENU_NEGATIVE_ONLY_FILTER_TAGS.has(tag))
    .sort((a, b) => {
      const orderA = MENU_FILTER_TAG_PREFERENCE.has(a) ? MENU_FILTER_TAG_PREFERENCE.get(a) : Number.MAX_SAFE_INTEGER;
      const orderB = MENU_FILTER_TAG_PREFERENCE.has(b) ? MENU_FILTER_TAG_PREFERENCE.get(b) : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      const labelA = MENU_ITEM_TAG_OPTION_MAP.get(a)?.label || a;
      const labelB = MENU_ITEM_TAG_OPTION_MAP.get(b)?.label || b;
      return labelA.localeCompare(labelB);
    })
    .map((tag) => tag);

  const spicinessKeys = MENU_FILTER_SPICINESS_ORDER
    .filter((level) => (spicinessCounts.get(level) || 0) > 0)
    .map((level) => `spiciness-${level}`);

  const negativeKeys = MENU_ITEM_TAG_OPTIONS
    .map((option) => option.key)
    .filter((tag) => isMenuFilterTagAvailable(tag, enabledTagSet) && MENU_NEGATIVE_ONLY_FILTER_TAGS.has(tag) && (tagCounts.get(tag) || 0) > 0)
    .map((tag) => `hide-${tag}`);

  return [...positiveKeys, ...spicinessKeys, ...negativeKeys];
};

const resolveMenuFilterKeys = (menuFeaturesRaw = {}, items = [], enabledTagKeys = MENU_ITEM_TAG_KEYS) => {
  if (hasExplicitMenuFilterKeys(menuFeaturesRaw)) {
    return filterMenuFilterKeysByEnabledTags(
      menuFeaturesRaw.enabledFilterKeys ?? menuFeaturesRaw.enabled_filter_keys,
      enabledTagKeys
    );
  }
  return buildDefaultMenuFilterKeys(items, enabledTagKeys);
};

const compareLanguageCodesForDisplay = (a, b) => {
  const normalizedA = String(a || '').trim().toLowerCase();
  const normalizedB = String(b || '').trim().toLowerCase();
  const baseA = normalizedA.split('-')[0];
  const baseB = normalizedB.split('-')[0];
  const indexA = DEFAULT_LANGUAGE_DISPLAY_ORDER.indexOf(baseA);
  const indexB = DEFAULT_LANGUAGE_DISPLAY_ORDER.indexOf(baseB);
  if (indexA !== -1 || indexB !== -1) {
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    if (indexA !== indexB) return indexA - indexB;
  }
  return normalizedA.localeCompare(normalizedB);
};

const resolveCatalogDisplaySettingsDraft = ({ catalog, site, area, catalogConfig, items = [] }) => {
  const raw = catalog?.displaySettings && typeof catalog.displaySettings === 'object'
    ? catalog.displaySettings
    : {};
  const menuFeaturesRaw = raw.menuFeatures && typeof raw.menuFeatures === 'object'
    ? raw.menuFeatures
    : {};
  const enabledTagKeys = resolveMenuEnabledTagKeys(menuFeaturesRaw);
  const fallbackTitle = resolveCatalogTitleFallback({ catalog, site, catalogConfig });
  const fallbackSubtitle = resolveCatalogSubtitleFallback({ site, area });
  const resolvedTitle = raw.headerTitle === null || raw.headerTitle === undefined
    ? fallbackTitle
    : String(raw.headerTitle);
  const resolvedSubtitle = raw.headerSubtitle === null || raw.headerSubtitle === undefined
    ? fallbackSubtitle
    : String(raw.headerSubtitle);
  const primarySurfaceColor = normalizeHexColor(raw.surfaceColor ?? raw.surface_color, DEFAULT_CATALOG_SURFACE_COLOR);
  const bodyTextColor = normalizeHexColor(raw.textColor ?? raw.text_color, DEFAULT_CATALOG_TEXT_COLOR);
  const promotionPositionsRaw = raw.promotionPositions && typeof raw.promotionPositions === 'object'
    ? raw.promotionPositions
    : {};
  const promotionStylesRaw = raw.promotionStyles && typeof raw.promotionStyles === 'object'
    ? raw.promotionStyles
    : {};
  return {
    headerTitle: resolvedTitle,
    headerSubtitle: resolvedSubtitle,
    logoSize: normalizeCatalogLogoSize(raw.logoSize, 'normal'),
    backgroundColor: normalizeHexColor(raw.backgroundColor ?? raw.background_color, DEFAULT_CATALOG_BACKGROUND_COLOR),
    surfaceColor: primarySurfaceColor,
    secondarySurfaceColor: normalizeHexColor(
      raw.secondarySurfaceColor ?? raw.secondary_surface_color,
      primarySurfaceColor || DEFAULT_CATALOG_SECONDARY_SURFACE_COLOR
    ),
    tertiarySurfaceColor: normalizeHexColor(
      raw.tertiarySurfaceColor ?? raw.tertiary_surface_color,
      primarySurfaceColor || DEFAULT_CATALOG_TERTIARY_SURFACE_COLOR
    ),
    headingColor: normalizeHexColor(raw.headingColor ?? raw.heading_color, bodyTextColor || DEFAULT_CATALOG_HEADING_COLOR),
    textColor: bodyTextColor,
    photoDisplay: normalizeCatalogPhotoDisplay(raw.photoDisplay, 'fill'),
    promotionPositions: Object.fromEntries(
      Object.keys(CATALOG_PROMOTION_LABELS)
        .map((typeId) => [typeId, Number(promotionPositionsRaw[typeId])])
        .filter(([, position]) => Number.isInteger(position) && position > 0 && position <= 500)
    ),
    promotionStyles: Object.fromEntries(
      Object.entries(CATALOG_PROMOTION_STYLE_DEFAULTS).map(([typeId, defaults]) => {
        const style = promotionStylesRaw[typeId] && typeof promotionStylesRaw[typeId] === 'object'
          ? promotionStylesRaw[typeId]
          : {};
        return [typeId, {
          emphasis: ['compact', 'standard', 'prominent'].includes(style.emphasis)
            ? style.emphasis
            : defaults.emphasis,
          accentColor: normalizeHexColor(style.accentColor ?? style.accent_color, defaults.accentColor),
        }];
      })
    ),
    socialSection: normalizeCatalogSocialSection(raw.socialSection),
    menuFeatures: {
      showItemTags: resolveMenuShowItemTags(menuFeaturesRaw, items, enabledTagKeys),
      enabledTagKeys,
      enabledFilterKeys: resolveMenuFilterKeys(menuFeaturesRaw, items, enabledTagKeys),
      autoRotatePhotos: Boolean(menuFeaturesRaw.autoRotatePhotos ?? menuFeaturesRaw.auto_rotate_photos),
      tagIconSet: normalizeMenuTagIconSet(menuFeaturesRaw.tagIconSet ?? menuFeaturesRaw.tag_icon_set),
      customIconUrls: normalizeMenuCustomIconMap(menuFeaturesRaw.customIconUrls ?? menuFeaturesRaw.custom_icon_urls),
    },
  };
};

const normalizeCampaignSectionMode = (value, fallback = 'single') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'dynamic') return 'dynamic';
  if (normalized === 'single') return 'single';
  return fallback;
};

const normalizeCampaignSectionKickerLabel = (value, fallback = DEFAULT_CATALOG_CAMPAIGN_SECTION_KICKER_LABEL) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value || '').trim().slice(0, 80);
  return normalized || fallback;
};

const getSectionDraftKey = (section) => {
  if (section?.id) return `section:${section.id}`;
  return `draft:${section?.draftId || ''}`;
};

const getSectionHeaderDraftKey = (sectionHeader) => {
  if (sectionHeader?.id) return `section-header:${sectionHeader.id}`;
  return `section-header-draft:${sectionHeader?.draftId || ''}`;
};

const getCampaignSectionDraftKey = (campaignSection) => {
  if (campaignSection?.id) return `campaign-section:${campaignSection.id}`;
  return `campaign-section-draft:${campaignSection?.draftId || ''}`;
};

const sortSectionsByOrder = (rows = []) => (
  (Array.isArray(rows) ? rows : [])
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
);

const sortSectionHeadersByOrder = (rows = []) => (
  (Array.isArray(rows) ? rows : [])
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
);

const resolveDropPosition = (event) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const midpoint = rect.top + (rect.height / 2);
  return event.clientY >= midpoint ? 'after' : 'before';
};

const resolveEffectiveDropPosition = (
  event,
  list = [],
  sourceKey = '',
  targetKey = '',
  getKey = (value) => value
) => {
  const safeList = Array.isArray(list) ? list : [];
  const normalizedSourceKey = String(sourceKey || '');
  const normalizedTargetKey = String(targetKey || '');
  if (!normalizedSourceKey || !normalizedTargetKey || normalizedSourceKey === normalizedTargetKey) return '';

  const sourceIndex = safeList.findIndex((entry) => getKey(entry) === normalizedSourceKey);
  const targetIndex = safeList.findIndex((entry) => getKey(entry) === normalizedTargetKey);
  if (sourceIndex === -1 || targetIndex === -1) return '';

  return resolveUsefulDropPosition(sourceIndex, targetIndex, resolveDropPosition(event));
};

const reorderListByDrop = (list = [], sourceIndex = -1, targetIndex = -1, position = 'before') => {
  if (!Array.isArray(list) || sourceIndex < 0 || targetIndex < 0) return Array.isArray(list) ? list : [];
  if (sourceIndex === targetIndex && position === 'before') return list;

  const next = list.slice();
  const [moved] = next.splice(sourceIndex, 1);
  if (moved === undefined) return list;

  let insertionIndex = position === 'after' ? targetIndex + 1 : targetIndex;
  if (sourceIndex < targetIndex) insertionIndex -= 1;
  insertionIndex = Math.max(0, Math.min(next.length, insertionIndex));
  next.splice(insertionIndex, 0, moved);
  return next;
};

const reorderListByKeys = (list = [], sourceKey = '', targetKey = '', getKey = (value) => value, position = 'before') => {
  const safeList = Array.isArray(list) ? list : [];
  const sourceIndex = safeList.findIndex((entry) => getKey(entry) === sourceKey);
  const targetIndex = safeList.findIndex((entry) => getKey(entry) === targetKey);
  if (sourceIndex === -1 || targetIndex === -1) return safeList;
  return reorderListByDrop(safeList, sourceIndex, targetIndex, position);
};

const buildSharedCatalogDraftsFromSource = (sourceCatalog) => {
  const sourceCatalogId = normalizeId(sourceCatalog?.catalogId || sourceCatalog?.id) || 'shared';
  const sortedSourceHeaders = sortSectionHeadersByOrder(sourceCatalog?.sectionHeaders || []);
  const sectionHeadersDraft = sortedSourceHeaders.map((sectionHeader, index) => ({
    id: null,
    draftId: `shared-header-${sourceCatalogId}-${sectionHeader?.id || index + 1}`,
    name: sectionHeader?.name || '',
    enabled: Boolean(sectionHeader?.enabled),
    sort_order: index + 1,
  }));
  const headerKeyBySourceId = new Map(
    sortedSourceHeaders.map((sectionHeader, index) => [
      String(sectionHeader?.id || `header-${index + 1}`),
      getSectionHeaderDraftKey(sectionHeadersDraft[index]),
    ])
  );
  const sectionKeyByName = new Map();
  const sectionsDraft = sortSectionsByOrder(sourceCatalog?.sections || []).map((section, index) => {
    const nextSection = {
      id: null,
      draftId: `shared-section-${sourceCatalogId}-${section?.id || index + 1}`,
      name: section?.name || '',
      enabled: Boolean(section?.enabled),
      sort_order: index + 1,
      headerKey: section?.headerId ? (headerKeyBySourceId.get(String(section.headerId)) || '') : '',
    };
    const normalizedName = normalizeSectionNameKey(nextSection.name);
    if (normalizedName && !sectionKeyByName.has(normalizedName)) {
      sectionKeyByName.set(normalizedName, getSectionDraftKey(nextSection));
    }
    return nextSection;
  });
  return {
    sectionHeadersDraft,
    sectionsDraft,
    sectionKeyByName,
  };
};

const normalizeCampaignSectionOrder = (rows = []) => {
  const counters = new Map();
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const beforeSectionKey = String(row?.beforeSectionKey || '');
    const nextSortOrder = (counters.get(beforeSectionKey) || 0) + 1;
    counters.set(beforeSectionKey, nextSortOrder);
    return {
      ...row,
      mode: normalizeCampaignSectionMode(row?.mode, 'single'),
      beforeSectionKey,
      sort_order: nextSortOrder,
    };
  });
};

const resolveDefaultPromotionPosition = (typeId, sectionCount) => {
  const count = Math.max(1, Number(sectionCount || 0));
  if (typeId === 'landing_activity_blocks' || typeId === 'landing_city_widgets') return count + 1;
  if (typeId === 'landing_inline_links') return Math.min(2, count);
  return 1;
};

const buildSectionEditorRows = ({
  sections = [],
  campaignSections = [],
  socialSection = null,
  promotionTypeIds = [],
  promotionPositions = {},
} = {}) => {
  const sortedSections = sortSectionsByOrder(sections);
  const normalizedCampaignSections = normalizeCampaignSectionOrder(campaignSections);
  const groupedCampaignSections = new Map();
  const validSectionKeys = new Set(sortedSections.map((section) => getSectionDraftKey(section)));
  const fallbackSectionKey = sortedSections.length ? getSectionDraftKey(sortedSections[0]) : '';
  const shouldRenderSocialSection = Boolean(socialSection?.enabled)
    && Array.isArray(socialSection?.linkKeys)
    && socialSection.linkKeys.length > 0;
  const socialSortOrder = shouldRenderSocialSection
    ? resolveCatalogSocialSortOrder(socialSection, sortedSections.length)
    : null;
  const promotionRowsByPosition = new Map();
  (Array.isArray(promotionTypeIds) ? promotionTypeIds : []).forEach((typeId) => {
    if (!CATALOG_PROMOTION_LABELS[typeId]) return;
    const requestedPosition = Number(promotionPositions?.[typeId]);
    const maxPosition = typeId === 'landing_network_ads'
      ? Math.max(1, sortedSections.length)
      : Math.max(1, sortedSections.length + 1);
    const position = Number.isInteger(requestedPosition) && requestedPosition > 0
      ? Math.min(maxPosition, requestedPosition)
      : resolveDefaultPromotionPosition(typeId, sortedSections.length);
    const existing = promotionRowsByPosition.get(position) || [];
    existing.push({
      kind: 'promotion',
      key: `promotion:${typeId}`,
      typeId,
      position,
    });
    promotionRowsByPosition.set(position, existing);
  });

  normalizedCampaignSections.forEach((campaignSection) => {
    const requestedKey = String(campaignSection.beforeSectionKey || '');
    const sectionKey = validSectionKeys.has(requestedKey) ? requestedKey : fallbackSectionKey;
    if (!sectionKey) return;
    const existing = groupedCampaignSections.get(sectionKey) || [];
    existing.push({
      ...campaignSection,
      beforeSectionKey: sectionKey,
    });
    groupedCampaignSections.set(sectionKey, existing);
  });

  const rows = [];
  sortedSections.forEach((section, sectionIndex) => {
    rows.push(...(promotionRowsByPosition.get(sectionIndex + 1) || []));
    const sectionKey = getSectionDraftKey(section);
    const sectionCampaignSections = groupedCampaignSections.get(sectionKey) || [];
    sectionCampaignSections.forEach((campaignSection) => {
      rows.push({
        kind: 'campaign',
        key: getCampaignSectionDraftKey(campaignSection),
        campaignSection,
        sectionKey,
      });
    });
    if (shouldRenderSocialSection && socialSortOrder === rows.filter((row) => row.kind === 'section').length + 1) {
      rows.push({
        kind: 'socials',
        key: CATALOG_SOCIAL_SECTION_KEY,
        socialSection: {
          ...socialSection,
          sort_order: socialSortOrder,
        },
      });
    }
    rows.push({
      kind: 'section',
      key: sectionKey,
      section,
    });
  });
  if (shouldRenderSocialSection && socialSortOrder === sortedSections.length + 1) {
    rows.push({
      kind: 'socials',
      key: CATALOG_SOCIAL_SECTION_KEY,
      socialSection: {
        ...socialSection,
        sort_order: socialSortOrder,
      },
    });
  }
  rows.push(...(promotionRowsByPosition.get(sortedSections.length + 1) || []));
  return rows;
};

const resolveSocialSortOrderFromEditorRows = (rows = []) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  let sectionCount = 0;
  for (const row of safeRows) {
    if (row?.kind === 'socials') return sectionCount + 1;
    if (row?.kind === 'section') sectionCount += 1;
  }
  return null;
};

const resolvePromotionPositionsFromEditorRows = (rows = []) => {
  const positions = {};
  let sectionCount = 0;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.kind === 'section') {
      sectionCount += 1;
      return;
    }
    if (row?.kind === 'promotion' && row.typeId) {
      positions[row.typeId] = sectionCount + 1;
    }
  });
  return positions;
};

const rebuildCampaignSectionsFromEditorRows = (rows = [], campaignSections = []) => {
  const campaignSectionByKey = new Map(
    (Array.isArray(campaignSections) ? campaignSections : []).map((campaignSection) => [
      getCampaignSectionDraftKey(campaignSection),
      campaignSection,
    ])
  );

  const normalizedRows = Array.isArray(rows) ? rows : [];
  const sectionRows = normalizedRows.filter((row) => row.kind === 'section');
  const fallbackSectionKey = sectionRows.length ? sectionRows[sectionRows.length - 1].key : '';
  const next = [];
  let pendingCampaignSectionKeys = [];

  normalizedRows.forEach((row) => {
    if (row.kind === 'campaign') {
      pendingCampaignSectionKeys.push(row.key);
      return;
    }

    if (row.kind !== 'section') return;
    const beforeSectionKey = row.key;
    pendingCampaignSectionKeys.forEach((campaignKey, index) => {
      const campaignSection = campaignSectionByKey.get(campaignKey);
      if (!campaignSection) return;
      next.push({
        ...campaignSection,
        beforeSectionKey,
        sort_order: index + 1,
      });
    });
    pendingCampaignSectionKeys = [];
  });

  if (pendingCampaignSectionKeys.length && fallbackSectionKey) {
    const startOrder = next.filter((row) => row.beforeSectionKey === fallbackSectionKey).length;
    pendingCampaignSectionKeys.forEach((campaignKey, index) => {
      const campaignSection = campaignSectionByKey.get(campaignKey);
      if (!campaignSection) return;
      next.push({
        ...campaignSection,
        beforeSectionKey: fallbackSectionKey,
        sort_order: startOrder + index + 1,
      });
    });
  }

  return normalizeCampaignSectionOrder(next);
};

const buildCatalogLandingUrl = (config, landingKey, { unitNumber, unitLabel, itemId, sectionId } = {}) => {
  if (!landingKey) return '';
  const landingPath = config?.landingPath || '/lp/m/';
  const unitParam = config?.unitParam || 'table';
  const unitLabelParam = config?.unitLabelParam || 'tlabel';
  if (typeof window === 'undefined') return `${landingPath}${landingKey}`;
  const url = new URL(`${window.location.origin}${landingPath}${landingKey}`);
  if (unitNumber) url.searchParams.set(unitParam, String(unitNumber));
  if (unitLabel) url.searchParams.set(unitLabelParam, String(unitLabel));
  if (itemId) url.searchParams.set('item', String(itemId));
  if (sectionId) url.searchParams.set('section', String(sectionId));
  return url.toString();
};

function TagFilter({ value = [], suggestions = [], onChange }) {
  const handleToggle = (tag) => {
    if (value.includes(tag)) {
      onChange(value.filter((item) => item !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {suggestions.map((tag) => (
        <button
          key={tag}
          type="button"
          className={`rounded-full px-3 py-1 text-xs ${
            value.includes(tag)
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          onClick={() => handleToggle(tag)}
        >
          {tag}
        </button>
      ))}
    </div>
  );
}

function MenuItemTagBadges({ tags = [], spiciness = '', language = 'en' }) {
  const normalizedTags = normalizeMenuItemTags(tags);
  const normalizedSpiciness = normalizeMenuItemSpiciness(spiciness);
  const menuTagI18n = getMenuTagI18nPack(language);
  if (!normalizedTags.length && !normalizedSpiciness) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {normalizedTags.map((tag) => {
        const option = MENU_ITEM_TAG_OPTION_MAP.get(tag) || null;
        const localizedLabel = menuTagI18n.tags?.[tag] || option?.label || tag;
        return (
          <span
            key={tag}
            className={`rounded-full border px-2 py-1 text-[11px] font-medium ${option?.chipClass || 'border-gray-700 bg-gray-900/60 text-gray-100'}`}
          >
            {localizedLabel}
          </span>
        );
      })}
      {normalizedSpiciness && (
        <span
          className={`rounded-full border px-2 py-1 text-[11px] font-medium ${MENU_ITEM_SPICINESS_OPTION_MAP.get(normalizedSpiciness)?.chipClass || 'border-gray-700 bg-gray-900/60 text-gray-100'}`}
        >
          {menuTagI18n.spiciness?.[normalizedSpiciness] || MENU_ITEM_SPICINESS_OPTION_MAP.get(normalizedSpiciness)?.label || normalizedSpiciness}
        </span>
      )}
    </div>
  );
}

function renderMenuTagIconNode(meta, iconSet, customIconUrl = '') {
  const normalizedIconSet = normalizeMenuTagIconSet(iconSet);
  if (!meta) return <span className="text-sm leading-none">•</span>;
  if (normalizedIconSet === 'custom' && customIconUrl) {
    return <img src={customIconUrl} alt="" className="h-[22px] w-[22px] object-contain" />;
  }
  if (normalizedIconSet === 'emoji') {
    return <span className="text-[17px] leading-none">{meta.emoji || meta.shortLabel || '•'}</span>;
  }
  if (normalizedIconSet === 'mono') {
    return <span className="text-[10px] font-black uppercase tracking-[0.08em] leading-none">{meta.shortLabel || '•'}</span>;
  }
  const IconComponent = meta.Icon;
  if (!IconComponent) {
    return <span className="text-[10px] font-black uppercase tracking-[0.08em] leading-none">{meta.shortLabel || '•'}</span>;
  }
  return <IconComponent className="h-[18px] w-[18px]" strokeWidth={normalizedIconSet === 'tile' ? 2.2 : 1.9} />;
}

function MenuTagIconPreviewBadge({
  tag = '',
  spiciness = '',
  iconSet = DEFAULT_MENU_TAG_ICON_SET,
  customIconUrls = {},
  language = 'en',
}) {
  const normalizedTag = normalizeMenuItemTag(tag);
  const normalizedSpiciness = normalizeMenuItemSpiciness(spiciness);
  const option = normalizedTag
    ? (MENU_ITEM_TAG_OPTION_MAP.get(normalizedTag) || null)
    : (MENU_ITEM_SPICINESS_OPTION_MAP.get(normalizedSpiciness) || null);
  const meta = normalizedTag
    ? MENU_ITEM_TAG_ICON_META[normalizedTag]
    : MENU_ITEM_SPICINESS_ICON_META[normalizedSpiciness];
  if (!meta) return null;

  const normalizedIconSet = normalizeMenuTagIconSet(iconSet);
  const badgeShapeClass = normalizedIconSet === 'tile' || normalizedIconSet === 'custom' ? 'rounded-xl' : 'rounded-full';
  const badgeClass = option?.chipClass || 'border-gray-700 bg-gray-900/60 text-gray-100';
  const title = normalizedTag
    ? getLocalizedMenuTagLabel(normalizedTag, language)
    : getLocalizedMenuSpicinessLabel(normalizedSpiciness, language);
  const customIconKey = getMenuCustomIconKey({ tag: normalizedTag, spiciness: normalizedSpiciness });
  const customIconUrl = normalizeMenuCustomIconUrl(customIconUrls?.[customIconKey]);

  return (
    <span
      title={title}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center border ${badgeShapeClass} ${badgeClass}`}
    >
      {renderMenuTagIconNode(meta, normalizedIconSet, customIconUrl)}
    </span>
  );
}

function MenuCustomTagIconUploader({ value = {}, enabledTagKeys = MENU_ITEM_TAG_KEYS, language = 'en', onChange }) {
  const normalizedValue = normalizeMenuCustomIconMap(value);
  const enabledTagSet = useMemo(
    () => new Set(normalizeMenuEnabledTagKeys(enabledTagKeys, MENU_ITEM_TAG_KEYS)),
    [enabledTagKeys]
  );
  const localizedTargets = useMemo(
    () => buildLocalizedMenuCustomIconTargets(language),
    [language]
  );
  const visibleTargets = useMemo(
    () => localizedTargets.filter((target) => target.spiciness || enabledTagSet.has(target.tag)),
    [enabledTagSet, localizedTargets]
  );

  const handleFileSelect = useCallback(async (targetKey, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!targetKey || !file) return;
    if (!String(file.type || '').startsWith('image/')) {
      window.alert('Choose an image file for the custom menu tag icon.');
      return;
    }
    if (file.size > MENU_CUSTOM_ICON_MAX_BYTES) {
      window.alert('Custom menu tag icons must be 64 KB or smaller.');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const normalizedUrl = normalizeMenuCustomIconUrl(dataUrl);
      if (!normalizedUrl) throw new Error('Unsupported icon file.');
      onChange({
        ...normalizedValue,
        [targetKey]: normalizedUrl,
      });
    } catch (err) {
      console.error('Failed to load custom menu tag icon', err);
      window.alert(err?.message || 'Could not load that icon file.');
    }
  }, [normalizedValue, onChange]);

  const handleRemove = useCallback((targetKey) => {
    if (!targetKey) return;
    const next = { ...normalizedValue };
    delete next[targetKey];
    onChange(next);
  }, [normalizedValue, onChange]);

  return (
    <div className="mt-4 rounded-xl border border-gray-800 bg-gray-900/60 p-4">
      <div className="text-sm font-semibold text-white">Custom Badge Uploads</div>
      <div className="mt-1 text-xs text-gray-400">
        Upload small transparent icons for any tags or spice levels you want to override on the landing page. Keep each file under 64 KB.
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {visibleTargets.map((target) => {
          const customIconUrl = normalizeMenuCustomIconUrl(normalizedValue[target.key]);
          return (
            <div key={target.key} className="rounded-xl border border-gray-800 bg-gray-950/80 p-3">
              <div className="flex items-center gap-3">
                <MenuTagIconPreviewBadge
                  tag={target.tag}
                  spiciness={target.spiciness}
                  iconSet="custom"
                  customIconUrls={normalizedValue}
                  language={language}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">{target.label}</div>
                  <div className="text-[11px] text-gray-500">
                    {customIconUrl ? 'Custom icon uploaded' : 'Falls back to the default icon until uploaded'}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <label className="inline-flex cursor-pointer items-center rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500">
                  {customIconUrl ? 'Replace icon' : 'Upload icon'}
                  <input
                    type="file"
                    accept="image/png,image/webp,image/svg+xml,image/jpeg"
                    className="hidden"
                    onChange={(event) => handleFileSelect(target.key, event)}
                  />
                </label>
                <button
                  type="button"
                  className="rounded-lg border border-gray-700 px-3 py-2 text-xs font-semibold text-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleRemove(target.key)}
                  disabled={!customIconUrl}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MenuTagIconSetPicker({
  value = DEFAULT_MENU_TAG_ICON_SET,
  customIconUrls = {},
  enabledTagKeys = MENU_ITEM_TAG_KEYS,
  language = 'en',
  onChange,
  onChangeCustomIcons,
}) {
  const normalizedValue = normalizeMenuTagIconSet(value);
  const previewSamples = [
    { tag: 'gluten_free' },
    { tag: 'poultry' },
    { tag: 'vegan' },
    { spiciness: 'hot' },
  ];

  return (
    <div className="mt-4">
      <div className="text-sm font-semibold text-white">Tag icon set</div>
      <div className="mt-1 text-xs text-gray-400">
        Pick the badge style used for landing-page filters and menu item tags.
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {MENU_TAG_ICON_SET_OPTIONS.map((option) => {
          const active = option.key === normalizedValue;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange(option.key)}
              className={`rounded-xl border px-4 py-3 text-left transition ${
                active
                  ? 'border-indigo-400 bg-indigo-500/10 shadow-[0_0_0_1px_rgba(129,140,248,0.45)]'
                  : 'border-gray-800 bg-gray-900/60 hover:border-gray-700 hover:bg-gray-900'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">{option.label}</div>
                  <div className="mt-1 text-xs text-gray-400">{option.description}</div>
                </div>
                {active && (
                  <span className="rounded-full bg-indigo-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-indigo-200">
                    Active
                  </span>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {previewSamples.map((sample) => (
                  <MenuTagIconPreviewBadge
                    key={`${option.key}:${sample.tag || sample.spiciness}`}
                    tag={sample.tag}
                    spiciness={sample.spiciness}
                    iconSet={option.key}
                    customIconUrls={customIconUrls}
                    language={language}
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>
      {normalizedValue === 'custom' && (
        <MenuCustomTagIconUploader
          value={customIconUrls}
          enabledTagKeys={enabledTagKeys}
          language={language}
          onChange={onChangeCustomIcons}
        />
      )}
    </div>
  );
}

function MenuTagAvailabilityPicker({
  value = [],
  items = [],
  iconSet = DEFAULT_MENU_TAG_ICON_SET,
  customIconUrls = {},
  language = 'en',
  onChange,
}) {
  const enabledTagKeys = useMemo(
    () => normalizeMenuEnabledTagKeys(value, MENU_ITEM_TAG_KEYS),
    [value]
  );
  const localizedTagOptions = useMemo(
    () => buildLocalizedMenuTagOptions(language),
    [language]
  );
  const enabledTagSet = useMemo(
    () => new Set(enabledTagKeys),
    [enabledTagKeys]
  );
  const usageByTagKey = useMemo(() => {
    const counts = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
      normalizeMenuItemTags(item?.tags).forEach((tagKey) => {
        counts.set(tagKey, (counts.get(tagKey) || 0) + 1);
      });
    });
    return counts;
  }, [items]);
  const showSeafoodHint = enabledTagSet.has('seafood')
    && ['fish', 'shrimp', 'squid', 'crab'].some((tagKey) => enabledTagSet.has(tagKey));
  const seafoodLabel = getLocalizedMenuTagLabel('seafood', language);

  const handleToggle = useCallback((tagKey) => {
    if (!tagKey) return;
    if (enabledTagSet.has(tagKey)) {
      onChange?.(enabledTagKeys.filter((entry) => entry !== tagKey));
      return;
    }
    onChange?.([...enabledTagKeys, tagKey]);
  }, [enabledTagKeys, enabledTagSet, onChange]);

  return (
    <div className="mt-4 rounded border border-gray-800 bg-gray-900/60 px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">Available Item Tags</div>
          <div className="mt-1 text-xs text-gray-400">
            Turn individual tags on or off for this menu. Disabled tags disappear from item editing, landing-page badges, and most filter choices, but existing assignments stay stored so you can re-enable them later.
          </div>
        </div>
        <button
          type="button"
          className="rounded border border-gray-700 bg-gray-950/70 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => onChange?.([...MENU_ITEM_TAG_KEYS])}
          disabled={enabledTagKeys.length === MENU_ITEM_TAG_KEYS.length}
        >
          Enable All
        </button>
      </div>
      {showSeafoodHint && (
        <div className="mt-3 rounded border border-cyan-800/60 bg-cyan-950/20 px-3 py-2 text-xs text-cyan-100">
          Tip: if you tag fish, shrimp, squid, or crab separately, you can switch off the broader <span className="font-semibold">{seafoodLabel}</span> item tag to avoid duplicate fish-style badges. The {seafoodLabel} filter can still stay visible on the landing page by enabling it below.
        </div>
      )}
      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {localizedTagOptions.map((option) => {
          const enabled = enabledTagSet.has(option.key);
          const usageCount = usageByTagKey.get(option.key) || 0;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => handleToggle(option.key)}
              className={`rounded-xl border px-3 py-3 text-left transition ${
                enabled
                  ? 'border-indigo-400 bg-indigo-500/10 shadow-[0_0_0_1px_rgba(129,140,248,0.28)]'
                  : 'border-gray-800 bg-gray-950/70 hover:border-gray-700 hover:bg-gray-900'
              }`}
            >
              <div className="flex items-start gap-3">
                <MenuTagIconPreviewBadge
                  tag={option.key}
                  iconSet={iconSet}
                  customIconUrls={customIconUrls}
                  language={language}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-white">{option.label}</div>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                      enabled
                        ? 'bg-indigo-500/20 text-indigo-200'
                        : 'bg-gray-800 text-gray-400'
                    }`}>
                      {enabled ? 'On' : 'Off'}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-gray-500">
                    {usageCount ? `${usageCount} item${usageCount === 1 ? '' : 's'} currently tagged` : 'Not used on any items yet'}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MenuFilterVisibilityPicker({ value = [], items = [], enabledTagKeys = MENU_ITEM_TAG_KEYS, language = 'en', onChange }) {
  const normalizedEnabledTagKeys = useMemo(
    () => normalizeMenuEnabledTagKeys(enabledTagKeys, MENU_ITEM_TAG_KEYS),
    [enabledTagKeys]
  );
  const localizedTagOptions = useMemo(
    () => buildLocalizedMenuTagOptions(language),
    [language]
  );
  const localizedSpicinessOptions = useMemo(
    () => buildLocalizedMenuSpicinessOptions(language),
    [language]
  );
  const localizedFilterVariants = useMemo(
    () => buildLocalizedMenuFilterVariants(language),
    [language]
  );
  const localizedFilterVariantByKey = useMemo(
    () => new Map(localizedFilterVariants.map((option) => [option.key, option])),
    [localizedFilterVariants]
  );
  const enabledTagSet = useMemo(
    () => new Set(normalizedEnabledTagKeys),
    [normalizedEnabledTagKeys]
  );
  const orderedSelectedKeys = useMemo(
    () => filterMenuFilterKeysByEnabledTags(value, normalizedEnabledTagKeys),
    [normalizedEnabledTagKeys, value]
  );
  const selectedKeys = useMemo(
    () => new Set(orderedSelectedKeys),
    [orderedSelectedKeys]
  );
  const [draggedFilterKey, setDraggedFilterKey] = useState('');
  const availabilityByKey = useMemo(() => {
    const counts = new Map();
    localizedFilterVariants.forEach((option) => {
      if (option.variant !== 'spiciness' && !isMenuFilterTagAvailable(option.tag, enabledTagSet)) return;
      const matchCount = (Array.isArray(items) ? items : []).reduce((total, item) => (
        total + (menuFilterMatchesItem(option.key, {
          tags: item?.tags,
          spiciness: item?.spiciness,
        }) ? 1 : 0)
      ), 0);
      counts.set(option.key, matchCount);
    });
    return counts;
  }, [enabledTagSet, items, localizedFilterVariants]);
  const selectedOptions = useMemo(
    () => orderedSelectedKeys
      .map((key) => localizedFilterVariantByKey.get(key) || null)
      .filter(Boolean),
    [localizedFilterVariantByKey, orderedSelectedKeys]
  );
  const availableTagGroups = useMemo(() => (
    localizedTagOptions
      .filter((option) => isMenuFilterTagAvailable(option.key, enabledTagSet))
      .map((option) => {
        const filters = [option.key, `hide-${option.key}`]
          .filter((filterKey) => !selectedKeys.has(filterKey))
          .map((filterKey) => localizedFilterVariantByKey.get(filterKey) || null)
          .filter(Boolean);
        if (!filters.length) return null;
        return {
          tagKey: option.key,
          label: option.label,
          filters,
        };
      })
      .filter(Boolean)
  ), [enabledTagSet, localizedFilterVariantByKey, localizedTagOptions, selectedKeys]);
  const availableSpicinessOptions = useMemo(() => (
    localizedSpicinessOptions
      .filter((option) => option.key)
      .map((option) => localizedFilterVariantByKey.get(`spiciness-${option.key}`) || null)
      .filter((option) => option && !selectedKeys.has(option.key))
      .sort(compareMenuFilterVariantOptions)
  ), [localizedFilterVariantByKey, localizedSpicinessOptions, selectedKeys]);

  const toggleKey = useCallback((filterKey) => {
    const normalized = normalizeMenuFilterKey(filterKey);
    if (!normalized) return;
    if (selectedKeys.has(normalized)) {
      onChange?.(orderedSelectedKeys.filter((key) => key !== normalized));
      return;
    }
    onChange?.([...orderedSelectedKeys, normalized]);
  }, [onChange, orderedSelectedKeys, selectedKeys]);

  const handleDragStart = useCallback((event, filterKey) => {
    setDraggedFilterKey(filterKey);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', filterKey);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedFilterKey('');
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }, []);

  const handleDrop = useCallback((event, targetKey) => {
    event.preventDefault();
    const sourceKey = String(
      draggedFilterKey
        || event.dataTransfer?.getData('text/plain')
        || ''
    ).trim();
    if (!sourceKey || sourceKey === targetKey) {
      setDraggedFilterKey('');
      return;
    }
    const position = resolveDropPosition(event);
    const next = reorderListByKeys(
      orderedSelectedKeys,
      sourceKey,
      targetKey,
      (entry) => entry,
      position
    );
    onChange?.(next);
    setDraggedFilterKey('');
  }, [draggedFilterKey, onChange, orderedSelectedKeys]);

  return (
    <details className="mt-4 rounded border border-gray-800 bg-gray-900/60 px-3 py-3">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">Landing Page Filter Visibility</div>
          <div className="mt-1 text-xs text-gray-400">
            These controls only affect the filter bar. Item badges remain controlled by the toggle above.
          </div>
        </div>
        <span className="shrink-0 rounded border border-gray-700 bg-gray-950/70 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-300">
          {selectedOptions.length} active
        </span>
      </summary>

      <div className="mt-3 space-y-3">
        <div className="rounded border border-indigo-900/40 bg-indigo-950/20 px-3 py-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-white">Enabled Filters</div>
              <div className="mt-1 text-xs text-gray-400">
                Drag these to control the left-to-right order on the landing page.
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-wide text-indigo-200">
              {selectedOptions.length} visible
            </div>
          </div>
          {selectedOptions.length ? (
            <div className="mt-3 space-y-2">
              {selectedOptions.map((filterOption) => {
                const matchCount = availabilityByKey.get(filterOption.key) || 0;
                const isDragging = draggedFilterKey === filterOption.key;
                return (
                  <div
                    key={`menu-filter-enabled-${filterOption.key}`}
                    draggable
                    onDragStart={(event) => handleDragStart(event, filterOption.key)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(event) => handleDrop(event, filterOption.key)}
                    className={`flex cursor-move items-start gap-3 rounded border px-3 py-2 text-sm transition ${
                      isDragging
                        ? 'border-indigo-400 bg-indigo-900/40 text-white'
                        : 'border-indigo-800/60 bg-indigo-950/30 text-white'
                    }`}
                  >
                    <div className="mt-0.5 text-xs uppercase tracking-[0.12em] text-indigo-200">Drag</div>
                    <div className="flex-1">
                      <div className="font-medium">{filterOption.label}</div>
                      <div className="mt-1 text-xs text-indigo-100/80">
                        {matchCount > 0
                          ? `${matchCount} current item${matchCount === 1 ? '' : 's'} would match`
                          : 'No current items match yet'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="rounded border border-indigo-700/60 bg-indigo-950/40 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-indigo-100"
                      onClick={() => toggleKey(filterOption.key)}
                    >
                      Hide
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-3 rounded border border-gray-800 bg-black/20 px-3 py-3 text-xs text-gray-400">
              No landing-page filters are enabled yet.
            </div>
          )}
        </div>

        <div className="rounded border border-gray-800 bg-black/20 px-3 py-3">
          <div className="text-sm font-medium text-white">Available Filters</div>
          <div className="mt-1 text-xs text-gray-400">
            Disabled filters stay ordered here until you add them to the active list above.
          </div>

          <div className="mt-3 space-y-3">
            {availableTagGroups.map((group) => {
              const positiveCount = availabilityByKey.get(group.tagKey) || 0;
              const negativeCount = availabilityByKey.get(`hide-${group.tagKey}`) || 0;
              return (
                <div key={`menu-filter-row-${group.tagKey}`} className="rounded border border-gray-800 bg-gray-950/60 px-3 py-3">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-white">{group.label}</div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500">
                      {positiveCount} include • {negativeCount} exclude
                    </div>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {group.filters.map((filterOption) => {
                      const matchCount = availabilityByKey.get(filterOption.key) || 0;
                      return (
                        <div
                          key={`menu-filter-choice-${filterOption.key}`}
                          className="flex items-start gap-3 rounded border border-gray-800 bg-black/20 px-3 py-2 text-sm text-gray-200"
                        >
                          <div className="flex-1">
                            <span className="block font-medium">{filterOption.label}</span>
                            <span className="mt-1 block text-xs text-inherit opacity-75">
                              {matchCount > 0
                                ? `${matchCount} current item${matchCount === 1 ? '' : 's'} would match`
                                : 'No current items match yet'}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="rounded border border-gray-700 bg-gray-900/80 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-100"
                            onClick={() => toggleKey(filterOption.key)}
                          >
                            Show
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {availableSpicinessOptions.length ? (
              <div className="rounded border border-gray-800 bg-gray-950/60 px-3 py-3">
                <div className="text-sm font-medium text-white">Spiciness Filters</div>
                <div className="mt-2 grid gap-2 md:grid-cols-3">
                  {availableSpicinessOptions.map((option) => {
                    const matchCount = availabilityByKey.get(option.key) || 0;
                    return (
                      <div
                        key={`menu-filter-spiciness-${option.key}`}
                        className="flex items-start gap-3 rounded border border-gray-800 bg-black/20 px-3 py-2 text-sm text-gray-200"
                      >
                        <span className="flex-1">
                          <span className="block font-medium">{option.label}</span>
                          <span className="mt-1 block text-xs text-inherit opacity-75">
                            {matchCount > 0
                              ? `${matchCount} current item${matchCount === 1 ? '' : 's'} would match`
                              : 'No current items match yet'}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="rounded border border-gray-700 bg-gray-900/80 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-100"
                          onClick={() => toggleKey(option.key)}
                        >
                          Show
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </details>
  );
}

function SettingsDisclosurePanel({ title, description = '', children, defaultOpen = false }) {
  return (
    <details className="bg-gray-950 rounded p-4 border border-gray-800" open={defaultOpen || undefined}>
      <summary className="cursor-pointer list-none">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold">{title}</div>
            {description ? (
              <div className="mt-1 text-xs text-gray-400">{description}</div>
            ) : null}
          </div>
          <span className="shrink-0 rounded border border-gray-700 bg-gray-900/60 px-2 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-300">
            Expand
          </span>
        </div>
      </summary>
      <div className="mt-4 space-y-5">
        {children}
      </div>
    </details>
  );
}

function CatalogColorField({
  label,
  description,
  value,
  defaultValue,
  onChange,
}) {
  const normalizedValue = normalizeHexColor(value, defaultValue);
  return (
    <div className="rounded border border-gray-800 bg-gray-900/60 px-3 py-3">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-100">{label}</div>
          {description ? <div className="mt-1 text-xs text-gray-400">{description}</div> : null}
        </div>
        <button
          type="button"
          className="text-xs text-indigo-300 underline"
          onClick={() => onChange(defaultValue)}
        >
          Reset
        </button>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="color"
          className="h-10 w-14 rounded border border-gray-700 bg-transparent p-1"
          value={normalizedValue}
          onChange={(event) => onChange(event.target.value)}
        />
        <div className="text-sm text-gray-200">{normalizedValue}</div>
      </div>
    </div>
  );
}

function MenuItemTagPicker({ value = [], availableTagKeys = MENU_ITEM_TAG_KEYS, language = 'en', onChange }) {
  const normalizedValue = normalizeMenuItemTags(value);
  const localizedTagOptions = useMemo(
    () => buildLocalizedMenuTagOptions(language),
    [language]
  );
  const normalizedAvailableTagKeys = useMemo(
    () => normalizeMenuEnabledTagKeys(availableTagKeys, MENU_ITEM_TAG_KEYS),
    [availableTagKeys]
  );
  const availableTagSet = useMemo(
    () => new Set(normalizedAvailableTagKeys),
    [normalizedAvailableTagKeys]
  );
  const hiddenAssignedTags = useMemo(
    () => normalizedValue.filter((tagKey) => !availableTagSet.has(tagKey)),
    [availableTagSet, normalizedValue]
  );

  const handleToggle = (tagKey) => {
    if (!tagKey) return;
    if (normalizedValue.includes(tagKey)) {
      onChange(normalizedValue.filter((tag) => tag !== tagKey));
      return;
    }
    onChange([...normalizedValue, tagKey]);
  };

  return (
    <div>
      {!normalizedAvailableTagKeys.length ? (
        <div className="rounded border border-dashed border-gray-700 bg-gray-900/40 px-3 py-3 text-xs text-gray-400">
          No item tags are enabled for this menu right now. Re-enable tags in Menu Settings to label proteins, allergens, or dietary options.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {localizedTagOptions.filter((option) => availableTagSet.has(option.key)).map((option) => {
            const active = normalizedValue.includes(option.key);
            return (
              <button
                key={option.key}
                type="button"
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  active
                    ? option.chipClass
                    : 'border-gray-700 bg-gray-900/60 text-gray-300 hover:bg-gray-800'
                }`}
                onClick={() => handleToggle(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
      {!!hiddenAssignedTags.length && (
        <div className="mt-3 rounded border border-amber-800/70 bg-amber-950/20 px-3 py-2 text-xs text-amber-100">
          Hidden by Menu Settings but still saved on this item:{' '}
          {hiddenAssignedTags
            .map((tagKey) => getLocalizedMenuTagLabel(tagKey, language))
            .join(', ')}.
        </div>
      )}
    </div>
  );
}

function QrPreviewModal({ title, url, onClose }) {
  const [dataUrl, setDataUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function generate() {
      if (!url) {
        setDataUrl('');
        return;
      }
      setError('');
      try {
        const next = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 2, width: 520 });
        if (!cancelled) setDataUrl(next);
      } catch (err) {
        console.warn('Failed to generate QR code', err);
        if (!cancelled) setError('Failed to generate QR code.');
      }
    }

    generate();

    return () => {
      cancelled = true;
    };
  }, [url]);

  const handleCopyUrl = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      alert('URL copied to clipboard.');
    } catch (err) {
      console.warn('Clipboard copy failed', err);
      window.prompt('Copy URL:', url);
    }
  };

  const handleOpenUrl = () => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4" onClick={onClose}>
      <div
        className="relative bg-gray-950 rounded-lg w-full max-w-2xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{title || 'QR Code'}</div>
            {url && <div className="text-xs text-gray-400 truncate">{url}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-700 rounded text-white" onClick={onClose}>Close</button>
            <button className="px-3 py-1 bg-indigo-600 rounded text-white" onClick={handleCopyUrl} disabled={!url}>
              Copy URL
            </button>
            <button className="px-3 py-1 bg-indigo-600 rounded text-white" onClick={handleOpenUrl} disabled={!url}>
              Open Link
            </button>
          </div>
        </div>

        <div className="p-4">
          {error && <div className="text-sm text-red-300 mb-3">{error}</div>}
          {!url && <div className="text-sm text-gray-400">Nothing to show yet.</div>}
          {url && !dataUrl && !error && <div className="text-sm text-gray-300">Generating…</div>}
          {dataUrl && (
            <div className="flex flex-col items-center gap-3">
              <img src={dataUrl} alt="QR code" className="bg-white p-2 rounded" style={{ width: 240, height: 240 }} />
              <div className="text-xs text-gray-400">
                Opens the page.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkQrPreviewModal({ title, entries, onClose }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function generate() {
      const safeEntries = Array.isArray(entries) ? entries : [];
      if (!safeEntries.length) {
        setItems([]);
        return;
      }

      setGenerating(true);
      setError('');
      try {
        const next = [];
        for (const entry of safeEntries) {
          if (cancelled) return;
          const nextUrl = entry?.url || '';
          const dataUrl = nextUrl
            ? await QRCode.toDataURL(nextUrl, { errorCorrectionLevel: 'M', margin: 2, width: 360 })
            : '';
          next.push({
            title: entry?.title || 'QR Code',
            url: nextUrl,
            dataUrl,
          });
        }
        if (!cancelled) setItems(next);
      } catch (err) {
        console.warn('Failed to generate bulk QR codes', err);
        if (!cancelled) setError('Failed to generate one or more QR codes.');
      } finally {
        if (!cancelled) setGenerating(false);
      }
    }

    generate();

    return () => {
      cancelled = true;
    };
  }, [entries]);

  const canPrint = !generating && items.length && items.every((item) => Boolean(item.dataUrl));

  const handlePrint = () => {
    if (!canPrint) return;
    const win = window.open('', '_blank', 'noopener,noreferrer');
    if (!win) {
      alert('Popup blocked. Allow popups to print.');
      return;
    }

    const escapeHtml = (text) => String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    const safeTitle = escapeHtml(title || 'QR Codes');
    const body = items
      .map((item) => {
        const safeItemTitle = escapeHtml(item.title || '');
        const safeDataUrl = String(item.dataUrl || '');
        return `
          <div class="card">
            <div class="label">${safeItemTitle}</div>
            <img src="${safeDataUrl}" alt="QR" />
          </div>
        `;
      })
      .join('\n');

    win.document.open();
    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${safeTitle}</title>
          <style>
            body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; margin: 24px; }
            h1 { font-size: 18px; margin: 0 0 16px; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
            .card { border: 1px solid #ddd; border-radius: 10px; padding: 12px; }
            .label { font-size: 12px; margin-bottom: 8px; color: #111; }
            img { width: 100%; height: auto; display: block; background: white; }
            @media print {
              body { margin: 0; }
              .card { break-inside: avoid; page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>${safeTitle}</h1>
          <div class="grid">${body}</div>
        </body>
      </html>
    `);
    win.document.close();

    const imgs = Array.from(win.document.images || []);
    const waitForImages = () => Promise.all(
      imgs.map((img) => new Promise((resolve) => {
        if (img.complete) return resolve();
        img.onload = () => resolve();
        img.onerror = () => resolve();
      }))
    );

    waitForImages().finally(() => {
      win.focus();
      win.print();
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4" onClick={onClose}>
      <div
        className="relative bg-gray-950 rounded-lg w-full max-w-5xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{title || 'QR Codes'}</div>
            <div className="text-xs text-gray-400 truncate">{items.length || 0} codes</div>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-700 rounded text-white" onClick={onClose}>Close</button>
            <button
              className="px-3 py-1 bg-indigo-600 rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
              onClick={handlePrint}
              disabled={!canPrint}
            >
              Print
            </button>
          </div>
        </div>

        <div className="p-4">
          {error && <div className="text-sm text-red-300 mb-3">{error}</div>}
          {generating && <div className="text-sm text-gray-300 mb-3">Generating…</div>}
          {!items.length && !generating && <div className="text-sm text-gray-400">Nothing to show yet.</div>}

          {!!items.length && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => (
                <div key={item.url || item.title} className="rounded border border-gray-800 bg-gray-900 p-3">
                  <div className="text-sm font-semibold mb-2 truncate">{item.title}</div>
                  {item.dataUrl ? (
                    <img src={item.dataUrl} alt="QR code" className="bg-white p-2 rounded w-full" />
                  ) : (
                    <div className="text-xs text-gray-400">No QR</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssetThumbnail({ asset, isSelected, onSelect }) {
  const [thumbSrc, setThumbSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const thumbKind = useMemo(() => resolveThumbKind(asset), [asset]);

  useEffect(() => {
    if (!asset || !thumbKind) {
      setThumbSrc(null);
      return () => {};
    }

    let objectUrl = null;
    let active = true;
    const controller = new AbortController();
    const token = localStorage.getItem('token');

    async function loadThumb() {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/creative/assets/${asset.id}/files/${encodeURIComponent(thumbKind)}`,
          {
            cache: 'no-store',
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (!res.ok) throw new Error(`Failed to load thumbnail (${res.status})`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (active) setThumbSrc(objectUrl);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch asset thumbnail', err);
        if (active) setThumbSrc(null);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadThumb();

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset, thumbKind]);

  return (
    <button
      type="button"
      onClick={() => onSelect?.(asset)}
      className={`relative w-full h-24 rounded-md overflow-hidden border ${
        isSelected ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-700'
      }`}
    >
      {thumbSrc ? (
        <img src={thumbSrc} alt="Asset thumbnail" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gray-800 flex items-center justify-center text-lg">No preview</div>
      )}
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center text-xs uppercase tracking-wide">
          Loading...
        </div>
      )}
    </button>
  );
}

function MiniAssetThumb({ asset, assetId, size = 40, className, onClick, title }) {
  const [thumbSrc, setThumbSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const thumbKind = useMemo(() => resolveThumbKind(asset), [asset]);

  useEffect(() => {
    if (!assetId || !thumbKind) {
      setThumbSrc(null);
      return () => {};
    }

    let objectUrl = null;
    let active = true;
    const controller = new AbortController();
    const token = localStorage.getItem('token');

    async function loadThumb() {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/creative/assets/${assetId}/files/${encodeURIComponent(thumbKind)}`,
          {
            cache: 'no-store',
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (!res.ok) throw new Error(`Failed to load thumbnail (${res.status})`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (active) setThumbSrc(objectUrl);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch asset thumbnail', err);
        if (active) setThumbSrc(null);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadThumb();

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, thumbKind]);

  const buttonClass = `relative overflow-hidden rounded border border-gray-700 bg-gray-800/60 ${className || ''}`;
  return (
    <button
      type="button"
      className={buttonClass}
      style={{ width: size, height: size }}
      onClick={onClick}
      title={title || ''}
    >
      {thumbSrc ? (
        <img src={thumbSrc} alt="Thumb" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-300">
          {loading ? '…' : ''}
        </div>
      )}
    </button>
  );
}

function PhotoThumbPager({
  assetIds,
  imageAssetById,
  activeAssetId,
  onActiveAssetIdChange,
  onThumbClick,
  size = 40,
  className,
  titlePrefix = 'Photo',
}) {
  const safeAssetIds = useMemo(
    () => (Array.isArray(assetIds) ? assetIds.filter(Boolean) : []),
    [assetIds]
  );
  const [localIndex, setLocalIndex] = useState(0);

  const controlledIndex = useMemo(() => {
    if (!activeAssetId) return -1;
    return safeAssetIds.findIndex((assetId) => String(assetId) === String(activeAssetId));
  }, [activeAssetId, safeAssetIds]);

  useEffect(() => {
    if (!safeAssetIds.length) {
      if (localIndex !== 0) setLocalIndex(0);
      return;
    }
    if (controlledIndex >= 0) return;
    if (localIndex >= safeAssetIds.length) {
      setLocalIndex(safeAssetIds.length - 1);
    }
  }, [controlledIndex, localIndex, safeAssetIds]);

  if (!safeAssetIds.length) return null;

  const currentIndex = controlledIndex >= 0 ? controlledIndex : Math.min(localIndex, safeAssetIds.length - 1);
  const currentAssetId = safeAssetIds[currentIndex];
  const remainingCount = Math.max(0, safeAssetIds.length - 1);

  const updateIndex = (nextIndex) => {
    if (!safeAssetIds.length) return;
    const normalized = (nextIndex + safeAssetIds.length) % safeAssetIds.length;
    if (controlledIndex < 0) setLocalIndex(normalized);
    onActiveAssetIdChange?.(safeAssetIds[normalized]);
  };

  return (
    <div className={`flex items-center gap-2 ${className || ''}`}>
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded border border-gray-700 bg-gray-800/80 text-xs text-white disabled:cursor-not-allowed disabled:bg-gray-900 disabled:text-gray-500"
        onClick={() => updateIndex(currentIndex - 1)}
        disabled={safeAssetIds.length <= 1}
        aria-label={`Previous ${titlePrefix.toLowerCase()}`}
      >
        {'<'}
      </button>
      <div className="flex items-center gap-2 rounded border border-gray-800 bg-gray-900/70 px-2 py-2">
        <MiniAssetThumb
          assetId={currentAssetId}
          asset={imageAssetById.get(String(currentAssetId)) || null}
          size={size}
          title={`${titlePrefix} ${currentIndex + 1}`}
          onClick={() => onThumbClick?.(currentAssetId)}
        />
        <div className="min-w-0 text-[11px] leading-4 text-gray-300">
          <div className="font-medium text-gray-100">
            {currentIndex + 1} of {safeAssetIds.length}
          </div>
          {currentIndex === 0 && remainingCount > 0 ? (
            <div className="text-gray-400">+{remainingCount} more</div>
          ) : (
            <div className="truncate text-gray-500">Asset #{currentAssetId}</div>
          )}
        </div>
      </div>
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded border border-gray-700 bg-gray-800/80 text-xs text-white disabled:cursor-not-allowed disabled:bg-gray-900 disabled:text-gray-500"
        onClick={() => updateIndex(currentIndex + 1)}
        disabled={safeAssetIds.length <= 1}
        aria-label={`Next ${titlePrefix.toLowerCase()}`}
      >
        {'>'}
      </button>
    </div>
  );
}

function LogoPickerModal({ siteId, initialSelectedId, onClose, onConfirm }) {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(initialSelectedId || null);
  const fileInputRef = useRef(null);

  const loadLogos = useCallback(async () => {
    if (!siteId) return;
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/sites/${siteId}/logos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load logos (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data.logos) ? data.logos : [];
      setLogos(list);
      if (!selectedId && list.length) setSelectedId(list[0].id);
    } catch (err) {
      console.error('Failed to load site logos', err);
      setError(err.message || 'Failed to load site logos.');
    } finally {
      setLoading(false);
    }
  }, [selectedId, siteId]);

  useEffect(() => {
    loadLogos();
  }, [loadLogos]);

  const selectedLogo = useMemo(
    () => logos.find((logo) => String(logo.id) === String(selectedId)) || null,
    [logos, selectedId]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!siteId) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    const token = localStorage.getItem('token');
    setUploading(true);
    setError('');

    try {
      const presignRes = await fetch(
        `/api/sites/${siteId}/logo/presign?filename=${encodeURIComponent(file.name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!presignRes.ok) {
        const body = await presignRes.text().catch(() => '');
        throw new Error(body || `Failed to get upload URL (${presignRes.status})`);
      }
      const { url, key } = await presignRes.json();
      if (!url || !key) throw new Error('Invalid presign response');

      const putRes = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) {
        const body = await putRes.text().catch(() => '');
        throw new Error(body || `Upload failed (${putRes.status})`);
      }

      const finalizeRes = await fetch(`/api/sites/${siteId}/logo/finalize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ key, filename: file.name, fileType: file.type }),
      });
      if (!finalizeRes.ok) {
        const body = await finalizeRes.text().catch(() => '');
        throw new Error(body || `Finalize failed (${finalizeRes.status})`);
      }
      const finalized = await finalizeRes.json().catch(() => ({}));
      const creativeId = finalized?.creativeId || null;
      await loadLogos();
      if (creativeId) setSelectedId(creativeId);
    } catch (err) {
      console.error('Logo upload failed', err);
      setError(err.message || 'Failed to upload logo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4" onClick={onClose}>
      <div
        className="relative bg-gray-950 rounded-lg w-full max-w-6xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">Select Logo</div>
            <div className="text-xs text-gray-400 truncate">Uses Settings → Site Details → logos.</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 bg-gray-700 rounded text-white"
              onClick={onClose}
              disabled={uploading}
            >
              Close
            </button>
            <button
              className="px-3 py-1 bg-gray-700 rounded text-white"
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Upload Logo'}
            </button>
            <button
              className="px-3 py-1 bg-indigo-600 rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
              onClick={() => onConfirm?.(selectedId || null)}
              disabled={!selectedId || uploading}
            >
              Use Logo
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>

        <div className="p-4">
          {error && <div className="mb-3 text-sm text-red-300">{error}</div>}
          {loading && <div className="text-sm text-gray-300">Loading…</div>}
          {!loading && !logos.length && (
            <div className="text-sm text-gray-400">No logos uploaded yet.</div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {logos.map((logo) => (
              <AssetThumbnail
                key={logo.id}
                asset={logo}
                isSelected={String(logo.id) === String(selectedId)}
                onSelect={() => setSelectedId(logo.id)}
              />
            ))}
          </div>

          <div className="mt-4">
            <SelectedAssetPreview asset={selectedLogo} />
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetPickerModal({
  title,
  siteId,
  assets,
  selectedAssetIds,
  onChangeSelectedAssetIds,
  onConfirm,
  confirmLabel,
  onClose,
  onRefreshAssets,
}) {
  const photoBrowserRef = useRef(null);
  const imageAssets = useMemo(
    () => (Array.isArray(assets) ? assets.filter((asset) => isImageAsset(asset)) : []),
    [assets]
  );
  const selectedAsset = useMemo(
    () => {
      const ids = Array.isArray(selectedAssetIds) ? selectedAssetIds : [];
      const id = ids.length ? ids[0] : null;
      if (!id) return null;
      return imageAssets.find((asset) => String(asset.id) === String(id)) || null;
    },
    [imageAssets, selectedAssetIds]
  );
  const selectedCount = Array.isArray(selectedAssetIds) ? selectedAssetIds.length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4" onClick={onClose}>
      <div
        className="relative bg-gray-950 rounded-lg w-full max-w-6xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{title || 'Select Photo'}</div>
            <div className="text-xs text-gray-400 truncate">Upload uses the existing S3 workflow.</div>
            {!!selectedCount && <div className="text-xs text-gray-400 truncate">Selected: {selectedCount}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-700 rounded text-white" onClick={onClose}>Close</button>
            <button
              className="px-3 py-1 bg-indigo-600 rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
              onClick={onConfirm}
              disabled={!Array.isArray(selectedAssetIds) || !selectedAssetIds.length}
            >
              {confirmLabel || 'Use Photo'}
            </button>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <PhotoAssetBrowser
            ref={photoBrowserRef}
            assets={assets}
            siteId={siteId}
            promptText=""
            multiSelect
            selectedAssetIds={selectedAssetIds}
            onToggleAsset={(asset) => {
              const assetId = asset?.id || null;
              if (!assetId) return;
              onChangeSelectedAssetIds?.((prev) => {
                const current = Array.isArray(prev) ? prev : [];
                const idx = current.findIndex((id) => String(id) === String(assetId));
                if (idx !== -1) {
                  const next = current.filter((id) => String(id) !== String(assetId));
                  return next;
                }
                return [...current, assetId];
              });
            }}
            onUploadComplete={onRefreshAssets}
            className="lg:col-span-2"
          />
          <SelectedAssetPreview asset={selectedAsset} />
        </div>
      </div>
    </div>
  );
}

function MoveCatalogEntriesModal({
  open,
  mode,
  sourceType,
  areaId,
  itemCount,
  sectionCount,
  onClose,
  onConfirm,
}) {
  const [targetType, setTargetType] = useState(sourceType || 'menu');
  const [targetSections, setTargetSections] = useState([]);
  const [targetSectionId, setTargetSectionId] = useState('');
  const [targetSectionName, setTargetSectionName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isSectionMove = mode === 'sections';

  useEffect(() => {
    if (!open) return;
    const defaultType = isSectionMove
      ? (sourceType === 'products' ? 'menu' : 'products')
      : sourceType || 'menu';
    setTargetType(defaultType);
    setTargetSectionId('');
    setTargetSectionName('');
    setError('');
  }, [isSectionMove, open, sourceType]);

  useEffect(() => {
    if (!open || !areaId || !targetType || isSectionMove) {
      setTargetSections([]);
      return () => {};
    }

    let cancelled = false;
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');
    fetch(`${getCatalogConfig(targetType).apiBase}/area/${areaId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(body || `Failed to load destination sections (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const sections = Array.isArray(data.sections) ? data.sections.filter((section) => section.enabled) : [];
        setTargetSections(sections);
        setTargetSectionId(sections[0]?.id ? String(sections[0].id) : '');
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load destination catalog', err);
        setTargetSections([]);
        setError(err.message || 'Failed to load destination sections.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [areaId, isSectionMove, open, targetType]);

  if (!open) return null;

  const targetOptions = CATALOG_MOVE_TYPES.filter((option) => (
    isSectionMove ? option.value !== sourceType : true
  ));
  const useNewSection = !isSectionMove && (!targetSectionId || targetSectionId === '__new');
  const canConfirm = isSectionMove
    ? targetType && targetType !== sourceType
    : Boolean(targetType && ((targetSectionId && targetSectionId !== '__new') || targetSectionName.trim()));

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSaving(true);
    setError('');
    try {
      await onConfirm?.({
        targetType,
        targetSectionId: !isSectionMove && targetSectionId !== '__new' ? Number(targetSectionId) : null,
        targetSectionName: !isSectionMove && useNewSection ? targetSectionName.trim() : '',
      });
    } catch (err) {
      setError(err.message || 'Move failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4" onClick={onClose}>
      <div
        className="relative bg-gray-950 rounded-lg w-full max-w-xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">
              Move {isSectionMove ? `${sectionCount} Section${sectionCount === 1 ? '' : 's'}` : `${itemCount} Item${itemCount === 1 ? '' : 's'}`}
            </div>
            <div className="text-xs text-gray-400 truncate">
              {isSectionMove
                ? 'Sections move with every item inside them.'
                : 'Items can move to another section in this catalog or another QR catalog.'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-700 rounded text-white" onClick={onClose} disabled={saving}>Close</button>
            <button
              className="px-3 py-1 bg-indigo-600 rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
              onClick={handleConfirm}
              disabled={!canConfirm || saving}
            >
              {saving ? 'Moving…' : 'Move'}
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {error && <div className="text-sm text-red-300">{error}</div>}
          <label className="block">
            <div className="text-xs text-gray-300 mb-1">Destination</div>
            <select
              className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value);
                setTargetSectionId('');
                setTargetSectionName('');
              }}
              disabled={saving}
            >
              {targetOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          {!isSectionMove && (
            <>
              <label className="block">
                <div className="text-xs text-gray-300 mb-1">Destination Section</div>
                <select
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                  value={targetSectionId || '__new'}
                  onChange={(e) => setTargetSectionId(e.target.value)}
                  disabled={loading || saving}
                >
                  {targetSections.map((section) => (
                    <option key={section.id} value={section.id}>{section.name}</option>
                  ))}
                  <option value="__new">Create new section…</option>
                </select>
              </label>

              {(useNewSection || !targetSections.length) && (
                <label className="block">
                  <div className="text-xs text-gray-300 mb-1">New Section Name</div>
                  <input
                    className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                    value={targetSectionName}
                    onChange={(e) => setTargetSectionName(e.target.value)}
                    placeholder="e.g. Merchandise"
                    disabled={saving}
                  />
                </label>
              )}

              {loading && <div className="text-sm text-gray-300">Loading destination sections…</div>}
            </>
          )}

          {isSectionMove && (
            <div className="rounded border border-gray-800 bg-gray-900/60 p-3 text-sm text-gray-300">
              A matching section will be created in the destination catalog for each selected section. Items, photos, and item translations stay attached to the moved items.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTranslationModal({
  title = 'Edit Translation',
  entityLabel = 'Section',
  englishName = '',
  languageLabel,
  initialName,
  onClose,
  onSave,
}) {
  const [name, setName] = useState(initialName || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(initialName || '');
  }, [initialName]);

  const handleSave = async () => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setError(`${entityLabel} name is required.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave?.(trimmed);
    } catch (err) {
      setError(err.message || 'Failed to save translation.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4" onClick={onClose}>
      <div
        className="relative bg-gray-950 rounded-lg w-full max-w-xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{title}</div>
            <div className="text-xs text-gray-400 truncate">
              {englishName ? `English: ${englishName}` : 'English name not set.'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-700 rounded text-white" onClick={onClose}>Close</button>
            <button
              className="px-3 py-1 bg-indigo-600 rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {error && <div className="text-sm text-red-300">{error}</div>}
          <label className="block">
            <div className="text-xs text-gray-300 mb-1">{languageLabel || 'Translation'} {entityLabel} Name</div>
            <input
              className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Translated ${String(entityLabel || 'section').toLowerCase()} name`}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function MenuItemEditorModal({
  siteId,
  apiBase,
  itemLabel,
  supportsItemTags,
  availableTagKeys,
  assets,
  sections,
  getSectionLabel,
  initialItem,
  translation,
  language,
  languageLabel,
  onClose,
  onSave,
  onSaveTranslation,
  onRefreshAssets,
}) {
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pastingPhoto, setPastingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');
  const [recentUploadedAssets, setRecentUploadedAssets] = useState([]);
  const isTranslation = Boolean(language && language !== 'en');
  const [sectionId, setSectionId] = useState(initialItem?.sectionId || '');
  const [name, setName] = useState(isTranslation ? translation?.name || '' : initialItem?.name || '');
  const [subtitle, setSubtitle] = useState(isTranslation ? translation?.subtitle || '' : initialItem?.subtitle || '');
  const [description, setDescription] = useState(isTranslation ? translation?.description || '' : initialItem?.description || '');
  const [price, setPrice] = useState(initialItem?.priceText || '');
  const [tags, setTags] = useState(
    supportsItemTags && !isTranslation
      ? normalizeMenuItemTags(initialItem?.tags)
      : []
  );
  const [spiciness, setSpiciness] = useState(
    supportsItemTags && !isTranslation
      ? normalizeMenuItemSpiciness(initialItem?.spiciness)
      : ''
  );
  const [photoAssetIds, setPhotoAssetIds] = useState(Array.isArray(initialItem?.photoAssetIds) ? initialItem.photoAssetIds : []);
  const [picker, setPicker] = useState({ open: false, selectedAssetIds: [] });
  const [photoPreviewId, setPhotoPreviewId] = useState(null);
  const [draggingPhotoAssetId, setDraggingPhotoAssetId] = useState(null);
  const [photoDropIndicator, setPhotoDropIndicator] = useState({ assetId: null, position: 'before' });
  const [copyLength, setCopyLength] = useState(3);
  const [copyStyle, setCopyStyle] = useState('Informative');
  const localizedSpicinessOptions = useMemo(
    () => buildLocalizedMenuSpicinessOptions(language),
    [language]
  );

  const availableAssets = useMemo(() => {
    const next = [];
    const seen = new Set();
    (Array.isArray(assets) ? assets : []).forEach((asset) => {
      const key = String(asset?.id || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      next.push(asset);
    });
    (Array.isArray(recentUploadedAssets) ? recentUploadedAssets : []).forEach((asset) => {
      const key = String(asset?.id || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      next.push(asset);
    });
    return next;
  }, [assets, recentUploadedAssets]);
  const imageAssets = useMemo(
    () => availableAssets.filter((asset) => isImageAsset(asset)),
    [availableAssets]
  );
  const imageAssetById = useMemo(() => {
    const map = new Map();
    for (const asset of imageAssets) {
      if (!asset?.id) continue;
      map.set(String(asset.id), asset);
    }
    return map;
  }, [imageAssets]);

  const previewAsset = useMemo(() => {
    const id = photoPreviewId || (photoAssetIds.length ? photoAssetIds[0] : null);
    if (!id) return null;
    return imageAssets.find((asset) => String(asset.id) === String(id)) || null;
  }, [imageAssets, photoAssetIds, photoPreviewId]);

  const activePhotoIndex = useMemo(
    () => photoAssetIds.findIndex((assetId) => String(assetId) === String(photoPreviewId)),
    [photoAssetIds, photoPreviewId]
  );

  const activePhotoAssetId = activePhotoIndex >= 0
    ? photoAssetIds[activePhotoIndex]
    : (photoAssetIds.length ? photoAssetIds[0] : null);

  useEffect(() => {
    if (!sectionId && sections.length) setSectionId(sections[0].id);
  }, [sectionId, sections]);

  useEffect(() => {
    setSectionId(initialItem?.sectionId || '');
    setPrice(initialItem?.priceText || '');
    setTags(supportsItemTags && !isTranslation ? normalizeMenuItemTags(initialItem?.tags) : []);
    setSpiciness(supportsItemTags && !isTranslation ? normalizeMenuItemSpiciness(initialItem?.spiciness) : '');
    setPhotoAssetIds(Array.isArray(initialItem?.photoAssetIds) ? initialItem.photoAssetIds : []);
    if (isTranslation) {
      setName(translation?.name || '');
      setSubtitle(translation?.subtitle || '');
      setDescription(translation?.description || '');
    } else {
      setName(initialItem?.name || '');
      setSubtitle(initialItem?.subtitle || '');
      setDescription(initialItem?.description || '');
    }
    setPhotoUploadError('');
  }, [initialItem, isTranslation, supportsItemTags, translation]);

  useEffect(() => {
    if (!recentUploadedAssets.length) return;
    const knownIds = new Set((Array.isArray(assets) ? assets : []).map((asset) => String(asset?.id || '').trim()).filter(Boolean));
    if (!knownIds.size) return;
    setRecentUploadedAssets((prev) => prev.filter((asset) => !knownIds.has(String(asset?.id || '').trim())));
  }, [assets, recentUploadedAssets.length]);

  useEffect(() => {
    if (!photoAssetIds.length) {
      if (photoPreviewId !== null) setPhotoPreviewId(null);
      return;
    }
    const hasSelectedPreview = photoAssetIds.some((assetId) => String(assetId) === String(photoPreviewId));
    if (!hasSelectedPreview) {
      setPhotoPreviewId(photoAssetIds[0]);
    }
  }, [photoAssetIds, photoPreviewId]);

  const canGenerateCopy = Boolean((name || '').trim()) && !isTranslation;
  const hasPhoto = Boolean(photoAssetIds.length);

  const handleGenerateCopy = async () => {
    const title = (name || '').trim();
    if (!title) return;
    if (!hasPhoto) {
      const ok = window.confirm('Generate Copy needs a photo. Add a photo now?');
      if (ok) setPicker({ open: true, selectedAssetIds: [] });
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in again.');
      return;
    }

    setGenerating(true);
    try {
      const res = await fetch(`${apiBase}/item-copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          siteId,
          assetId: photoAssetIds[0],
          title,
          length: Number(copyLength),
          style: String(copyStyle || '').trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Generate failed (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      if (typeof data.subtitle === 'string') setSubtitle(data.subtitle);
      if (typeof data.description === 'string') setDescription(data.description);
    } catch (err) {
      console.error('Failed to generate menu item copy', err);
      alert(err.message || 'Failed to generate copy.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      alert('Item name is required.');
      return;
    }
    if (!sectionId) {
      alert('Please choose a section.');
      return;
    }
    setSaving(true);
    try {
      if (isTranslation) {
        const nextTranslation = {
          id: initialItem?.id || null,
          name: trimmedName,
          subtitle: (subtitle || '').trim(),
          description: (description || '').trim(),
        };
        await onSaveTranslation?.(nextTranslation);
      } else {
        const next = {
          id: initialItem?.id || null,
          sectionId,
          name: trimmedName,
          subtitle: (subtitle || '').trim(),
          description: (description || '').trim(),
          priceText: (price || '').trim(),
          tags: supportsItemTags ? normalizeMenuItemTags(tags) : [],
          spiciness: supportsItemTags ? normalizeMenuItemSpiciness(spiciness) : '',
          photoAssetIds: photoAssetIds.filter(Boolean),
          enabled: true,
          sort_order: initialItem?.id ? initialItem?.sort_order : undefined,
        };
        await onSave?.(next);
      }
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = (assetId) => {
    setPhotoAssetIds((prev) => prev.filter((id) => String(id) !== String(assetId)));
  };

  const movePhoto = (assetId, direction) => {
    setPhotoAssetIds((prev) => {
      const index = prev.findIndex((id) => String(id) === String(assetId));
      if (index === -1) return prev;
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const reorderPhotosByDrop = useCallback((sourceAssetId, targetAssetId, position = 'before') => {
    if (!sourceAssetId || !targetAssetId) return;
    setPhotoAssetIds((prev) => reorderListByKeys(
      prev,
      String(sourceAssetId),
      String(targetAssetId),
      (assetId) => String(assetId),
      position
    ));
  }, []);

  const handlePastePhoto = useCallback(async () => {
    if (!siteId) return;
    if (!navigator?.clipboard?.read) {
      setPhotoUploadError('This browser does not allow image clipboard reads for Paste Photo.');
      return;
    }

    setPastingPhoto(true);
    setPhotoUploadError('');
    try {
      const clipboardItems = await navigator.clipboard.read();
      let clipboardBlob = null;

      for (const item of clipboardItems) {
        const imageType = (Array.isArray(item?.types) ? item.types : []).find((type) => String(type || '').toLowerCase().startsWith('image/'));
        if (!imageType) continue;
        clipboardBlob = await item.getType(imageType);
        break;
      }

      if (!clipboardBlob) {
        throw new Error('No image was found in the clipboard.');
      }

      const webpFile = await convertImageBlobToWebpFile(
        clipboardBlob,
        `pasted-photo-${Date.now()}.webp`
      );
      const uploadedAsset = await uploadImageAssetFile({
        siteId,
        file: webpFile,
        title: `${(name || initialItem?.name || itemLabel).trim() || itemLabel} Photo`,
      });

      setRecentUploadedAssets((prev) => {
        const next = [...(Array.isArray(prev) ? prev : [])];
        if (!next.some((asset) => String(asset?.id) === String(uploadedAsset.id))) {
          next.push(uploadedAsset);
        }
        return next;
      });
      setPhotoAssetIds((prev) => {
        const next = [...(Array.isArray(prev) ? prev : [])];
        if (!next.some((assetId) => String(assetId) === String(uploadedAsset.id))) {
          next.push(uploadedAsset.id);
        }
        return next;
      });
      setPhotoPreviewId(uploadedAsset.id);
      await onRefreshAssets?.();
    } catch (err) {
      console.error('Failed to paste photo from clipboard', err);
      setPhotoUploadError(err.message || 'Failed to paste photo.');
    } finally {
      setPastingPhoto(false);
    }
  }, [initialItem?.name, itemLabel, name, onRefreshAssets, siteId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4" onClick={onClose}>
      <div
        className="relative bg-gray-950 rounded-lg w-full max-w-5xl max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800 p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{initialItem?.id ? `Edit ${itemLabel}` : `New ${itemLabel}`}</div>
            <div className="text-xs text-gray-400 truncate">Saves to this {itemLabel === 'Menu Item' ? 'menu' : 'catalog'}.</div>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 bg-gray-700 rounded text-white" onClick={onClose}>Close</button>
            <button
              className="px-3 py-1 bg-indigo-600 rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-gray-900 rounded p-4 space-y-3">
              {isTranslation && (
                <div className="bg-gray-950 rounded p-3 border border-gray-800 text-xs text-gray-300">
                  Editing {languageLabel || language} translation. Photos, price, and section stay in English.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-xs text-gray-300 mb-1">Section</div>
                  <select
                    className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                    value={sectionId}
                    onChange={(e) => setSectionId(e.target.value)}
                    disabled={isTranslation}
                  >
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {getSectionLabel?.(section) || section.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <div className="text-xs text-gray-300 mb-1">Price</div>
                  <input
                    className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g. £12.50"
                    disabled={isTranslation}
                  />
                </label>
              </div>

              <label className="block">
                <div className="text-xs text-gray-300 mb-1">Item Name</div>
                <input
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={isTranslation ? initialItem?.name || 'Translated item name' : 'e.g. Margherita Pizza'}
                />
              </label>

              {supportsItemTags && !isTranslation && (
                <div className="rounded border border-gray-800 bg-gray-950 p-3">
                  <div className="text-sm font-semibold text-gray-100">Item Tags</div>
                  <div className="mt-1 text-xs text-gray-400">
                    These drive landing page filters and the optional food icon badges.
                  </div>
                  <div className="mt-3">
                    <MenuItemTagPicker value={tags} availableTagKeys={availableTagKeys} language={language} onChange={setTags} />
                  </div>
                  <label className="mt-4 block">
                    <div className="text-xs text-gray-300 mb-1">Spiciness</div>
                    <select
                      className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                      value={spiciness}
                      onChange={(e) => setSpiciness(normalizeMenuItemSpiciness(e.target.value))}
                    >
                      {localizedSpicinessOptions.map((option) => (
                        <option key={option.key || 'none'} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="mt-1 text-xs text-gray-500">
                      Use this when the menu item explicitly shows a spice level or chili indicator.
                    </div>
                  </label>
                </div>
              )}

              {canGenerateCopy && (
                <div className="bg-gray-950 rounded p-3 border border-gray-800">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-100">Generate Copy</div>
                      <div className="text-xs text-gray-400">
                        Requires a photo. Generates subtitle + description you can edit before saving.
                      </div>
                    </div>
                    <button
                      className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                      onClick={handleGenerateCopy}
                      disabled={generating || saving}
                    >
                      {generating ? 'Generating…' : 'Generate Copy'}
                    </button>
                  </div>

                  {!hasPhoto && (
                    <div className="mt-2 text-xs text-yellow-200 bg-yellow-900/20 border border-yellow-800 rounded p-2">
                      Add at least one photo to enable copy generation.
                      <button
                        className="ml-2 underline text-yellow-100"
                        type="button"
                        onClick={() => setPicker({ open: true, selectedAssetId: null })}
                      >
                        Add Photo
                      </button>
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="text-xs text-gray-300 mb-1">Length (1-5 sentences)</div>
                      <input
                        type="number"
                        min={1}
                        max={5}
                        className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                        value={copyLength}
                        onChange={(e) => setCopyLength(e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <div className="text-xs text-gray-300 mb-1">Style</div>
                      <select
                        className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                        value={copyStyle}
                        onChange={(e) => setCopyStyle(e.target.value)}
                      >
                        <option value="Formal">Formal</option>
                        <option value="Informative">Informative</option>
                        <option value="Informal">Informal</option>
                        <option value="Playful">Playful</option>
                      </select>
                    </label>
                  </div>
                </div>
              )}

              <label className="block">
                <div className="text-xs text-gray-300 mb-1">Subtitle (optional)</div>
                <input
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder={isTranslation ? initialItem?.subtitle || 'Translated subtitle' : 'e.g. Classic Neapolitan'}
                />
              </label>

              <label className="block">
                <div className="text-xs text-gray-300 mb-1">Description</div>
                <textarea
                  className="w-full min-h-28 px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={isTranslation ? initialItem?.description || 'Translated description' : 'Short description of the item…'}
                />
              </label>

              {isTranslation && initialItem && (
                <div className="bg-gray-950 rounded p-3 border border-gray-800 text-xs text-gray-300 space-y-2">
                  <div className="text-gray-200 font-semibold">English reference</div>
                  <div><span className="text-gray-400">Name:</span> {initialItem.name || '—'}</div>
                  <div><span className="text-gray-400">Subtitle:</span> {initialItem.subtitle || '—'}</div>
                  <div><span className="text-gray-400">Description:</span> {initialItem.description || '—'}</div>
                </div>
              )}
            </div>

            <div className="bg-gray-900 rounded p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="text-lg font-semibold">Photos</div>
                  <div className="text-xs text-gray-400">
                    Only the active thumbnail loads here. The landing page stays on the first photo unless auto-rotate is enabled in Settings.
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    className="px-3 py-1 rounded bg-amber-600 text-white disabled:bg-amber-900 disabled:text-gray-400"
                    onClick={handlePastePhoto}
                    disabled={!siteId || isTranslation || pastingPhoto}
                    title={!siteId ? 'Select a site first.' : 'Paste and upload the current clipboard image.'}
                  >
                    {pastingPhoto ? 'Pasting…' : 'Paste Photo'}
                  </button>
                  <button
                    className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                    onClick={() => setPicker({ open: true, selectedAssetIds: [] })}
                    disabled={!siteId || isTranslation}
                  >
                    Add Photos
                  </button>
                </div>
              </div>

              {photoUploadError ? (
                <div className="mb-3 rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-200">
                  {photoUploadError}
                </div>
              ) : null}

              {!photoAssetIds.length && (
                <div className="text-sm text-gray-400">No photos selected.</div>
              )}

              {!!photoAssetIds.length && (
                <div className="space-y-3">
                  <PhotoThumbPager
                    assetIds={photoAssetIds}
                    imageAssetById={imageAssetById}
                    activeAssetId={activePhotoAssetId}
                    onActiveAssetIdChange={setPhotoPreviewId}
                    onThumbClick={setPhotoPreviewId}
                    size={52}
                    titlePrefix="Photo"
                  />
                  <div className="flex flex-col gap-3 rounded border border-gray-700 bg-gray-800/60 px-3 py-3 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0 text-sm text-gray-200">
                      <div className="font-medium">
                        Photo {activePhotoIndex >= 0 ? activePhotoIndex + 1 : 1} of {photoAssetIds.length}
                      </div>
                      <button
                        type="button"
                        className="truncate text-xs text-indigo-200 underline"
                        onClick={() => activePhotoAssetId && setPhotoPreviewId(activePhotoAssetId)}
                        title="Preview this photo"
                      >
                        Asset #{activePhotoAssetId}
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                        onClick={() => activePhotoAssetId && movePhoto(activePhotoAssetId, -1)}
                        disabled={activePhotoIndex <= 0 || isTranslation}
                      >
                        Up
                      </button>
                      <button
                        className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                        onClick={() => activePhotoAssetId && movePhoto(activePhotoAssetId, 1)}
                        disabled={activePhotoIndex < 0 || activePhotoIndex === photoAssetIds.length - 1 || isTranslation}
                      >
                        Down
                      </button>
                      <button
                        className="px-2 py-1 bg-red-700 text-xs rounded text-white disabled:bg-red-900 disabled:text-gray-400"
                        onClick={() => activePhotoAssetId && removePhoto(activePhotoAssetId)}
                        disabled={!activePhotoAssetId || isTranslation}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {photoAssetIds.map((assetId, index) => {
                      const asset = imageAssetById.get(String(assetId)) || null;
                      const isActive = String(activePhotoAssetId || '') === String(assetId);
                      const isDropTarget = photoDropIndicator.assetId === String(assetId);
                      return (
                        <div
                          key={`photo-grid-${assetId}-${index}`}
                          className={`rounded border px-2 py-2 transition ${
                            isActive
                              ? 'border-indigo-500 bg-indigo-950/30'
                              : 'border-gray-700 bg-gray-900/70'
                          } ${
                            isDropTarget
                              ? (photoDropIndicator.position === 'after' ? 'border-b-2 border-b-sky-400' : 'border-t-2 border-t-sky-400')
                              : ''
                          }`}
                          onDragOver={(event) => {
                            if (!draggingPhotoAssetId || String(draggingPhotoAssetId) === String(assetId)) return;
                            event.preventDefault();
                            setPhotoDropIndicator({
                              assetId: String(assetId),
                              position: resolveDropPosition(event),
                            });
                          }}
                          onDrop={(event) => {
                            if (!draggingPhotoAssetId || String(draggingPhotoAssetId) === String(assetId)) return;
                            event.preventDefault();
                            reorderPhotosByDrop(draggingPhotoAssetId, assetId, resolveDropPosition(event));
                            setDraggingPhotoAssetId(null);
                            setPhotoDropIndicator({ assetId: null, position: 'before' });
                          }}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <button
                              type="button"
                              className="cursor-grab rounded border border-gray-700 bg-gray-800 px-2 py-1 text-[11px] uppercase tracking-[0.08em] text-gray-300 active:cursor-grabbing disabled:cursor-not-allowed disabled:text-gray-500"
                              draggable={!isTranslation}
                              onDragStart={() => setDraggingPhotoAssetId(String(assetId))}
                              onDragEnd={() => {
                                setDraggingPhotoAssetId(null);
                                setPhotoDropIndicator({ assetId: null, position: 'before' });
                              }}
                              disabled={isTranslation}
                            >
                              Drag
                            </button>
                            <button
                              type="button"
                              className={`rounded px-2 py-1 text-[11px] ${isActive ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-200'}`}
                              onClick={() => setPhotoPreviewId(assetId)}
                            >
                              Preview
                            </button>
                          </div>
                          <MiniAssetThumb
                            assetId={assetId}
                            asset={asset}
                            size={120}
                            className="w-full"
                            title={`Photo ${index + 1}`}
                            onClick={() => setPhotoPreviewId(assetId)}
                          />
                          <div className="mt-2 text-[11px] text-gray-400">
                            Photo {index + 1}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 self-start lg:sticky lg:top-4">
            <SelectedAssetPreview asset={previewAsset} />
            <div className="bg-gray-900 rounded p-4">
              <div className="text-lg font-semibold mb-2">Notes</div>
              <div className="text-sm text-gray-400 space-y-2">
                <div>- Only the visible photo thumbnail loads in this editor.</div>
                <div>- Landing pages show the first photo by default. Visitors can use the arrows to load more.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {picker.open && (
        <AssetPickerModal
          title={`Add Photos to ${itemLabel}`}
          siteId={siteId}
          assets={assets}
          selectedAssetIds={picker.selectedAssetIds}
          onChangeSelectedAssetIds={(updater) => {
            setPicker((prev) => {
              const current = Array.isArray(prev.selectedAssetIds) ? prev.selectedAssetIds : [];
              const next = typeof updater === 'function' ? updater(current) : updater;
              return { ...prev, selectedAssetIds: Array.isArray(next) ? next : current };
            });
          }}
          confirmLabel="Photos"
          onConfirm={() => {
            const selected = Array.isArray(picker.selectedAssetIds) ? picker.selectedAssetIds : [];
            if (!selected.length) return;
            setPhotoAssetIds((prev) => {
              const existing = Array.isArray(prev) ? prev : [];
              const next = [...existing];
              for (const id of selected) {
                if (next.some((v) => String(v) === String(id))) continue;
                next.push(id);
              }
              return next;
            });
            if (!photoPreviewId && selected.length) setPhotoPreviewId(selected[0]);
            setPicker({ open: false, selectedAssetIds: [] });
          }}
          onClose={() => setPicker({ open: false, selectedAssetIds: [] })}
          onRefreshAssets={onRefreshAssets}
        />
      )}
    </div>
  );
}

export default function MenuItems({ site, area, assets, onRefreshAssets, catalogType = 'menu', onEditWidget, onCatalogLoaded }) {
  const siteId = site?.id || null;
  const areaId = area?.id || null;
  const token = localStorage.getItem('token');
  const { enqueueFiles } = useUploadQueue();
  const isAdmin = getUserRoleFromToken(token) === 'admin';
  const uploadMenuInputRef = useRef(null);
  const menuPasteTargetRef = useRef(null);
  const catalogLoadRequestRef = useRef(null);
  useEffect(() => () => { catalogLoadRequestRef.current?.abort(); }, []);
  const catalogConfig = useMemo(() => getCatalogConfig(catalogType), [catalogType]);
  const isMenuCatalog = catalogConfig.catalogType === 'menu';
  const supportsSharedSourceCatalogs = catalogConfig.catalogType === 'products';
  const supportsCampaignAdvertising = catalogConfig.catalogType === 'menu'
    || catalogConfig.catalogType === 'products'
    || catalogConfig.catalogType === 'services';
  const supportsCatalogMonetisation = catalogConfig.catalogType === 'menu'
    || catalogConfig.catalogType === 'products'
    || catalogConfig.catalogType === 'services';
  const itemLabel = useMemo(() => {
    if (catalogConfig.catalogType === 'products') return 'Product';
    if (catalogConfig.catalogType === 'services') return 'Service';
    return 'Menu Item';
  }, [catalogConfig.catalogType]);
  const itemLabelPlural = useMemo(() => {
    if (itemLabel === 'Menu Item') return 'Menu Items';
    if (itemLabel === 'Service') return 'Services';
    return `${itemLabel}s`;
  }, [itemLabel]);
  const [view, setView] = useState('settings');
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [downloadingCatalogQr, setDownloadingCatalogQr] = useState(false);
  const [error, setError] = useState('');
  const [menuImporting, setMenuImporting] = useState(false);
  const [gordonTask, setGordonTask] = useState('');
  const [gordonInstructions, setGordonInstructions] = useState('');
  const [menuSourcesUploading, setMenuSourcesUploading] = useState(false);
  useEffect(() => { setGordonTask(''); }, [areaId, siteId]);
  const [menuImportMessage, setMenuImportMessage] = useState('');
  const [menuImportError, setMenuImportError] = useState('');
  const [menuImportJob, setMenuImportJob] = useState(null);
  const [recentMenuUploadAssetIds, setRecentMenuUploadAssetIds] = useState([]);
  const [recentMenuTextEntries, setRecentMenuTextEntries] = useState([]);
  const [menuPastePanelOpen, setMenuPastePanelOpen] = useState(false);
  const [menuPasteDetection, setMenuPasteDetection] = useState('');
  const [menu, setMenu] = useState(null);
  const [sectionHeaders, setSectionHeaders] = useState([]);
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [campaignAds, setCampaignAds] = useState([]);
  const [campaignAdsLoading, setCampaignAdsLoading] = useState(false);
  const [campaignAdsError, setCampaignAdsError] = useState('');
  const [campaignAdsFilter, setCampaignAdsFilter] = useState({ q: '' });
  const [campaignAdsDynamicMode, setCampaignAdsDynamicMode] = useState(false);
  const [selectedCampaignAdId, setSelectedCampaignAdId] = useState(null);
  const [selectedCampaignAdIds, setSelectedCampaignAdIds] = useState([]);
  const [campaignSectionKickerEnabled, setCampaignSectionKickerEnabled] = useState(true);
  const [campaignSectionKickerLabel, setCampaignSectionKickerLabel] = useState(DEFAULT_CATALOG_CAMPAIGN_SECTION_KICKER_LABEL);
  const [advertisingOpen, setAdvertisingOpen] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const [widgetsError, setWidgetsError] = useState('');
  const [widgetFilter, setWidgetFilter] = useState({ q: '', status: 'all', partner: 'all', tags: [] });
  const [widgetPicker, setWidgetPicker] = useState({ open: false, sectionId: null });
  const [selectedWidgetId, setSelectedWidgetId] = useState(null);
  const [defaults, setDefaults] = useState({ logoCreativeId: null });
  const monetisationPolicy = menu?.monetisation || defaults?.monetisation || null;
  const monetisationIsEnforced = supportsCatalogMonetisation
    && !isAdmin
    && Boolean(monetisationPolicy?.isEnforced);
  const [translationsState, setTranslationsState] = useState({
    loading: false,
    saving: false,
    starting: false,
    error: '',
    availableLanguages: [],
    enabledLanguages: ['en'],
    stats: [],
    runningJobId: null,
    jobId: null,
    job: null,
    estimate: null,
  });
  const [socialTranslationState, setSocialTranslationState] = useState({
    translating: false,
    error: '',
  });
  const [translationOptions, setTranslationOptions] = useState({
    maintainTitles: true,
    translateSectionHeaders: true,
    includeManualEdits: false,
  });
  const [activeSocialTranslationLanguage, setActiveSocialTranslationLanguage] = useState('');
  const [manualSocialTranslationLanguage, setManualSocialTranslationLanguage] = useState('');
  const [activeLanguage, setActiveLanguage] = useState('en');
  const [languageData, setLanguageData] = useState({
    loading: false,
    error: '',
    languageCode: 'en',
    sectionHeaders: [],
    sections: [],
    items: [],
  });

  const [settingsDirty, setSettingsDirty] = useState(false);
  const [sectionHeadersDraft, setSectionHeadersDraft] = useState([]);
  const [sectionsDraft, setSectionsDraft] = useState([]);
  const [campaignSectionsDraft, setCampaignSectionsDraft] = useState([]);
  const [sectionHeaderName, setSectionHeaderName] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [displaySettingsDraft, setDisplaySettingsDraft] = useState(() => resolveCatalogDisplaySettingsDraft({
    catalog: null,
    site,
    area,
    catalogConfig,
    items: [],
  }));
  const [contentModeDraft, setContentModeDraft] = useState('native');
  const [sourceCatalogIdDraft, setSourceCatalogIdDraft] = useState(null);
  const [sourceCatalogOptions, setSourceCatalogOptions] = useState([]);
  const [sourceCatalogsLoading, setSourceCatalogsLoading] = useState(false);
  const [sourceCatalogsError, setSourceCatalogsError] = useState('');
  const [logoCreativeIdDraft, setLogoCreativeIdDraft] = useState(null);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const [editor, setEditor] = useState({ open: false, item: null });
  const [sectionHeaderTranslationEditor, setSectionHeaderTranslationEditor] = useState({ open: false, sectionHeader: null });
  const [sectionTranslationEditor, setSectionTranslationEditor] = useState({ open: false, section: null });
  const [qrPreview, setQrPreview] = useState({ open: false, title: '', url: '' });
  const [tableNumber, setTableNumber] = useState('');
  const [tableLabel, setTableLabel] = useState('');
  const [tableJumpSectionId, setTableJumpSectionId] = useState('');
  const [tableJumpItemId, setTableJumpItemId] = useState('');
  const [tableCountDraft, setTableCountDraft] = useState('');
  const [tableLabelsDraft, setTableLabelsDraft] = useState({});
  const [bulkQrPreview, setBulkQrPreview] = useState({ open: false, title: '', entries: [] });
  const autoViewSetRef = useRef(false);
  const menuUploadCaptureRef = useRef(null);
  const [monetisationModeDraft, setMonetisationModeDraft] = useState('off');
  const [monetisationTypesDraft, setMonetisationTypesDraft] = useState([]);
  const [selectedSectionKeys, setSelectedSectionKeys] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [movingItemIds, setMovingItemIds] = useState([]);
  const [draggingSectionHeaderKey, setDraggingSectionHeaderKey] = useState('');
  const [sectionHeaderDropIndicator, setSectionHeaderDropIndicator] = useState({ key: '', position: 'before' });
  const [draggingSectionEditorKey, setDraggingSectionEditorKey] = useState('');
  const [sectionEditorDropIndicator, setSectionEditorDropIndicator] = useState({ key: '', position: 'before' });
  const [draggingItemState, setDraggingItemState] = useState({ sectionId: null, itemId: null });
  const [itemDropIndicator, setItemDropIndicator] = useState({ sectionId: null, itemId: null, position: 'before' });
  const [openSectionIds, setOpenSectionIds] = useState([]);
  const [moveModal, setMoveModal] = useState({ open: false, mode: 'items' });

  const parseTableLabels = useCallback((value) => {
    const parse = (input) => {
      if (!input) return {};
      if (typeof input === 'object') {
        if (Array.isArray(input)) {
          const next = {};
          for (const row of input) {
            const number = normalizeId(row?.number);
            const label = String(row?.label || '').trim();
            if (!number || !label) continue;
            next[String(number)] = label;
          }
          return next;
        }
        const next = {};
        for (const [key, label] of Object.entries(input)) {
          const number = normalizeId(key);
          const cleaned = String(label || '').trim();
          if (!number || !cleaned) continue;
          next[String(number)] = cleaned;
        }
        return next;
      }
      if (typeof input === 'string') {
        try {
          const parsed = JSON.parse(input);
          return parse(parsed);
        } catch (err) {
          return {};
        }
      }
      return {};
    };
    return parse(value);
  }, []);

  const imageAssets = useMemo(
    () => (Array.isArray(assets) ? assets.filter((asset) => isImageAsset(asset)) : []),
    [assets]
  );

  useEffect(() => {
    const capture = menuUploadCaptureRef.current;
    if (!capture) return;
    const newIds = imageAssets
      .map((asset) => String(asset?.id || '').trim())
      .filter(Boolean)
      .filter((id) => !capture.knownIds.has(id));
    if (!newIds.length) return;

    newIds.forEach((id) => capture.knownIds.add(id));
    capture.capturedCount += newIds.length;
    setRecentMenuUploadAssetIds((prev) => {
      const next = new Set((Array.isArray(prev) ? prev : []).map((id) => String(id)));
      newIds.forEach((id) => next.add(String(id)));
      return Array.from(next);
    });
    if (capture.expectedCount && capture.capturedCount >= capture.expectedCount) {
      menuUploadCaptureRef.current = null;
    }
  }, [imageAssets]);

  const imageAssetById = useMemo(() => {
    const map = new Map();
    for (const asset of imageAssets) {
      if (!asset?.id) continue;
      map.set(String(asset.id), asset);
    }
    return map;
  }, [imageAssets]);

  const pendingMenuImageCount = recentMenuUploadAssetIds.length;
  const pendingMenuTextCount = recentMenuTextEntries.length;

  const selectedLogoAsset = useMemo(() => {
    if (!logoCreativeIdDraft) return null;
    return imageAssets.find((asset) => String(asset.id) === String(logoCreativeIdDraft)) || null;
  }, [imageAssets, logoCreativeIdDraft]);

  const availableCatalogSocialEntries = useMemo(
    () => buildAvailableCatalogSocialEntries(site),
    [site]
  );

  const availableCatalogSocialEntryByKey = useMemo(() => {
    const map = new Map();
    availableCatalogSocialEntries.forEach((entry) => {
      map.set(entry.key, entry);
    });
    return map;
  }, [availableCatalogSocialEntries]);

  const defaultHeaderTitle = useMemo(
    () => resolveCatalogTitleFallback({ catalog: menu, site, catalogConfig }),
    [catalogConfig, menu, site]
  );

  const defaultHeaderSubtitle = useMemo(
    () => resolveCatalogSubtitleFallback({ site, area }),
    [area, site]
  );

  const siteDefaultMonetisationMode = useMemo(
    () => normalizeMonetisationMode(
      site?.defaultMonetisationMode || site?.default_monetisation_mode || defaults?.defaultMonetisationMode || defaults?.default_monetisation_mode || 'off',
      'off'
    ),
    [
      defaults?.defaultMonetisationMode,
      defaults?.default_monetisation_mode,
      site?.defaultMonetisationMode,
      site?.default_monetisation_mode,
    ]
  );

  const savedMonetisationMode = useMemo(
    () => normalizeMonetisationMode(
      menu?.monetisationMode || menu?.monetisation_mode || siteDefaultMonetisationMode,
      'off'
    ),
    [menu?.monetisationMode, menu?.monetisation_mode, siteDefaultMonetisationMode]
  );
  const savedMonetisationTypes = useMemo(
    () => normalizeMonetisationTypeIds(menu?.monetisationTypes || menu?.monetisation_types_json),
    [menu?.monetisationTypes, menu?.monetisation_types_json]
  );

  const effectiveMonetisationMode = useMemo(
    () => (monetisationIsEnforced
      ? normalizeMonetisationMode(monetisationPolicy?.effectiveMode, 'off')
      : normalizeMonetisationMode(monetisationModeDraft, siteDefaultMonetisationMode)),
    [monetisationIsEnforced, monetisationModeDraft, monetisationPolicy?.effectiveMode, siteDefaultMonetisationMode]
  );
  const effectiveMonetisationTypes = useMemo(
    () => (effectiveMonetisationMode === 'custom'
      ? normalizeMonetisationTypeIds(monetisationTypesDraft)
      : getDefaultMonetisationTypeIdsForMode(effectiveMonetisationMode)),
    [effectiveMonetisationMode, monetisationTypesDraft]
  );

  const enabledSections = useMemo(() => {
    const safe = Array.isArray(sections) ? sections : [];
    return safe
      .filter((section) => Boolean(section.enabled))
      .slice()
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [sections]);

  const sortedSectionHeadersDraft = useMemo(
    () => sortSectionHeadersByOrder(sectionHeadersDraft),
    [sectionHeadersDraft]
  );

  const sortedSectionsDraft = useMemo(
    () => supportsSharedSourceCatalogs && normalizeCatalogContentMode(contentModeDraft) === 'shared'
      ? sortSectionsByOrder(sectionsDraft)
      : groupSectionsByHeading(sectionsDraft, sectionHeadersDraft),
    [sectionsDraft, sectionHeadersDraft, supportsSharedSourceCatalogs, contentModeDraft]
  );

  const sectionGroupingPending = useMemo(() => {
    const savedDraftOrder = sortSectionsByOrder(sectionsDraft);
    return sortedSectionsDraft.some((section, index) => getSectionDraftKey(section) !== getSectionDraftKey(savedDraftOrder[index]));
  }, [sectionsDraft, sortedSectionsDraft]);

  const handleMonetisationModeChange = useCallback((value) => {
    const nextMode = normalizeMonetisationMode(value, 'off');
    const previousMode = normalizeMonetisationMode(monetisationModeDraft, 'off');
    setMonetisationModeDraft(nextMode);
    if (nextMode === 'custom') {
      setMonetisationTypesDraft((current) => (
        current.length ? current : getDefaultMonetisationTypeIdsForMode(previousMode)
      ));
    }
    setSettingsDirty(true);
  }, [monetisationModeDraft]);

  const handleMonetisationTypeToggle = useCallback((typeId) => {
    setMonetisationTypesDraft((current) => (
      current.includes(typeId)
        ? current.filter((entry) => entry !== typeId)
        : [...current, typeId]
    ));
    setSettingsDirty(true);
  }, []);

  const savedSectionHeaderById = useMemo(() => {
    const map = new Map();
    (Array.isArray(sectionHeaders) ? sectionHeaders : []).forEach((sectionHeader) => {
      if (!sectionHeader?.id) return;
      map.set(String(sectionHeader.id), sectionHeader);
    });
    return map;
  }, [sectionHeaders]);

  const sectionHeaderDraftByKey = useMemo(() => {
    const map = new Map();
    (Array.isArray(sectionHeadersDraft) ? sectionHeadersDraft : []).forEach((sectionHeader) => {
      map.set(getSectionHeaderDraftKey(sectionHeader), sectionHeader);
    });
    return map;
  }, [sectionHeadersDraft]);

  const sectionHeaderDraftUsageByKey = useMemo(() => {
    const map = new Map();
    (Array.isArray(sectionsDraft) ? sectionsDraft : []).forEach((section) => {
      const key = String(section?.headerKey || '').trim();
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [sectionsDraft]);

  const socialSectionDraft = useMemo(
    () => normalizeCatalogSocialSectionDraft(displaySettingsDraft?.socialSection),
    [displaySettingsDraft]
  );
  const savedSocialSection = useMemo(
    () => normalizeCatalogSocialSectionDraft(menu?.displaySettings?.socialSection),
    [menu?.displaySettings?.socialSection]
  );

  const menuFeaturesDraft = useMemo(() => {
    const raw = displaySettingsDraft?.menuFeatures && typeof displaySettingsDraft.menuFeatures === 'object'
      ? displaySettingsDraft.menuFeatures
      : {};
    const enabledTagKeys = resolveMenuEnabledTagKeys(raw);
    return {
      showItemTags: resolveMenuShowItemTags(raw, items, enabledTagKeys),
      enabledTagKeys,
      enabledFilterKeys: resolveMenuFilterKeys(raw, items, enabledTagKeys),
      autoRotatePhotos: Boolean(raw.autoRotatePhotos ?? raw.auto_rotate_photos),
      tagIconSet: normalizeMenuTagIconSet(raw.tagIconSet ?? raw.tag_icon_set),
      customIconUrls: normalizeMenuCustomIconMap(raw.customIconUrls ?? raw.custom_icon_urls),
    };
  }, [displaySettingsDraft, items]);

  const selectedCatalogSocialEntries = useMemo(
    () => socialSectionDraft.linkKeys
      .map((key) => availableCatalogSocialEntryByKey.get(key) || null)
      .filter(Boolean),
    [availableCatalogSocialEntryByKey, socialSectionDraft]
  );
  const availableCatalogSocialSectionEntries = useMemo(
    () => availableCatalogSocialEntries.map((entry) => ({
      ...entry,
      ctaLabel: resolveCatalogSocialEntryTitle(entry, socialSectionDraft),
    })),
    [availableCatalogSocialEntries, socialSectionDraft]
  );
  const selectedCatalogSocialSectionEntries = useMemo(
    () => selectedCatalogSocialEntries.map((entry) => ({
      ...entry,
      ctaLabel: resolveCatalogSocialEntryTitle(entry, socialSectionDraft),
    })),
    [selectedCatalogSocialEntries, socialSectionDraft]
  );
  const selectedCatalogHeaderSocialEntries = useMemo(
    () => socialSectionDraft.headerLinkKeys
      .map((key) => availableCatalogSocialEntryByKey.get(key) || null)
      .filter(Boolean),
    [availableCatalogSocialEntryByKey, socialSectionDraft]
  );
  const languageLabelMap = useMemo(() => {
    const map = new Map();
    (translationsState.availableLanguages || []).forEach((lang) => {
      const code = String(lang.code || '').trim();
      const normalized = code.toLowerCase();
      if (!code) return;
      map.set(normalized, lang.label || code);
    });
    return map;
  }, [translationsState.availableLanguages]);
  const getCatalogLanguageLabel = useCallback((languageCode) => {
    const normalized = normalizeCatalogLanguageCode(languageCode);
    return languageLabelMap.get(normalized.toLowerCase()) || normalized || 'Language';
  }, [languageLabelMap]);
  const socialTranslationRows = useMemo(
    () => normalizeCatalogSocialTranslationsDraft(
      socialSectionDraft.translations,
      normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en')
    ),
    [socialSectionDraft.sourceLanguageCode, socialSectionDraft.translations]
  );
  const suggestedCatalogSocialTranslationLanguages = useMemo(() => {
    const sourceLanguageCode = normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en');
    const enabled = Array.isArray(translationsState.enabledLanguages) ? translationsState.enabledLanguages : ['en'];
    return Array.from(new Set([
      ...DEFAULT_SOCIAL_SUGGESTED_LANGUAGE_CODES,
      ...enabled,
    ]))
      .filter((languageCode) => !sameCatalogLanguageCode(languageCode, sourceLanguageCode))
      .sort((left, right) => {
        const leftPinned = DEFAULT_SOCIAL_SUGGESTED_LANGUAGE_CODES.findIndex((code) => sameCatalogLanguageCode(code, left));
        const rightPinned = DEFAULT_SOCIAL_SUGGESTED_LANGUAGE_CODES.findIndex((code) => sameCatalogLanguageCode(code, right));
        if (leftPinned !== -1 || rightPinned !== -1) {
          if (leftPinned === -1) return 1;
          if (rightPinned === -1) return -1;
          if (leftPinned !== rightPinned) return leftPinned - rightPinned;
        }
        return compareLanguageCodesForDisplay(left, right);
      });
  }, [socialSectionDraft.sourceLanguageCode, translationsState.enabledLanguages]);
  const catalogSocialTranslationLanguageOptions = useMemo(() => {
    const sourceLanguageCode = normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en');
    const labels = new Map();
    (translationsState.availableLanguages || []).forEach((language) => {
      const code = normalizeCatalogLanguageCode(language?.code);
      if (!code || sameCatalogLanguageCode(code, sourceLanguageCode)) return;
      labels.set(code, language?.label || code);
    });
    (translationsState.enabledLanguages || []).forEach((code) => {
      const normalizedCode = normalizeCatalogLanguageCode(code);
      if (!normalizedCode || sameCatalogLanguageCode(normalizedCode, sourceLanguageCode)) return;
      if (!labels.has(normalizedCode)) labels.set(normalizedCode, getCatalogLanguageLabel(normalizedCode));
    });
    socialTranslationRows.forEach((row) => {
      if (!labels.has(row.languageCode)) labels.set(row.languageCode, getCatalogLanguageLabel(row.languageCode));
    });
    return Array.from(labels.entries())
      .map(([code, label]) => ({ code, label }))
      .sort((left, right) => compareLanguageCodesForDisplay(left.code, right.code));
  }, [
    getCatalogLanguageLabel,
    socialSectionDraft.sourceLanguageCode,
    socialTranslationRows,
    translationsState.availableLanguages,
    translationsState.enabledLanguages,
  ]);
  const selectedCatalogSocialTranslation = useMemo(
    () => socialTranslationRows.find((row) => sameCatalogLanguageCode(row.languageCode, activeSocialTranslationLanguage)) || null,
    [activeSocialTranslationLanguage, socialTranslationRows]
  );

  const missingSelectedCatalogSocialCount = useMemo(
    () => socialSectionDraft.linkKeys.filter((key) => !availableCatalogSocialEntryByKey.has(key)).length,
    [availableCatalogSocialEntryByKey, socialSectionDraft]
  );
  const missingSelectedCatalogHeaderSocialCount = useMemo(
    () => socialSectionDraft.headerLinkKeys.filter((key) => !availableCatalogSocialEntryByKey.has(key)).length,
    [availableCatalogSocialEntryByKey, socialSectionDraft]
  );

  const renderableSocialSectionDraft = useMemo(() => {
    const linkKeys = selectedCatalogSocialEntries.map((entry) => entry.key);
    const headerLinkKeys = selectedCatalogHeaderSocialEntries.map((entry) => entry.key);
    return {
      enabled: Boolean(socialSectionDraft.enabled),
      title: normalizeCatalogSocialSectionText(socialSectionDraft.title, {
        fallback: DEFAULT_CATALOG_SOCIAL_SECTION_TITLE,
        maxLength: 120,
      }),
      description: normalizeCatalogSocialSectionText(socialSectionDraft.description, {
        fallback: DEFAULT_CATALOG_SOCIAL_SECTION_DESCRIPTION,
        maxLength: 240,
      }),
      ctaLabels: normalizeCatalogSocialCtaLabels(socialSectionDraft.ctaLabels),
      linkKeys,
      headerLinkKeys,
      showInHeader: Boolean(socialSectionDraft.showInHeader),
      headerPosition: normalizeCatalogSocialHeaderPosition(socialSectionDraft.headerPosition),
      sortOrder: resolveCatalogSocialSortOrder(socialSectionDraft, sortedSectionsDraft.length),
      sourceLanguageCode: normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en'),
      translations: socialTranslationRows.map((row) => ({
        languageCode: row.languageCode,
        title: normalizeCatalogSocialDraftText(row.title, { fallback: '', maxLength: 120 }),
        description: normalizeCatalogSocialDraftText(row.description, { fallback: '', maxLength: 240 }),
        ctaLabels: normalizeCatalogSocialCtaLabelsDraft(row.ctaLabels, { allowEmpty: true }),
      })),
    };
  }, [
    selectedCatalogHeaderSocialEntries,
    selectedCatalogSocialEntries,
    socialSectionDraft,
    socialTranslationRows,
    sortedSectionsDraft.length,
  ]);
  const catalogSocialTranslationsReviewReasons = useMemo(() => {
    if (!socialTranslationRows.length) return [];
    const reasons = [];
    if (!sameCatalogLanguageCode(savedSocialSection.sourceLanguageCode, socialSectionDraft.sourceLanguageCode)) {
      reasons.push('source language changed');
    }
    if ((savedSocialSection.title || '') !== (socialSectionDraft.title || '')) {
      reasons.push('section title changed');
    }
    if ((savedSocialSection.description || '') !== (socialSectionDraft.description || '')) {
      reasons.push('intro text changed');
    }
    if (JSON.stringify(savedSocialSection.linkKeys || []) !== JSON.stringify(socialSectionDraft.linkKeys || [])) {
      reasons.push('selected section links changed');
    }
    const allCtaKeys = Array.from(new Set([
      ...Object.keys(savedSocialSection.ctaLabels || {}),
      ...Object.keys(socialSectionDraft.ctaLabels || {}),
    ]));
    if (allCtaKeys.some((key) => (
      normalizeCatalogSocialDraftText(savedSocialSection.ctaLabels?.[key], {
        fallback: getCatalogSocialDefaultCtaLabel(key),
        maxLength: 120,
      }) !== normalizeCatalogSocialDraftText(socialSectionDraft.ctaLabels?.[key], {
        fallback: getCatalogSocialDefaultCtaLabel(key),
        maxLength: 120,
      })
    ))) {
      reasons.push('section link titles changed');
    }
    return reasons;
  }, [
    savedSocialSection.ctaLabels,
    savedSocialSection.description,
    savedSocialSection.linkKeys,
    savedSocialSection.sourceLanguageCode,
    savedSocialSection.title,
    socialSectionDraft.ctaLabels,
    socialSectionDraft.description,
    socialSectionDraft.linkKeys,
    socialSectionDraft.sourceLanguageCode,
    socialSectionDraft.title,
    socialTranslationRows.length,
  ]);
  const catalogSocialTranslationsReviewNeeded = catalogSocialTranslationsReviewReasons.length > 0;
  const catalogSocialMissingTranslationLanguages = useMemo(() => {
    const selectedKeys = selectedCatalogSocialEntries.map((entry) => entry.key);
    return socialTranslationRows
      .filter((row) => {
        if (catalogSocialDraftFieldNeedsTranslation(socialSectionDraft.title, row.title, { maxLength: 120 })) return true;
        if (catalogSocialDraftFieldNeedsTranslation(socialSectionDraft.description, row.description, { maxLength: 240 })) return true;
        return selectedKeys.some((key) => {
          const sourceCta = normalizeCatalogSocialDraftText(socialSectionDraft.ctaLabels?.[key], {
            fallback: getCatalogSocialDefaultCtaLabel(key),
            maxLength: 120,
          });
          return catalogSocialDraftFieldNeedsTranslation(sourceCta, row.ctaLabels?.[key], { maxLength: 120 });
        });
      })
      .map((row) => row.languageCode);
  }, [selectedCatalogSocialEntries, socialSectionDraft.ctaLabels, socialSectionDraft.description, socialSectionDraft.title, socialTranslationRows]);

  const campaignAdsById = useMemo(() => {
    const map = new Map();
    (Array.isArray(campaignAds) ? campaignAds : []).forEach((campaignAd) => {
      if (campaignAd?.campaignId) map.set(String(campaignAd.campaignId), campaignAd);
    });
    return map;
  }, [campaignAds]);

  const filteredCampaignAds = useMemo(() => {
    const q = String(campaignAdsFilter.q || '').trim().toLowerCase();
    return (Array.isArray(campaignAds) ? campaignAds : []).filter((campaignAd) => {
      if (!q) return true;
      return String(campaignAd.title || '').toLowerCase().includes(q)
        || String(campaignAd.siteName || '').toLowerCase().includes(q)
        || String(campaignAd.landingKey || '').toLowerCase().includes(q);
    });
  }, [campaignAds, campaignAdsFilter]);

  const selectedCampaignAds = useMemo(
    () => (Array.isArray(selectedCampaignAdIds) ? selectedCampaignAdIds : [])
      .map((campaignId) => campaignAdsById.get(String(campaignId)) || null)
      .filter(Boolean),
    [campaignAdsById, selectedCampaignAdIds]
  );

  const selectedCampaignAd = useMemo(
    () => (selectedCampaignAdId ? campaignAdsById.get(String(selectedCampaignAdId)) || null : null),
    [campaignAdsById, selectedCampaignAdId]
  );

  const sectionEditorRows = useMemo(
    () => buildSectionEditorRows({
      sections: sortedSectionsDraft,
      campaignSections: campaignSectionsDraft,
      socialSection: renderableSocialSectionDraft,
      promotionTypeIds: effectiveMonetisationTypes,
      promotionPositions: displaySettingsDraft?.promotionPositions,
    }),
    [
      campaignSectionsDraft,
      displaySettingsDraft?.promotionPositions,
      effectiveMonetisationTypes,
      renderableSocialSectionDraft,
      sortedSectionsDraft,
    ]
  );
  const selectableSectionKeys = useMemo(
    () => sortedSectionsDraft.map((section) => getSectionDraftKey(section)),
    [sortedSectionsDraft]
  );
  const allItemIds = useMemo(
    () => (Array.isArray(items) ? items : []).map((item) => item.id).filter(Boolean),
    [items]
  );

  const sectionDraftPositionByKey = useMemo(() => {
    const map = new Map();
    sortedSectionsDraft.forEach((section, index) => {
      map.set(getSectionDraftKey(section), index);
    });
    return map;
  }, [sortedSectionsDraft]);

  const isSharedCatalogMode = supportsSharedSourceCatalogs && normalizeCatalogContentMode(contentModeDraft) === 'shared';
  const availableSourceCatalogOptions = useMemo(
    () => (Array.isArray(sourceCatalogOptions) ? sourceCatalogOptions : []).filter((sourceCatalog) => (
      normalizeCatalogContentMode(sourceCatalog?.contentMode) !== 'shared'
        && String(sourceCatalog?.catalogId || '') !== String(menu?.id || '')
    )),
    [menu?.id, sourceCatalogOptions]
  );
  const selectedSourceCatalogOption = useMemo(
    () => (Array.isArray(sourceCatalogOptions) ? sourceCatalogOptions : []).find(
      (sourceCatalog) => String(sourceCatalog?.catalogId || '') === String(sourceCatalogIdDraft || '')
    ) || null,
    [sourceCatalogIdDraft, sourceCatalogOptions]
  );
  const selectedSourceCatalogLabel = useMemo(() => {
    if (selectedSourceCatalogOption) return buildSourceCatalogOptionLabel(selectedSourceCatalogOption);
    if (menu?.sourceCatalog) return buildSourceCatalogOptionLabel(menu.sourceCatalog);
    return '';
  }, [menu?.sourceCatalog, selectedSourceCatalogOption]);
  const canEditItems = Boolean(menu?.id && !settingsDirty && !isSharedCatalogMode);
  const canManageTranslations = Boolean(menu?.id) && !settingsDirty && !isSharedCatalogMode;
  const isTranslationMode = activeLanguage !== 'en';
  const canAddItems = Boolean(enabledSections.length) && !isTranslationMode;

  const handleUploadMenuFiles = useCallback(async (event) => {
    if (!siteId) return;
    const selectedFiles = Array.from(event?.target?.files || []);
    const selectedImages = selectedFiles.filter((file) => isMenuImportImageFile(file));
    const selectedPdfs = selectedFiles.filter(isPdfMenuSource);
    const selectedTextFiles = selectedFiles.filter((file) => isMenuImportTextFile(file));

    if (!selectedImages.length && !selectedTextFiles.length && !selectedPdfs.length) {
      setMenuImportError('Use a PDF, menu image or text file.');
      if (event?.target) event.target.value = '';
      return;
    }

    setMenuImportMessage('');
    setMenuImportError('');

    if (selectedPdfs.length) {
      setMenuSourcesUploading(true);
      try {
        for (const file of selectedPdfs) {
          const asset = await uploadMenuSourceFile({ siteId, file });
          setRecentMenuUploadAssetIds((previous) => [...new Set([...previous, String(asset.id)])]);
        }
      } catch (error) { setMenuImportError(error.message); }
      finally { setMenuSourcesUploading(false); }
    }

    if (selectedImages.length) {
      menuUploadCaptureRef.current = {
        knownIds: new Set(imageAssets.map((asset) => String(asset?.id || '').trim()).filter(Boolean)),
        expectedCount: selectedImages.length,
        capturedCount: 0,
      };
      enqueueFiles(siteId, selectedImages);
    }

    if (selectedTextFiles.length) {
      const loadedEntries = await Promise.all(selectedTextFiles.map(async (file) => {
        try {
          const text = await file.text();
          return buildMenuImportTextEntry({
            text,
            title: file.name || 'Uploaded text',
            filename: file.name || '',
            mimeType: file.type || '',
          });
        } catch (err) {
          console.error('Failed to read uploaded menu text file', err);
          return null;
        }
      }));
      const nextEntries = loadedEntries.filter(Boolean);
      if (nextEntries.length) {
        setRecentMenuTextEntries((prev) => [...prev, ...nextEntries]);
      } else if (!selectedImages.length) {
        setMenuImportError('The selected text files could not be read.');
      }
    }

    if (event?.target) event.target.value = '';
  }, [enqueueFiles, imageAssets, siteId]);

  const handleMenuPaste = useCallback((event) => {
    if (!siteId) return;
    const clipboardItems = Array.from(event?.clipboardData?.items || []);
    const pastedImageFiles = clipboardItems
      .filter((item) => String(item?.type || '').toLowerCase().startsWith('image/'))
      .map((item, index) => {
        const file = item.getAsFile();
        if (!file) return null;
        if (file.name) return file;
        const extension = file.type === 'image/jpeg' ? 'jpg' : 'png';
        return new File([file], `pasted-menu-${Date.now()}-${index + 1}.${extension}`, { type: file.type || 'image/png' });
      })
      .filter(Boolean);

    if (pastedImageFiles.length) {
      event.preventDefault();
      menuUploadCaptureRef.current = {
        knownIds: new Set(imageAssets.map((asset) => String(asset?.id || '').trim()).filter(Boolean)),
        expectedCount: pastedImageFiles.length,
        capturedCount: 0,
      };
      setMenuImportMessage('');
      setMenuImportError('');
      setMenuPasteDetection('Image Detected');
      setMenuPastePanelOpen(false);
      enqueueFiles(siteId, pastedImageFiles);
      return;
    }

    const plainText = String(event?.clipboardData?.getData('text/plain') || '');
    const htmlText = String(event?.clipboardData?.getData('text/html') || '');
    const pastedText = plainText.trim() ? plainText : htmlText;
    if (!pastedText.trim()) {
      setMenuImportError('Paste menu text or a menu image to continue.');
      return;
    }

    event.preventDefault();
    const entry = buildMenuImportTextEntry({
      text: pastedText,
      title: plainText.trim() ? 'Pasted Text' : 'Pasted HTML',
      filename: plainText.trim() ? `pasted-menu-${Date.now()}.txt` : `pasted-menu-${Date.now()}.html`,
      mimeType: plainText.trim() ? 'text/plain' : 'text/html',
    });
    if (!entry) {
      setMenuImportError('Paste menu text or a menu image to continue.');
      return;
    }
    setMenuImportMessage('');
    setMenuImportError('');
    setRecentMenuTextEntries((prev) => [...prev, entry]);
    setMenuPasteDetection('Text Detected');
    setMenuPastePanelOpen(false);
    if (menuPasteTargetRef.current) {
      menuPasteTargetRef.current.value = '';
    }
  }, [enqueueFiles, imageAssets, siteId]);

  const enabledLanguageOptions = useMemo(() => {
    const enabled = Array.isArray(translationsState.enabledLanguages) ? translationsState.enabledLanguages : ['en'];
    return [...enabled]
      .sort(compareLanguageCodesForDisplay)
      .map((code) => ({
        code,
        label: languageLabelMap.get(code) || code,
      }));
  }, [languageLabelMap, translationsState.enabledLanguages]);

  const activeLanguageLabel = useMemo(
    () => languageLabelMap.get(activeLanguage) || activeLanguage,
    [activeLanguage, languageLabelMap]
  );

  const updateDisplaySettingsDraft = useCallback((patch) => {
    setDisplaySettingsDraft((prev) => ({
      ...(prev || resolveCatalogDisplaySettingsDraft({ catalog: menu, site, area, catalogConfig, items })),
      ...(typeof patch === 'function' ? patch(prev || {}) : patch),
    }));
    setSettingsDirty(true);
  }, [area, catalogConfig, items, menu, site]);

  const updateSocialSectionDraft = useCallback((updater) => {
    updateDisplaySettingsDraft((prev) => {
      const baseDisplaySettings = prev && typeof prev === 'object' ? prev : {};
      const current = normalizeCatalogSocialSectionDraft(baseDisplaySettings.socialSection);
      const next = typeof updater === 'function'
        ? updater(current)
        : { ...current, ...(updater || {}) };
      return {
        ...baseDisplaySettings,
        socialSection: normalizeCatalogSocialSectionDraft(next),
      };
    });
  }, [updateDisplaySettingsDraft]);

  const addCatalogSocialTranslation = useCallback((languageCode, { copySource = false } = {}) => {
    const normalizedLanguageCode = normalizeCatalogLanguageCode(languageCode);
    if (!normalizedLanguageCode) return;
    updateSocialSectionDraft((current) => {
      const sourceLanguageCode = normalizeCatalogLanguageCode(current?.sourceLanguageCode, 'en');
      if (sameCatalogLanguageCode(normalizedLanguageCode, sourceLanguageCode)) return current;
      const existing = normalizeCatalogSocialTranslationsDraft(current?.translations, sourceLanguageCode);
      const nextTranslations = existing.some((row) => sameCatalogLanguageCode(row.languageCode, normalizedLanguageCode))
        ? existing
        : [
          ...existing,
          {
            languageCode: normalizedLanguageCode,
            title: copySource ? String(current?.title || '') : '',
            description: copySource ? String(current?.description || '') : '',
            ctaLabels: copySource
              ? normalizeCatalogSocialCtaLabelsDraft(current?.ctaLabels, { allowEmpty: true })
              : normalizeCatalogSocialCtaLabelsDraft({}, { allowEmpty: true }),
          },
        ];
      return {
        ...current,
        translations: normalizeCatalogSocialTranslationsDraft(nextTranslations, sourceLanguageCode),
      };
    });
    setActiveSocialTranslationLanguage(normalizedLanguageCode);
    setManualSocialTranslationLanguage('');
  }, [updateSocialSectionDraft]);

  const updateCatalogSocialTranslation = useCallback((languageCode, patch) => {
    const normalizedLanguageCode = normalizeCatalogLanguageCode(languageCode);
    if (!normalizedLanguageCode) return;
    updateSocialSectionDraft((current) => {
      const sourceLanguageCode = normalizeCatalogLanguageCode(current?.sourceLanguageCode, 'en');
      const nextTranslations = normalizeCatalogSocialTranslationsDraft(current?.translations, sourceLanguageCode).map((row) => {
        if (!sameCatalogLanguageCode(row.languageCode, normalizedLanguageCode)) return row;
        return normalizeCatalogSocialTranslationRowDraft({
          ...row,
          ...(typeof patch === 'function' ? patch(row) : patch),
        }, sourceLanguageCode) || row;
      });
      return {
        ...current,
        translations: nextTranslations,
      };
    });
  }, [updateSocialSectionDraft]);

  const removeCatalogSocialTranslation = useCallback((languageCode) => {
    const normalizedLanguageCode = normalizeCatalogLanguageCode(languageCode);
    if (!normalizedLanguageCode) return;
    updateSocialSectionDraft((current) => {
      const sourceLanguageCode = normalizeCatalogLanguageCode(current?.sourceLanguageCode, 'en');
      return {
        ...current,
        translations: normalizeCatalogSocialTranslationsDraft(current?.translations, sourceLanguageCode)
          .filter((row) => !sameCatalogLanguageCode(row.languageCode, normalizedLanguageCode)),
      };
    });
    setActiveSocialTranslationLanguage((prev) => (
      sameCatalogLanguageCode(prev, normalizedLanguageCode) ? '' : prev
    ));
  }, [updateSocialSectionDraft]);

  const copyCatalogSocialSourceToTranslation = useCallback((languageCode) => {
    const normalizedLanguageCode = normalizeCatalogLanguageCode(languageCode);
    if (!normalizedLanguageCode) return;
    updateSocialSectionDraft((current) => {
      const sourceLanguageCode = normalizeCatalogLanguageCode(current?.sourceLanguageCode, 'en');
      const nextTranslations = normalizeCatalogSocialTranslationsDraft(current?.translations, sourceLanguageCode).map((row) => {
        if (!sameCatalogLanguageCode(row.languageCode, normalizedLanguageCode)) return row;
        return {
          ...row,
          title: String(current?.title || ''),
          description: String(current?.description || ''),
          ctaLabels: normalizeCatalogSocialCtaLabelsDraft(current?.ctaLabels, { allowEmpty: true }),
        };
      });
      return {
        ...current,
        translations: nextTranslations,
      };
    });
  }, [updateSocialSectionDraft]);

  const handleCatalogSocialSourceLanguageChange = useCallback((languageCode) => {
    const normalizedLanguageCode = normalizeCatalogLanguageCode(languageCode, 'en');
    updateSocialSectionDraft((current) => {
      const nextTranslations = normalizeCatalogSocialTranslationsDraft(
        current?.translations,
        normalizedLanguageCode
      ).filter((row) => !sameCatalogLanguageCode(row.languageCode, normalizedLanguageCode));
      return {
        ...current,
        sourceLanguageCode: normalizedLanguageCode,
        translations: nextTranslations,
      };
    });
    setActiveSocialTranslationLanguage((prev) => (
      sameCatalogLanguageCode(prev, normalizedLanguageCode) ? '' : prev
    ));
  }, [updateSocialSectionDraft]);

  const translateMissingCatalogSocials = useCallback(async () => {
    if (!siteId || !areaId || !socialTranslationRows.length) return;
    const missingLanguages = catalogSocialMissingTranslationLanguages;
    if (!missingLanguages.length) {
      window.alert('No missing socials translations needed.');
      return;
    }
    const token = localStorage.getItem('token');
    setSocialTranslationState({ translating: true, error: '' });
    try {
      const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}/translations/socials/translate-missing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          socialSection: {
            enabled: Boolean(socialSectionDraft.enabled),
            title: normalizeCatalogSocialDraftText(socialSectionDraft.title, {
              fallback: '',
              maxLength: 120,
            }),
            description: normalizeCatalogSocialDraftText(socialSectionDraft.description, {
              fallback: '',
              maxLength: 240,
            }),
            ctaLabels: normalizeCatalogSocialCtaLabelsDraft(socialSectionDraft.ctaLabels, { allowEmpty: true }),
            linkKeys: selectedCatalogSocialEntries.map((entry) => entry.key),
            sourceLanguageCode: normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en'),
            translations: socialTranslationRows,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `Failed to translate socials (${res.status})`);
      }
      updateSocialSectionDraft((current) => ({
        ...current,
        translations: normalizeCatalogSocialTranslationsDraft(
          data?.translations,
          normalizeCatalogLanguageCode(current?.sourceLanguageCode, 'en')
        ),
      }));
      const translatedCount = Array.isArray(data?.translatedLanguages) ? data.translatedLanguages.length : 0;
      if (!translatedCount) {
        window.alert(data?.message || 'No missing socials translations needed.');
      }
    } catch (err) {
      console.error('Failed to translate missing socials', err);
      setSocialTranslationState({
        translating: false,
        error: err.message || 'Failed to translate socials.',
      });
      return;
    }
    setSocialTranslationState({ translating: false, error: '' });
  }, [
    areaId,
    catalogConfig.apiBase,
    catalogSocialMissingTranslationLanguages,
    selectedCatalogSocialEntries,
    siteId,
    socialSectionDraft,
    socialTranslationRows,
    updateSocialSectionDraft,
  ]);

  const updateMenuFeaturesDraft = useCallback((updater) => {
    updateDisplaySettingsDraft((prev) => {
      const baseDisplaySettings = prev && typeof prev === 'object' ? prev : {};
      const current = baseDisplaySettings.menuFeatures && typeof baseDisplaySettings.menuFeatures === 'object'
        ? baseDisplaySettings.menuFeatures
        : {
          showItemTags: false,
          enabledTagKeys: [...MENU_ITEM_TAG_KEYS],
          enabledFilterKeys: buildDefaultMenuFilterKeys(items),
          autoRotatePhotos: false,
          tagIconSet: DEFAULT_MENU_TAG_ICON_SET,
          customIconUrls: {},
        };
      const next = typeof updater === 'function'
        ? updater(current)
        : { ...current, ...(updater || {}) };
      const enabledTagKeys = normalizeMenuEnabledTagKeys(next.enabledTagKeys ?? next.enabled_tag_keys, MENU_ITEM_TAG_KEYS);
      return {
        ...baseDisplaySettings,
        menuFeatures: {
          showItemTags: Boolean(next.showItemTags),
          enabledTagKeys,
          enabledFilterKeys: filterMenuFilterKeysByEnabledTags(
            next.enabledFilterKeys ?? next.enabled_filter_keys,
            enabledTagKeys
          ),
          autoRotatePhotos: Boolean(next.autoRotatePhotos),
          tagIconSet: normalizeMenuTagIconSet(next.tagIconSet),
          customIconUrls: normalizeMenuCustomIconMap(next.customIconUrls ?? next.custom_icon_urls),
        },
      };
    });
  }, [items, updateDisplaySettingsDraft]);

  const syncSharedCatalogStructureFromSource = useCallback((sourceCatalog, { markDirty = true } = {}) => {
    if (!sourceCatalog) return;
    const { sectionHeadersDraft: nextHeaderDrafts, sectionsDraft: nextSectionDrafts, sectionKeyByName } = buildSharedCatalogDraftsFromSource(sourceCatalog);
    const currentSectionNameByKey = new Map(
      (Array.isArray(sectionsDraft) ? sectionsDraft : []).map((section) => [
        getSectionDraftKey(section),
        normalizeSectionNameKey(section?.name),
      ])
    );
    const fallbackSectionKey = nextSectionDrafts.length ? getSectionDraftKey(nextSectionDrafts[0]) : '';

    setSectionHeadersDraft(nextHeaderDrafts);
    setSectionsDraft(nextSectionDrafts);
    setSelectedSectionKeys((prev) => (
      (Array.isArray(prev) ? prev : [])
        .map((key) => sectionKeyByName.get(currentSectionNameByKey.get(String(key))) || null)
        .filter(Boolean)
    ));
    setCampaignSectionsDraft((prev) => normalizeCampaignSectionOrder(
      (Array.isArray(prev) ? prev : [])
        .map((campaignSection) => {
          const currentKey = String(campaignSection?.beforeSectionKey || '');
          const currentNameKey = currentSectionNameByKey.get(currentKey) || '';
          const nextBeforeSectionKey = sectionKeyByName.get(currentNameKey) || fallbackSectionKey;
          if (!nextBeforeSectionKey) return null;
          return {
            ...campaignSection,
            beforeSectionKey: nextBeforeSectionKey,
          };
        })
        .filter(Boolean)
    ));
    if (markDirty) setSettingsDirty(true);
  }, [sectionsDraft]);

  const loadSourceCatalogOptions = useCallback(async () => {
    if (!supportsSharedSourceCatalogs) {
      setSourceCatalogOptions([]);
      setSourceCatalogsError('');
      setSourceCatalogsLoading(false);
      return;
    }

    const authToken = localStorage.getItem('token');
    if (!authToken) return;

    setSourceCatalogsLoading(true);
    setSourceCatalogsError('');
    try {
      const sitesRes = await fetch('/api/sites', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!sitesRes.ok) {
        const body = await sitesRes.text().catch(() => '');
        throw new Error(body || `Failed to load sites (${sitesRes.status})`);
      }
      const sitesData = await sitesRes.json().catch(() => ({}));
      const sitesList = Array.isArray(sitesData.sites) ? sitesData.sites : [];
      const areaRows = sitesList.flatMap((sourceSite) => (
        (Array.isArray(sourceSite?.areas) ? sourceSite.areas : []).map((sourceArea) => ({
          siteId: sourceSite.id,
          siteName: sourceSite.name || '',
          areaId: sourceArea.id,
          areaName: sourceArea.name || '',
        }))
      ));
      const optionRows = await Promise.all(areaRows.map(async (sourceArea) => {
        try {
          const res = await fetch(`/api/catalogs/products/area/${encodeURIComponent(sourceArea.areaId)}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (!res.ok) return null;
          const data = await res.json().catch(() => ({}));
          const catalog = data.catalog || data.menu || null;
          if (!catalog?.id) return null;
          return {
            ...sourceArea,
            catalogId: catalog.id,
            title: catalog.title || '',
            landingKey: catalog.landingKey || '',
            contentMode: normalizeCatalogContentMode(catalog.contentMode || catalog.content_mode),
            sourceCatalogId: normalizeId(catalog.sourceCatalogId || catalog.source_catalog_id),
            sectionHeaders: Array.isArray(data.sectionHeaders) ? data.sectionHeaders : [],
            sections: Array.isArray(data.sections) ? data.sections : [],
          };
        } catch (err) {
          return null;
        }
      }));
      setSourceCatalogOptions(
        optionRows
          .filter(Boolean)
          .sort((a, b) => buildSourceCatalogOptionLabel(a).localeCompare(buildSourceCatalogOptionLabel(b)))
      );
    } catch (err) {
      console.error('Failed to load source catalogs', err);
      setSourceCatalogOptions([]);
      setSourceCatalogsError(err.message || 'Failed to load source catalogs.');
    } finally {
      setSourceCatalogsLoading(false);
    }
  }, [supportsSharedSourceCatalogs]);

  const fetchMenu = useCallback(async () => {
    catalogLoadRequestRef.current?.abort();
    if (!siteId || !areaId) {
      setMenu(null);
      setSectionHeaders([]);
      setSections([]);
      setItems([]);
      setDefaults({ logoCreativeId: null });
      setSectionHeadersDraft([]);
      setSectionsDraft([]);
      setCampaignSectionsDraft([]);
      setSectionHeaderName('');
      setDisplaySettingsDraft(resolveCatalogDisplaySettingsDraft({ catalog: null, site, area, catalogConfig, items: [] }));
      setContentModeDraft('native');
      setSourceCatalogIdDraft(null);
      setLogoCreativeIdDraft(null);
      setTableCountDraft('');
      setTableLabelsDraft({});
      setTableNumber('');
      setTableLabel('');
      setTableJumpSectionId('');
      setTableJumpItemId('');
      setMonetisationModeDraft('off');
      setMonetisationTypesDraft([]);
      setSettingsDirty(false);
      setError('');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    catalogLoadRequestRef.current = controller;
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      if (controller.signal.aborted) return;
      const nextMenu = data.menu || data.catalog || null;
      const nextCatalog = data.catalog || nextMenu || null;
      const nextSectionHeaders = Array.isArray(data.sectionHeaders) ? data.sectionHeaders : [];
      const nextSections = Array.isArray(data.sections) ? data.sections : [];
      const nextCampaignSections = Array.isArray(data.campaignSections) ? data.campaignSections : [];
      const nextItems = Array.isArray(data.items) ? data.items : [];
      const nextDefaults = data.defaults && typeof data.defaults === 'object'
        ? data.defaults
        : { logoCreativeId: null };

      onCatalogLoaded?.({ catalog: nextCatalog, sections: nextSections, items: nextItems });
      setMenu(nextMenu);
      setSectionHeaders(nextSectionHeaders);
      setSections(nextSections);
      setItems(nextItems);
      setDefaults(nextDefaults);
      setSectionHeaderName('');
      setSectionName('');
      setCampaignSectionKickerEnabled(true);
      setCampaignSectionKickerLabel(DEFAULT_CATALOG_CAMPAIGN_SECTION_KICKER_LABEL);
      const nextCount = nextMenu?.tableCount ?? nextMenu?.locationCount ?? null;
      const nextLabels = nextMenu?.tableLabels || nextMenu?.locationLabels || nextMenu?.tableLabelsJson || nextMenu?.locationLabelsJson;
      setTableCountDraft(nextCount ? String(nextCount) : '');
      setTableLabelsDraft(parseTableLabels(nextLabels));

      const nextSectionHeaderDrafts = nextSectionHeaders.map((sectionHeader) => ({
        id: sectionHeader.id,
        name: sectionHeader.name || '',
        enabled: Boolean(sectionHeader.enabled),
        sort_order: sectionHeader.sort_order || 0,
      }));
      const sectionHeaderKeyById = new Map(
        nextSectionHeaderDrafts
          .filter((sectionHeader) => sectionHeader?.id)
          .map((sectionHeader) => [String(sectionHeader.id), getSectionHeaderDraftKey(sectionHeader)])
      );
      setSectionHeadersDraft(nextSectionHeaderDrafts);
      setSectionsDraft(
        nextSections.map((section) => ({
          id: section.id,
          name: section.name || '',
          enabled: Boolean(section.enabled),
          sort_order: section.sort_order || 0,
          headerKey: section.headerId ? (sectionHeaderKeyById.get(String(section.headerId)) || '') : '',
        }))
      );
      setCampaignSectionsDraft(
        nextCampaignSections.map((campaignSection) => ({
          id: campaignSection.id,
          draftId: makeDraftId(),
          mode: normalizeCampaignSectionMode(campaignSection.mode, Array.isArray(campaignSection.campaignIds) && campaignSection.campaignIds.length > 1 ? 'dynamic' : 'single'),
          beforeSectionKey: campaignSection.beforeSectionId ? `section:${campaignSection.beforeSectionId}` : '',
          campaignId: normalizeId(campaignSection.campaignId),
          campaignIds: Array.isArray(campaignSection.campaignIds)
            ? campaignSection.campaignIds.map((value) => normalizeId(value)).filter(Boolean)
            : [],
          showKicker: campaignSection.showKicker !== false,
          kickerLabel: normalizeCampaignSectionKickerLabel(campaignSection.kickerLabel),
          sort_order: campaignSection.sort_order || 0,
        }))
      );
      setDisplaySettingsDraft(resolveCatalogDisplaySettingsDraft({
        catalog: nextCatalog,
        site,
        area,
        catalogConfig,
        items: nextItems,
      }));
      setContentModeDraft(normalizeCatalogContentMode(nextCatalog?.contentMode || nextCatalog?.content_mode));
      setSourceCatalogIdDraft(normalizeId(nextCatalog?.sourceCatalogId || nextCatalog?.source_catalog_id));
      const suggestedLogoId = nextMenu?.logoCreativeId || nextDefaults?.logoCreativeId || null;
      setLogoCreativeIdDraft(suggestedLogoId);
      setMonetisationModeDraft(
        normalizeMonetisationMode(
          nextMenu?.monetisationMode || nextMenu?.monetisation_mode || nextDefaults?.monetisationMode || nextDefaults?.monetisation_mode || site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off',
          'off'
        )
      );
      setMonetisationTypesDraft(
        normalizeMonetisationTypeIds(nextMenu?.monetisationTypes || nextMenu?.monetisation_types_json)
      );
      setSettingsDirty(false);

      if (!autoViewSetRef.current) {
        const canAutoItems = Boolean(nextMenu?.id) && nextSections.some((s) => Boolean(s.enabled));
        setView(canAutoItems ? 'items' : 'settings');
        autoViewSetRef.current = true;
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      onCatalogLoaded?.(null);
      console.error('Failed to load catalog', err);
      setError(err.message || 'Failed to load.');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [area, areaId, catalogConfig, catalogConfig.apiBase, onCatalogLoaded, parseTableLabels, site, site?.defaultMonetisationMode, site?.default_monetisation_mode, siteId]);

  useEffect(() => {
    autoViewSetRef.current = false;
  }, [areaId, catalogConfig.catalogType, siteId]);

  useEffect(() => {
    menuUploadCaptureRef.current = null;
    setRecentMenuUploadAssetIds([]);
    setRecentMenuTextEntries([]);
    setMenuPastePanelOpen(false);
    setMenuPasteDetection('');
    setMenuImportJob(null);
    setMenuImporting(false);
    setMenuImportMessage('');
    setMenuImportError('');
  }, [areaId, catalogConfig.catalogType, siteId]);

  useEffect(() => {
    if (!menuPastePanelOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      menuPasteTargetRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [menuPastePanelOpen]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  useEffect(() => {
    loadSourceCatalogOptions();
  }, [loadSourceCatalogOptions]);

  useEffect(() => {
    if (!isSharedCatalogMode || !selectedSourceCatalogOption) return;
    if ((Array.isArray(sectionsDraft) ? sectionsDraft : []).length > 0) return;
    syncSharedCatalogStructureFromSource(selectedSourceCatalogOption, { markDirty: false });
  }, [isSharedCatalogMode, sectionsDraft, selectedSourceCatalogOption, syncSharedCatalogStructureFromSource]);

  useEffect(() => {
    if (view !== 'items' && view !== 'translations') return;
    if (!isSharedCatalogMode) return;
    setView('settings');
  }, [isSharedCatalogMode, view]);

  useEffect(() => {
    setSelectedSectionKeys((prev) => prev.filter((key) => selectableSectionKeys.includes(key)));
  }, [selectableSectionKeys]);

  useEffect(() => {
    const validIds = new Set(allItemIds.map((id) => String(id)));
    setSelectedItemIds((prev) => prev.filter((id) => validIds.has(String(id))));
  }, [allItemIds]);

  const handleContentModeChange = useCallback((nextMode) => {
    const normalizedMode = normalizeCatalogContentMode(nextMode);
    setContentModeDraft(normalizedMode);
    setSettingsDirty(true);

    if (normalizedMode !== 'shared') {
      setSourceCatalogIdDraft(null);
      return;
    }

    const nextSourceCatalog = selectedSourceCatalogOption
      || availableSourceCatalogOptions[0]
      || null;
    const nextSourceCatalogId = normalizeId(nextSourceCatalog?.catalogId);
    setSourceCatalogIdDraft(nextSourceCatalogId);
    if (nextSourceCatalog) {
      syncSharedCatalogStructureFromSource(nextSourceCatalog, { markDirty: false });
    }
  }, [availableSourceCatalogOptions, selectedSourceCatalogOption, syncSharedCatalogStructureFromSource]);

  const handleSourceCatalogChange = useCallback((value) => {
    const nextSourceCatalogId = value ? normalizeId(value) : null;
    setSourceCatalogIdDraft(nextSourceCatalogId);
    setSettingsDirty(true);
    const nextSourceCatalog = availableSourceCatalogOptions.find(
      (sourceCatalog) => String(sourceCatalog?.catalogId || '') === String(nextSourceCatalogId || '')
    ) || null;
    if (nextSourceCatalog) {
      syncSharedCatalogStructureFromSource(nextSourceCatalog, { markDirty: false });
    }
  }, [availableSourceCatalogOptions, syncSharedCatalogStructureFromSource]);

  const handleImportUploadedMenus = useCallback(() => {
    if (siteId && areaId && (recentMenuUploadAssetIds.length || recentMenuTextEntries.length)) setGordonTask('import');
  }, [areaId, recentMenuTextEntries, recentMenuUploadAssetIds, siteId]);

  const clearPendingMenuImports = useCallback(() => {
    menuUploadCaptureRef.current = null;
    setRecentMenuUploadAssetIds([]);
    setRecentMenuTextEntries([]);
    setMenuImportJob(null);
    setMenuImporting(false);
    setMenuPasteDetection('');
    setMenuImportMessage('');
    setMenuImportError('');
    setMenuPastePanelOpen(false);
    if (uploadMenuInputRef.current) uploadMenuInputRef.current.value = '';
    if (menuPasteTargetRef.current) menuPasteTargetRef.current.value = '';
  }, []);

  useEffect(() => {
    const handleUploadDone = (evt) => {
      if (evt?.detail?.siteId && siteId && String(evt.detail.siteId) !== String(siteId)) return;
      onRefreshAssets?.();
    };
    window.addEventListener('uploadQueue:itemDone', handleUploadDone);
    return () => window.removeEventListener('uploadQueue:itemDone', handleUploadDone);
  }, [onRefreshAssets, siteId]);

  const loadWidgets = useCallback(async () => {
    if (!isAdmin) {
      setWidgets([]);
      setWidgetsError('');
      setWidgetsLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setWidgetsLoading(true);
    setWidgetsError('');
    try {
      const res = await fetch('/api/creative/widgets', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load widgets (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const rows = Array.isArray(data.widgets) ? data.widgets : [];
      setWidgets(rows);
    } catch (err) {
      console.error('Failed to load widgets', err);
      setWidgets([]);
      setWidgetsError(err.message || 'Failed to load widgets.');
    } finally {
      setWidgetsLoading(false);
    }
  }, [isAdmin]);

  const loadCampaignAds = useCallback(async () => {
    if (!supportsCampaignAdvertising) {
      setCampaignAds([]);
      setCampaignAdsError('');
      setCampaignAdsLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setCampaignAdsLoading(true);
    setCampaignAdsError('');
    try {
      const res = await fetch('/api/creative/campaign-ads', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load campaign ads (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const rows = Array.isArray(data.campaignAds) ? data.campaignAds : [];
      setCampaignAds(rows);
    } catch (err) {
      console.error('Failed to load campaign ads', err);
      setCampaignAds([]);
      setCampaignAdsError(err.message || 'Failed to load campaign ads.');
    } finally {
      setCampaignAdsLoading(false);
    }
  }, [supportsCampaignAdvertising]);

  useEffect(() => {
    if (!isAdmin) {
      setWidgetPicker({ open: false, sectionId: null });
      return;
    }
    loadWidgets();
  }, [isAdmin, loadWidgets]);

  useEffect(() => {
    loadCampaignAds();
  }, [loadCampaignAds]);

  useEffect(() => {
    if (campaignAdsDynamicMode) {
      setSelectedCampaignAdIds((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        if (current.length) return current;
        return selectedCampaignAdId ? [selectedCampaignAdId] : [];
      });
      return;
    }
    if (!selectedCampaignAdId && selectedCampaignAdIds.length) {
      setSelectedCampaignAdId(selectedCampaignAdIds[0]);
    }
  }, [campaignAdsDynamicMode, selectedCampaignAdId, selectedCampaignAdIds]);

  const fetchTranslationsStatus = useCallback(async ({ keepJob = true, silent = false } = {}) => {
    if (!siteId || !areaId || !menu?.id) return;
    const token = localStorage.getItem('token');
    if (!silent) {
      setTranslationsState((prev) => ({
        ...prev,
        loading: true,
        error: '',
      }));
    } else {
      setTranslationsState((prev) => ({
        ...prev,
        error: '',
      }));
    }
    try {
      const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}/translations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load translations (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const enabledLanguages = Array.isArray(data.enabledLanguages) ? data.enabledLanguages : ['en'];
      const availableLanguages = (Array.isArray(data.availableLanguages) ? data.availableLanguages : [])
        .slice()
        .sort((a, b) => compareLanguageCodesForDisplay(a?.code, b?.code));
      const stats = Array.isArray(data.stats) ? data.stats : [];
      const runningJobId = data.runningJobId || null;
      setTranslationsState((prev) => ({
        ...prev,
        loading: silent ? prev.loading : false,
        availableLanguages,
        enabledLanguages: (enabledLanguages.includes('en') ? enabledLanguages : ['en', ...enabledLanguages])
          .sort(compareLanguageCodesForDisplay),
        stats,
        runningJobId,
        jobId: keepJob ? prev.jobId || runningJobId : runningJobId,
      }));
    } catch (err) {
      console.error('Failed to fetch translation status', err);
      setTranslationsState((prev) => ({
        ...prev,
        loading: silent ? prev.loading : false,
        error: err.message || 'Failed to load translation status.',
      }));
    }
  }, [areaId, catalogConfig.apiBase, menu?.id, siteId]);

  useEffect(() => {
    if (!siteId || !areaId || !menu?.id) return;
    fetchTranslationsStatus({ keepJob: true, silent: true });
  }, [areaId, fetchTranslationsStatus, menu?.id, siteId]);

  useEffect(() => {
    if (view === 'items' && !canEditItems) setView('settings');
    if (view === 'translations' && !canManageTranslations) setView('settings');
  }, [canEditItems, canManageTranslations, view]);

  const fetchLanguageTranslations = useCallback(async (languageCode, { silent = false } = {}) => {
    if (!siteId || !areaId || !menu?.id) return;
    if (!languageCode || languageCode === 'en') {
      setLanguageData({
        loading: false,
        error: '',
        languageCode: 'en',
        sectionHeaders: [],
        sections: [],
        items: [],
      });
      return;
    }
    const token = localStorage.getItem('token');
    if (!silent) {
      setLanguageData((prev) => ({
        ...prev,
        loading: true,
        error: '',
        languageCode,
      }));
    }
    try {
      const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}/translations/${encodeURIComponent(languageCode)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load translations (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      setLanguageData({
        loading: false,
        error: '',
        languageCode: languageCode,
        sectionHeaders: Array.isArray(data.sectionHeaders) ? data.sectionHeaders : [],
        sections: Array.isArray(data.sections) ? data.sections : [],
        items: Array.isArray(data.items) ? data.items : [],
      });
    } catch (err) {
      console.error('Failed to fetch catalog translations', err);
      setLanguageData((prev) => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load translations.',
        languageCode,
        sectionHeaders: [],
      }));
    }
  }, [areaId, catalogConfig.apiBase, menu?.id, siteId]);

  const fetchTranslationJob = useCallback(async (jobId) => {
    if (!jobId) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${catalogConfig.apiBase}/translations/jobs/${encodeURIComponent(jobId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      setTranslationsState((prev) => ({ ...prev, job: data }));
    } catch (err) {
      // ignore polling failures
    }
  }, [catalogConfig.apiBase]);

  useEffect(() => {
    if (view !== 'translations') return () => {};
    fetchTranslationsStatus({ keepJob: true, silent: false });
    return () => {};
  }, [fetchTranslationsStatus, view]);

  useEffect(() => {
    if (view !== 'items') return () => {};
    fetchTranslationsStatus({ keepJob: true, silent: true });
    return () => {};
  }, [fetchTranslationsStatus, view]);

  useEffect(() => {
    if (view !== 'translations') return () => {};
    const activeJobId = translationsState.runningJobId || null;
    if (!activeJobId) return () => {};

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchTranslationJob(activeJobId);
      await fetchTranslationsStatus({ keepJob: true, silent: true });
    };
    tick();
    const interval = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [fetchTranslationJob, fetchTranslationsStatus, translationsState.runningJobId, view]);

  useEffect(() => {
    if (view !== 'translations') return;
    if (!translationsState.job) return;
    const status = String(translationsState.job.status || '');
    if (status === 'completed' || status === 'failed') {
      setTranslationsState((prev) => ({ ...prev, runningJobId: null }));
      fetchTranslationsStatus({ keepJob: true, silent: true });
    }
  }, [fetchTranslationsStatus, translationsState.job, view]);

  useEffect(() => {
    const enabled = Array.isArray(translationsState.enabledLanguages) ? translationsState.enabledLanguages : ['en'];
    if (!enabled.includes(activeLanguage)) setActiveLanguage('en');
  }, [activeLanguage, translationsState.enabledLanguages]);

  useEffect(() => {
    if (!socialTranslationRows.length) {
      setActiveSocialTranslationLanguage('');
      return;
    }
    const hasActiveRow = socialTranslationRows.some((row) => sameCatalogLanguageCode(row.languageCode, activeSocialTranslationLanguage));
    if (!hasActiveRow) {
      setActiveSocialTranslationLanguage(socialTranslationRows[0].languageCode);
    }
  }, [activeSocialTranslationLanguage, socialTranslationRows]);

  useEffect(() => {
    if (manualSocialTranslationLanguage) return;
    const firstAvailable = catalogSocialTranslationLanguageOptions.find(
      (option) => !socialTranslationRows.some((row) => sameCatalogLanguageCode(row.languageCode, option.code))
    );
    if (firstAvailable) {
      setManualSocialTranslationLanguage(firstAvailable.code);
    }
  }, [catalogSocialTranslationLanguageOptions, manualSocialTranslationLanguage, socialTranslationRows]);

  useEffect(() => {
    if (!isTranslationMode) {
      setLanguageData({
        loading: false,
        error: '',
        languageCode: 'en',
        sectionHeaders: [],
        sections: [],
        items: [],
      });
      return;
    }
    fetchLanguageTranslations(activeLanguage);
  }, [activeLanguage, fetchLanguageTranslations, isTranslationMode]);

  const applyTemplate = () => {
    if (sectionsDraft.length) return;
    const names = ['Starters', 'Mains', 'Desserts', 'Drinks'];
    setSectionsDraft(
      names.map((name, idx) => ({
        id: null,
        draftId: makeDraftId(),
        name,
        enabled: true,
        sort_order: idx + 1,
      }))
    );
    setSettingsDirty(true);
  };

  const addSectionHeader = () => {
    const trimmed = (sectionHeaderName || '').trim();
    if (!trimmed) return;
    const existing = Array.isArray(sectionHeadersDraft) ? sectionHeadersDraft : [];
    const maxOrder = existing.length ? Math.max(...existing.map((sectionHeader) => sectionHeader.sort_order || 0)) : 0;
    setSectionHeadersDraft([
      ...existing,
      { id: null, draftId: makeDraftId(), name: trimmed, enabled: true, sort_order: maxOrder + 1 },
    ]);
    setSectionHeaderName('');
    setSettingsDirty(true);
  };

  const updateSectionHeader = (key, patch) => {
    setSectionHeadersDraft((prev) => {
      const safe = Array.isArray(prev) ? prev : [];
      return safe.map((sectionHeader) => (
        getSectionHeaderDraftKey(sectionHeader) === String(key)
          ? { ...sectionHeader, ...patch }
          : sectionHeader
      ));
    });
    setSettingsDirty(true);
  };

  const reorderSectionHeaders = (next) => next.map((sectionHeader, idx) => ({ ...sectionHeader, sort_order: idx + 1 }));

  const moveSectionHeader = (key, direction) => {
    setSectionHeadersDraft((prev) => {
      const safe = Array.isArray(prev) ? prev.slice() : [];
      safe.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const index = safe.findIndex((sectionHeader) => getSectionHeaderDraftKey(sectionHeader) === String(key));
      if (index === -1) return prev;
      const target = index + direction;
      if (target < 0 || target >= safe.length) return prev;
      const next = [...safe];
      const temp = next[index];
      next[index] = next[target];
      next[target] = temp;
      return reorderSectionHeaders(next);
    });
    setSettingsDirty(true);
  };

  const reorderSectionHeadersByDrop = useCallback((sourceKey, targetKey, position = 'before') => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    setSectionHeadersDraft((prev) => {
      const sorted = sortSectionHeadersByOrder(prev);
      const reordered = reorderListByKeys(
        sorted,
        String(sourceKey),
        String(targetKey),
        (sectionHeader) => getSectionHeaderDraftKey(sectionHeader),
        position
      );
      return reorderSectionHeaders(reordered);
    });
    setSettingsDirty(true);
  }, []);

  const deleteSectionHeader = (key) => {
    const resolvedKey = String(key || '');
    if (!resolvedKey) return;
    const usageCount = sectionHeaderDraftUsageByKey.get(resolvedKey) || 0;
    if (usageCount) {
      const ok = window.confirm(
        `Delete this section header? ${usageCount} section${usageCount === 1 ? '' : 's'} will lose their assignment.`
      );
      if (!ok) return;
    }
    setSectionHeadersDraft((prev) => reorderSectionHeaders(
      (Array.isArray(prev) ? prev : []).filter((sectionHeader) => getSectionHeaderDraftKey(sectionHeader) !== resolvedKey)
    ));
    setSectionsDraft((prev) => (Array.isArray(prev) ? prev : []).map((section) => (
      String(section?.headerKey || '') === resolvedKey
        ? { ...section, headerKey: '' }
        : section
    )));
    setSettingsDirty(true);
  };

  const addSection = () => {
    const trimmed = (sectionName || '').trim();
    if (!trimmed) return;
    const existing = Array.isArray(sectionsDraft) ? sectionsDraft : [];
    const maxOrder = existing.length ? Math.max(...existing.map((s) => s.sort_order || 0)) : 0;
    const defaultSectionHeaderKey = sortedSectionHeadersDraft.length === 1
      ? getSectionHeaderDraftKey(sortedSectionHeadersDraft[0])
      : '';
    setSectionsDraft([
      ...existing,
      {
        id: null,
        draftId: makeDraftId(),
        name: trimmed,
        enabled: true,
        sort_order: maxOrder + 1,
        headerKey: defaultSectionHeaderKey,
      },
    ]);
    setSectionName('');
    setSettingsDirty(true);
  };

  const updateSection = (key, patch) => {
    setSectionsDraft((prev) => {
      const safe = Array.isArray(prev) ? prev : [];
      return safe.map((section) => (getSectionDraftKey(section) === String(key) ? { ...section, ...patch } : section));
    });
    setSettingsDirty(true);
  };

  const reorderSections = (next) => {
    return next.map((section, idx) => ({ ...section, sort_order: idx + 1 }));
  };

  const moveSection = (key, direction) => {
    setSectionsDraft((prev) => moveSectionWithinHeading(prev, sectionHeadersDraft, String(key), direction));
    setSettingsDirty(true);
  };

  const reorderSectionEditorRowsByDrop = useCallback((sourceKey, targetKey, position = 'before') => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    const sourceRow = sectionEditorRows.find((row) => row.key === String(sourceKey)) || null;
    const targetRow = sectionEditorRows.find((row) => row.key === String(targetKey)) || null;
    if (!sourceRow || !targetRow) return;

    if (sourceRow.kind === 'section') {
      if (targetRow.kind !== 'section') return;
      setSectionsDraft((prev) => dropSectionWithinHeading(
        prev, sectionHeadersDraft, String(sourceKey), String(targetKey), position
      ));
      setSettingsDirty(true);
      return;
    }

    const reorderedRows = reorderListByKeys(
      sectionEditorRows,
      String(sourceKey),
      String(targetKey),
      (row) => row.key,
      position
    );
    if (reorderedRows === sectionEditorRows) return;

    setSectionsDraft((prev) => {
      const sectionLookup = new Map(
        sortSectionsByOrder(Array.isArray(prev) ? prev : []).map((section) => [getSectionDraftKey(section), section])
      );
      const reorderedSections = reorderedRows
        .filter((row) => row.kind === 'section')
        .map((row) => sectionLookup.get(row.key) || row.section)
        .filter(Boolean);
      return reorderSections(reorderedSections);
    });
    setCampaignSectionsDraft((prev) => rebuildCampaignSectionsFromEditorRows(reorderedRows, prev));
    updateSocialSectionDraft((current) => ({
      ...current,
      sortOrder: resolveSocialSortOrderFromEditorRows(reorderedRows),
    }));
    setDisplaySettingsDraft((current) => ({
      ...(current || {}),
      promotionPositions: {
        ...(current?.promotionPositions || {}),
        ...resolvePromotionPositionsFromEditorRows(reorderedRows),
      },
    }));
    setSettingsDirty(true);
  }, [sectionEditorRows, sectionHeadersDraft, updateSocialSectionDraft]);

  const movePromotion = useCallback((key, direction) => {
    const promotion = sectionEditorRows.find((row) => row.kind === 'promotion' && row.key === key);
    if (!promotion) return;
    const maxPosition = promotion.typeId === 'landing_network_ads'
      ? Math.max(1, sortedSectionsDraft.length)
      : Math.max(1, sortedSectionsDraft.length + 1);
    const currentPosition = Number(promotion.position || 1);
    const nextPosition = Math.max(1, Math.min(maxPosition, currentPosition + direction));
    if (nextPosition === currentPosition) return;
    setDisplaySettingsDraft((current) => ({
      ...(current || {}),
      promotionPositions: {
        ...(current?.promotionPositions || {}),
        [promotion.typeId]: nextPosition,
      },
    }));
    setSettingsDirty(true);
  }, [sectionEditorRows, sortedSectionsDraft.length]);

  const updatePromotionStyle = useCallback((typeId, patch) => {
    const defaults = CATALOG_PROMOTION_STYLE_DEFAULTS[typeId];
    if (!defaults) return;
    setDisplaySettingsDraft((current) => ({
      ...(current || {}),
      promotionStyles: {
        ...(current?.promotionStyles || {}),
        [typeId]: {
          ...defaults,
          ...(current?.promotionStyles?.[typeId] || {}),
          ...patch,
        },
      },
    }));
    setSettingsDirty(true);
  }, []);

  const renderPromotionAppearanceControls = (typeId) => {
    const defaults = CATALOG_PROMOTION_STYLE_DEFAULTS[typeId];
    if (!defaults) return null;
    const style = displaySettingsDraft?.promotionStyles?.[typeId] || defaults;
    return (
      <div className="grid gap-3 border-t border-white/10 pt-3 sm:grid-cols-2">
        <label className="block">
          <div className="mb-1 text-xs uppercase tracking-wide text-gray-300">Emphasis</div>
          <select
            className="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-white"
            value={style.emphasis || defaults.emphasis}
            onChange={(event) => updatePromotionStyle(typeId, { emphasis: event.target.value })}
          >
            {CATALOG_PROMOTION_EMPHASIS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <div className="mb-1 text-xs uppercase tracking-wide text-gray-300">Accent Colour</div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-10 w-14 cursor-pointer rounded border border-gray-700 bg-gray-950 p-1"
              value={normalizeHexColor(style.accentColor, defaults.accentColor)}
              onChange={(event) => updatePromotionStyle(typeId, { accentColor: event.target.value })}
              aria-label={`${CATALOG_PROMOTION_LABELS[typeId] || 'Socials'} accent colour`}
            />
            <span className="font-mono text-xs text-gray-300">
              {normalizeHexColor(style.accentColor, defaults.accentColor)}
            </span>
          </div>
        </label>
      </div>
    );
  };

  const toggleSectionSelection = (key) => {
    setSelectedSectionKeys((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key];
    });
  };

  const deleteSelectedSections = () => {
    const selected = new Set(selectedSectionKeys);
    if (!selected.size) return;
    const ok = window.confirm(`Delete ${selected.size} selected section${selected.size === 1 ? '' : 's'}? Items assigned to them and advertising placed before them will also be removed.`);
    if (!ok) return;
    setSectionsDraft((prev) => {
      const safe = Array.isArray(prev) ? prev : [];
      return reorderSections(safe.filter((section) => !selected.has(getSectionDraftKey(section))));
    });
    setCampaignSectionsDraft((prev) => normalizeCampaignSectionOrder(
      (Array.isArray(prev) ? prev : []).filter((campaignSection) => !selected.has(String(campaignSection.beforeSectionKey || '')))
    ));
    setSelectedSectionKeys([]);
    setSettingsDirty(true);
  };

  const toggleCatalogSocialLink = useCallback((linkKey) => {
    const normalizedKey = normalizeCatalogSocialLinkKey(linkKey);
    if (!normalizedKey) return;
    updateSocialSectionDraft((current) => {
      const exists = current.linkKeys.includes(normalizedKey);
      return {
        ...current,
        enabled: true,
        linkKeys: exists
          ? current.linkKeys.filter((key) => key !== normalizedKey)
          : [...current.linkKeys, normalizedKey],
        sortOrder: current.sortOrder || Math.max(1, sortedSectionsDraft.length + 1),
      };
    });
  }, [sortedSectionsDraft.length, updateSocialSectionDraft]);

  const toggleCatalogHeaderSocialLink = useCallback((linkKey) => {
    const normalizedKey = normalizeCatalogSocialLinkKey(linkKey);
    if (!normalizedKey) return;
    updateSocialSectionDraft((current) => {
      const exists = current.headerLinkKeys.includes(normalizedKey);
      return {
        ...current,
        showInHeader: true,
        headerPosition: normalizeCatalogSocialHeaderPosition(current.headerPosition),
        headerLinkKeys: exists
          ? current.headerLinkKeys.filter((key) => key !== normalizedKey)
          : [...current.headerLinkKeys, normalizedKey],
      };
    });
  }, [updateSocialSectionDraft]);

  const moveSocialSection = useCallback((direction) => {
    updateSocialSectionDraft((current) => {
      const currentPosition = resolveCatalogSocialSortOrder(current, sortedSectionsDraft.length);
      const maxPosition = Math.max(1, sortedSectionsDraft.length + 1);
      const nextPosition = Math.max(1, Math.min(maxPosition, currentPosition + direction));
      return {
        ...current,
        enabled: true,
        sortOrder: nextPosition,
      };
    });
  }, [sortedSectionsDraft.length, updateSocialSectionDraft]);

  const toggleCampaignAdSelection = useCallback((campaignId) => {
    const normalizedCampaignId = normalizeId(campaignId);
    if (!normalizedCampaignId) return;
    if (campaignAdsDynamicMode) {
      setSelectedCampaignAdIds((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        const exists = current.some((id) => String(id) === String(normalizedCampaignId));
        const next = exists
          ? current.filter((id) => String(id) !== String(normalizedCampaignId))
          : [...current, normalizedCampaignId];
        setSelectedCampaignAdId(next.length ? next[0] : null);
        return next;
      });
      return;
    }
    setSelectedCampaignAdId(normalizedCampaignId);
    setSelectedCampaignAdIds([normalizedCampaignId]);
  }, [campaignAdsDynamicMode]);

  const addCampaignSection = useCallback(() => {
    const defaultSection = sortedSectionsDraft[0] || null;
    const beforeSectionKey = defaultSection ? getSectionDraftKey(defaultSection) : '';
    if (!beforeSectionKey) {
      alert('Add at least one section before placing advertising.');
      return;
    }

    if (campaignAdsDynamicMode) {
      const campaignIds = Array.from(new Set(
        (Array.isArray(selectedCampaignAdIds) ? selectedCampaignAdIds : [])
          .map((campaignId) => normalizeId(campaignId))
          .filter(Boolean)
      ));
      if (!campaignIds.length) {
        alert('Select one or more campaign ads first.');
        return;
      }
      setCampaignSectionsDraft((prev) => normalizeCampaignSectionOrder([
        ...(Array.isArray(prev) ? prev : []),
        {
          id: null,
          draftId: makeDraftId(),
          mode: 'dynamic',
          beforeSectionKey,
          campaignId: null,
          campaignIds,
          showKicker: campaignSectionKickerEnabled,
          kickerLabel: normalizeCampaignSectionKickerLabel(campaignSectionKickerLabel),
        },
      ]));
      setSettingsDirty(true);
      return;
    }

    const campaignId = normalizeId(selectedCampaignAdId);
    if (!campaignId) {
      alert('Select a campaign ad first.');
      return;
    }
    setCampaignSectionsDraft((prev) => normalizeCampaignSectionOrder([
      ...(Array.isArray(prev) ? prev : []),
      {
        id: null,
        draftId: makeDraftId(),
        mode: 'single',
        beforeSectionKey,
        campaignId,
        campaignIds: [],
        showKicker: campaignSectionKickerEnabled,
        kickerLabel: normalizeCampaignSectionKickerLabel(campaignSectionKickerLabel),
      },
    ]));
    setSettingsDirty(true);
  }, [
    campaignAdsDynamicMode,
    campaignSectionKickerEnabled,
    campaignSectionKickerLabel,
    selectedCampaignAdId,
    selectedCampaignAdIds,
    sortedSectionsDraft,
  ]);

  const updateCampaignSection = useCallback((key, updates) => {
    setCampaignSectionsDraft((prev) => normalizeCampaignSectionOrder(
      (Array.isArray(prev) ? prev : []).map((campaignSection) => (
        getCampaignSectionDraftKey(campaignSection) === String(key)
          ? { ...campaignSection, ...(updates || {}) }
          : campaignSection
      ))
    ));
    setSettingsDirty(true);
  }, []);

  const deleteCampaignSection = useCallback((key) => {
    setCampaignSectionsDraft((prev) => normalizeCampaignSectionOrder(
      (Array.isArray(prev) ? prev : []).filter((campaignSection) => getCampaignSectionDraftKey(campaignSection) !== String(key))
    ));
    setSettingsDirty(true);
  }, []);

  const canMoveCampaignSection = useCallback((key, direction) => {
    const index = sectionEditorRows.findIndex((row) => row.kind === 'campaign' && row.key === key);
    if (index === -1) return false;
    const target = index + direction;
    if (target < 0 || target >= sectionEditorRows.length) return false;
    if (
      direction > 0
      && sectionEditorRows[target]?.kind === 'section'
      && !sectionEditorRows.slice(target + 1).some((row) => row.kind === 'section')
    ) {
      return false;
    }
    return true;
  }, [sectionEditorRows]);

  const moveCampaignSection = useCallback((key, direction) => {
    if (!canMoveCampaignSection(key, direction)) return;
    const rows = sectionEditorRows.slice();
    const index = rows.findIndex((row) => row.kind === 'campaign' && row.key === key);
    if (index === -1) return;
    const target = index + direction;
    const temp = rows[index];
    rows[index] = rows[target];
    rows[target] = temp;
    setCampaignSectionsDraft(rebuildCampaignSectionsFromEditorRows(rows, campaignSectionsDraft));
    setSettingsDirty(true);
  }, [canMoveCampaignSection, campaignSectionsDraft, sectionEditorRows]);

  const saveSettings = async () => {
    if (!siteId || !areaId) return;
    const token = localStorage.getItem('token');
    setSavingSettings(true);
    setError('');
    try {
      if (supportsSharedSourceCatalogs && isSharedCatalogMode && !sourceCatalogIdDraft) {
        throw new Error('Choose a source products catalog before saving.');
      }
      const tableCount = normalizeId(tableCountDraft);
      const cleanedLabels = {};
      if (tableCount) {
        for (let i = 1; i <= tableCount; i += 1) {
          const label = String(tableLabelsDraft?.[String(i)] || '').trim();
          if (label) cleanedLabels[String(i)] = label;
        }
      }

      const locationConfigKey = catalogConfig.catalogType === 'menu' ? 'tableConfig' : 'locationConfig';
      const sortedSectionHeaders = sortSectionHeadersByOrder(sectionHeadersDraft);
      const payload = {
        logoCreativeId: logoCreativeIdDraft ? Number(logoCreativeIdDraft) : null,
        displaySettings: {
          headerTitle: String(displaySettingsDraft?.headerTitle || '').trim(),
          headerSubtitle: String(displaySettingsDraft?.headerSubtitle || '').trim(),
          logoSize: normalizeCatalogLogoSize(displaySettingsDraft?.logoSize, 'normal'),
          backgroundColor: normalizeHexColor(displaySettingsDraft?.backgroundColor, DEFAULT_CATALOG_BACKGROUND_COLOR),
          surfaceColor: normalizeHexColor(displaySettingsDraft?.surfaceColor, DEFAULT_CATALOG_SURFACE_COLOR),
          secondarySurfaceColor: normalizeHexColor(
            displaySettingsDraft?.secondarySurfaceColor,
            normalizeHexColor(displaySettingsDraft?.surfaceColor, DEFAULT_CATALOG_SECONDARY_SURFACE_COLOR)
          ),
          tertiarySurfaceColor: normalizeHexColor(
            displaySettingsDraft?.tertiarySurfaceColor,
            normalizeHexColor(displaySettingsDraft?.surfaceColor, DEFAULT_CATALOG_TERTIARY_SURFACE_COLOR)
          ),
          headingColor: normalizeHexColor(
            displaySettingsDraft?.headingColor,
            normalizeHexColor(displaySettingsDraft?.textColor, DEFAULT_CATALOG_HEADING_COLOR)
          ),
          textColor: normalizeHexColor(displaySettingsDraft?.textColor, DEFAULT_CATALOG_TEXT_COLOR),
          photoDisplay: normalizeCatalogPhotoDisplay(displaySettingsDraft?.photoDisplay, 'fill'),
          promotionPositions: Object.fromEntries(
            Object.keys(CATALOG_PROMOTION_LABELS)
              .map((typeId) => {
                const requested = Number(displaySettingsDraft?.promotionPositions?.[typeId]);
                const maxPosition = typeId === 'landing_network_ads'
                  ? Math.max(1, sectionsDraft.length)
                  : Math.max(1, sectionsDraft.length + 1);
                return [typeId, Number.isInteger(requested) ? Math.min(maxPosition, requested) : 0];
              })
              .filter(([, position]) => Number.isInteger(position) && position > 0 && position <= 500)
          ),
          promotionStyles: Object.fromEntries(
            Object.entries(CATALOG_PROMOTION_STYLE_DEFAULTS).map(([typeId, defaults]) => {
              const style = displaySettingsDraft?.promotionStyles?.[typeId] || {};
              return [typeId, {
                emphasis: ['compact', 'standard', 'prominent'].includes(style.emphasis)
                  ? style.emphasis
                  : defaults.emphasis,
                accentColor: normalizeHexColor(style.accentColor, defaults.accentColor),
              }];
            })
          ),
          menuFeatures: {
            showItemTags: isMenuCatalog ? Boolean(menuFeaturesDraft.showItemTags) : false,
            enabledTagKeys: isMenuCatalog ? normalizeMenuEnabledTagKeys(menuFeaturesDraft.enabledTagKeys, MENU_ITEM_TAG_KEYS) : [...MENU_ITEM_TAG_KEYS],
            enabledFilterKeys: isMenuCatalog
              ? filterMenuFilterKeysByEnabledTags(menuFeaturesDraft.enabledFilterKeys, menuFeaturesDraft.enabledTagKeys)
              : [],
            autoRotatePhotos: Boolean(menuFeaturesDraft.autoRotatePhotos),
            tagIconSet: normalizeMenuTagIconSet(menuFeaturesDraft.tagIconSet),
            customIconUrls: normalizeMenuCustomIconMap(menuFeaturesDraft.customIconUrls),
          },
          socialSection: {
            enabled: Boolean(socialSectionDraft.enabled),
            title: normalizeCatalogSocialSectionText(socialSectionDraft.title, {
              fallback: DEFAULT_CATALOG_SOCIAL_SECTION_TITLE,
              maxLength: 120,
            }),
            description: normalizeCatalogSocialSectionText(socialSectionDraft.description, {
              fallback: DEFAULT_CATALOG_SOCIAL_SECTION_DESCRIPTION,
              maxLength: 240,
            }),
            ctaLabels: normalizeCatalogSocialCtaLabels(socialSectionDraft.ctaLabels),
            linkKeys: selectedCatalogSocialEntries.map((entry) => entry.key),
            headerLinkKeys: selectedCatalogHeaderSocialEntries.map((entry) => entry.key),
            showInHeader: Boolean(socialSectionDraft.showInHeader),
            headerPosition: normalizeCatalogSocialHeaderPosition(socialSectionDraft.headerPosition),
            sortOrder: socialSectionDraft.enabled
              ? resolveCatalogSocialSortOrder(socialSectionDraft, sectionsDraft.length)
              : null,
            sourceLanguageCode: normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en'),
            translations: socialTranslationRows.map((row) => ({
              languageCode: row.languageCode,
              title: normalizeCatalogSocialDraftText(row.title, { fallback: '', maxLength: 120 }),
              description: normalizeCatalogSocialDraftText(row.description, { fallback: '', maxLength: 240 }),
              ctaLabels: normalizeCatalogSocialCtaLabelsDraft(row.ctaLabels, { allowEmpty: true }),
            })),
          },
        },
        [locationConfigKey]: {
          count: tableCount || null,
          labels: cleanedLabels,
        },
        ...(supportsSharedSourceCatalogs ? {
          sourceCatalogId: isSharedCatalogMode ? normalizeId(sourceCatalogIdDraft) : null,
        } : {}),
        sectionHeaders: sortedSectionHeaders.map((sectionHeader, idx) => ({
          id: normalizeId(sectionHeader.id),
          clientKey: getSectionHeaderDraftKey(sectionHeader),
          name: String(sectionHeader.name || '').trim(),
          enabled: Boolean(sectionHeader.enabled),
          sort_order: idx + 1,
        })),
        sections: sortedSectionsDraft
          .map((section, idx) => ({
            id: normalizeId(section.id),
            clientKey: getSectionDraftKey(section),
            headerId: String(section.headerKey || '').startsWith('section-header:')
              ? normalizeId(String(section.headerKey).slice('section-header:'.length))
              : null,
            headerClientKey: String(section.headerKey || '').startsWith('section-header-draft:')
              ? String(section.headerKey || '')
              : '',
            name: String(section.name || '').trim(),
            enabled: Boolean(section.enabled),
            sort_order: idx + 1,
          })),
      };
      if (supportsCampaignAdvertising) {
        payload.campaignSections = normalizeCampaignSectionOrder(Array.isArray(campaignSectionsDraft) ? campaignSectionsDraft : [])
          .map((campaignSection, idx) => ({
            id: normalizeId(campaignSection.id),
            mode: normalizeCampaignSectionMode(campaignSection.mode, 'single'),
            beforeSectionId: String(campaignSection.beforeSectionKey || '').startsWith('section:')
              ? normalizeId(String(campaignSection.beforeSectionKey || '').slice('section:'.length))
              : null,
            beforeSectionClientKey: String(campaignSection.beforeSectionKey || ''),
            campaignId: normalizeId(campaignSection.campaignId),
            campaignIds: Array.isArray(campaignSection.campaignIds)
              ? campaignSection.campaignIds.map((value) => normalizeId(value)).filter(Boolean)
              : [],
            showKicker: campaignSection.showKicker !== false,
            kickerLabel: normalizeCampaignSectionKickerLabel(campaignSection.kickerLabel),
            sort_order: idx + 1,
          }));
      }
      if (supportsCatalogMonetisation && !monetisationIsEnforced) {
        payload.monetisationMode = normalizeMonetisationMode(monetisationModeDraft, 'off');
        payload.monetisationTypes = normalizeMonetisationTypeIds(monetisationTypesDraft);
      }

      const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Save failed (${res.status})`);
      }
      await fetchMenu();
    } catch (err) {
      console.error('Failed to save settings', err);
      setError(err.message || 'Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const itemsBySection = useMemo(() => {
    const safeItems = Array.isArray(items) ? items : [];
    const bySection = new Map();
    for (const section of enabledSections) {
      bySection.set(section.id, []);
    }
    for (const item of safeItems) {
      if (!bySection.has(item.sectionId)) continue;
      bySection.get(item.sectionId).push(item);
    }
    for (const [key, list] of bySection.entries()) {
      list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      bySection.set(key, list);
    }
    return bySection;
  }, [enabledSections, items]);

  const widgetsById = useMemo(() => {
    const map = new Map();
    (Array.isArray(widgets) ? widgets : []).forEach((widget) => {
      if (widget?.id) map.set(String(widget.id), widget);
    });
    return map;
  }, [widgets]);

  const filteredWidgets = useMemo(() => {
    const q = String(widgetFilter.q || '').trim().toLowerCase();
    return (Array.isArray(widgets) ? widgets : []).filter((widget) => {
      const matchesStatus = widgetFilter.status === 'all' || widget.status === widgetFilter.status;
      const matchesPartner = widgetFilter.partner === 'all' || widget.partner === widgetFilter.partner;
      const matchesTags =
        widgetFilter.tags.length === 0 ||
        (Array.isArray(widget.tags) && widgetFilter.tags.every((tag) => widget.tags.includes(tag)));
      const matchesSearch = !q || String(widget.title || '').toLowerCase().includes(q)
        || String(widget.embed_html || '').toLowerCase().includes(q);
      return matchesStatus && matchesPartner && matchesTags && matchesSearch;
    });
  }, [widgets, widgetFilter]);

  const widgetTagSuggestions = useMemo(() => {
    const set = new Set();
    (Array.isArray(widgets) ? widgets : []).forEach((widget) => {
      if (Array.isArray(widget.tags)) {
        widget.tags.forEach((tag) => {
          if (tag) set.add(tag);
        });
      }
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [widgets]);

  const sectionHeaderTranslationMap = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(languageData.sectionHeaders) ? languageData.sectionHeaders : [];
    for (const row of rows) {
      if (!row?.sectionHeaderId) continue;
      map.set(String(row.sectionHeaderId), row);
    }
    return map;
  }, [languageData.sectionHeaders]);

  const sectionTranslationMap = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(languageData.sections) ? languageData.sections : [];
    for (const row of rows) {
      if (!row?.sectionId) continue;
      map.set(String(row.sectionId), row);
    }
    return map;
  }, [languageData.sections]);

  const itemTranslationMap = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(languageData.items) ? languageData.items : [];
    for (const row of rows) {
      if (!row?.itemId) continue;
      map.set(String(row.itemId), row);
    }
    return map;
  }, [languageData.items]);

  const getSectionHeaderDisplayName = useCallback((sectionHeader) => {
    if (!sectionHeader) return '';
    if (!isTranslationMode) return sectionHeader?.name || '';
    const translated = sectionHeaderTranslationMap.get(String(sectionHeader?.id));
    return translated?.name || sectionHeader?.name || '';
  }, [isTranslationMode, sectionHeaderTranslationMap]);

  const getSectionHeaderForSection = useCallback((section) => {
    const sectionHeaderId = normalizeId(section?.headerId);
    if (!sectionHeaderId) return null;
    return savedSectionHeaderById.get(String(sectionHeaderId)) || null;
  }, [savedSectionHeaderById]);

  const getSectionDisplayName = useCallback((section) => {
    if (!isTranslationMode) return section?.name || '';
    const translated = sectionTranslationMap.get(String(section?.id));
    return translated?.name || section?.name || '';
  }, [isTranslationMode, sectionTranslationMap]);

  const getItemTranslation = useCallback(
    (item) => (isTranslationMode ? itemTranslationMap.get(String(item?.id)) : null),
    [isTranslationMode, itemTranslationMap]
  );

  const tableQrUrl = useMemo(() => {
    if (!menu?.landingKey) return '';
    return buildCatalogLandingUrl(catalogConfig, menu.landingKey, {
      unitNumber: (tableNumber || '').trim(),
      unitLabel: (tableLabel || '').trim(),
      sectionId: tableJumpSectionId ? String(tableJumpSectionId) : '',
      itemId: tableJumpItemId ? String(tableJumpItemId) : '',
    });
  }, [catalogConfig, menu?.landingKey, tableJumpItemId, tableJumpSectionId, tableLabel, tableNumber]);

  const menuQrUrl = useMemo(() => buildCatalogLandingUrl(catalogConfig, menu?.landingKey || ''), [catalogConfig, menu?.landingKey]);

  const handleDownloadCatalogQr = async () => {
    if (!menu?.landingKey || !menuQrUrl || downloadingCatalogQr) return;
    setDownloadingCatalogQr(true);
    try {
      const dataUrl = await QRCode.toDataURL(menuQrUrl, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 1024,
        type: 'image/png',
      });
      const qrName = { menu: 'MenuQR', products: 'ProductQR', services: 'ServiceQR' }[catalogConfig.catalogType];
      const filename = `${qrName} - ${resolveSiteLabel(site) || 'Site'} - ${resolveAreaLabel(area) || 'Area'}`;
      const link = document.createElement('a');
      link.href = dataUrl;
      // Replace characters that cannot be used in Windows filenames.
      link.download = `${filename.replace(/[<>:"/\\|?*]/g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.warn('Failed to download catalog QR code', err);
      alert('Failed to download QR code. Please try again.');
    } finally {
      setDownloadingCatalogQr(false);
    }
  };

  const tableCountValue = useMemo(() => normalizeId(tableCountDraft), [tableCountDraft]);
  const bulkTableEntries = useMemo(() => {
    if (!menu?.landingKey) return [];
    if (!tableCountValue) return [];
    const entries = [];
    for (let i = 1; i <= tableCountValue; i += 1) {
      const label = String(tableLabelsDraft?.[String(i)] || '').trim();
      const title = label ? `${catalogConfig.unitLabel} ${i} — ${label}` : `${catalogConfig.unitLabel} ${i}`;
      const url = buildCatalogLandingUrl(catalogConfig, menu.landingKey, { unitNumber: i, unitLabel: label });
      entries.push({ title, url });
    }
    return entries;
  }, [catalogConfig, menu?.landingKey, tableCountValue, tableLabelsDraft]);

  const saveTranslations = async ({ sectionHeaders: sectionHeadersPayload = [], sections: sectionsPayload = [], items: itemsPayload = [] } = {}) => {
    if (!siteId || !areaId || !isTranslationMode) return;
    const token = localStorage.getItem('token');
    const res = await fetch(
      `${catalogConfig.apiBase}/area/${areaId}/translations/${encodeURIComponent(activeLanguage)}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sectionHeaders: sectionHeadersPayload, sections: sectionsPayload, items: itemsPayload }),
      }
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body || `Save failed (${res.status})`);
    }
    await fetchLanguageTranslations(activeLanguage, { silent: true });
  };

  const saveSectionTranslation = async ({ sectionId, name }) => {
    await saveTranslations({
      sections: [{ sectionId, name }],
    });
  };

  const saveSectionHeaderTranslation = async ({ sectionHeaderId, name }) => {
    await saveTranslations({
      sectionHeaders: [{ sectionHeaderId, name }],
    });
  };

  const saveItemTranslation = async (draft) => {
    if (!draft?.id) {
      throw new Error('Missing item id for translation.');
    }
    await saveTranslations({
      items: [{
        itemId: draft.id,
        name: draft.name,
        subtitle: draft.subtitle || '',
        description: draft.description || '',
      }],
    });
    setEditor({ open: false, item: null });
  };

  const buildCatalogItemPayload = useCallback((draft) => {
    const payload = {
      sectionId: Number(draft.sectionId),
      name: draft.name,
      subtitle: draft.subtitle || '',
      description: draft.description || '',
      priceText: draft.priceText || '',
      tags: isMenuCatalog && draft.itemType !== 'widget' ? normalizeMenuItemTags(draft.tags) : [],
      spiciness: isMenuCatalog && draft.itemType !== 'widget' ? normalizeMenuItemSpiciness(draft.spiciness) : '',
      photoAssetIds: Array.isArray(draft.photoAssetIds) ? draft.photoAssetIds : [],
      enabled: Boolean(draft.enabled),
    };
    if (draft.sort_order !== undefined && draft.sort_order !== null) {
      payload.sort_order = draft.sort_order;
    }
    if (draft.itemType) payload.itemType = draft.itemType;
    if (draft.widgetId) payload.widgetId = draft.widgetId;
    return payload;
  }, [isMenuCatalog]);

  const persistCatalogItem = useCallback(async (draft, { refresh = true, closeEditor = false } = {}) => {
    if (!siteId || !areaId) return;
    const token = localStorage.getItem('token');
    const payload = buildCatalogItemPayload(draft);
    const url = draft.id ? `${catalogConfig.apiBase}/items/${draft.id}` : `${catalogConfig.apiBase}/area/${areaId}/items`;
    const method = draft.id ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body || `Save failed (${res.status})`);
    }
    if (closeEditor) setEditor({ open: false, item: null });
    if (refresh) await fetchMenu();
  }, [areaId, buildCatalogItemPayload, catalogConfig.apiBase, fetchMenu, siteId]);

  const saveItem = async (draft) => {
    await persistCatalogItem(draft, { refresh: true, closeEditor: true });
  };

  const toggleItemSelection = (itemId) => {
    if (!itemId) return;
    setSelectedItemIds((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return current.some((id) => String(id) === String(itemId))
        ? current.filter((id) => String(id) !== String(itemId))
        : [...current, itemId];
    });
  };

  const deleteItems = async (itemIds, confirmationMessage) => {
    if (isTranslationMode) return;
    const ids = Array.from(new Set(itemIds.map((id) => normalizeId(id)).filter(Boolean)));
    if (!ids.length) return;
    const ok = window.confirm(confirmationMessage);
    if (!ok) return;
    const token = localStorage.getItem('token');
    for (const itemId of ids) {
      const res = await fetch(`${catalogConfig.apiBase}/items/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        alert(body || `Failed to delete item ${itemId} (${res.status})`);
        break;
      }
    }
    setSelectedItemIds((prev) => prev.filter((id) => !ids.some((deletedId) => String(deletedId) === String(id))));
    await fetchMenu();
  };

  const deleteSelectedItems = () => deleteItems(
    selectedItemIds,
    `Delete ${selectedItemIds.length} selected ${itemLabelPlural.toLowerCase()}?`
  );

  const persistSectionItemOrder = useCallback(async (sectionId, reorderedItems = [], movingItemId = null) => {
    const safeSectionId = normalizeId(sectionId);
    const safeMovingItemId = normalizeId(movingItemId);
    if (!safeSectionId || !Array.isArray(reorderedItems) || !reorderedItems.length) return;
    if (safeMovingItemId && (Array.isArray(movingItemIds) ? movingItemIds : []).some((id) => String(id) === String(safeMovingItemId))) return;

    const changedItems = reorderedItems
      .map((item, index) => {
        const nextSortOrder = index + 1;
        return Number(item.sort_order || 0) === nextSortOrder
          ? null
          : { ...item, sort_order: nextSortOrder };
      })
      .filter(Boolean);

    if (!changedItems.length) return;

    try {
      if (safeMovingItemId) {
        setMovingItemIds((prev) => Array.from(new Set([...(Array.isArray(prev) ? prev : []), safeMovingItemId])));
      }
      for (const item of changedItems) {
        await persistCatalogItem(item, { refresh: false, closeEditor: false });
      }
      await fetchMenu();
    } catch (err) {
      console.error('Failed to reorder catalog items', err);
      alert(err.message || 'Failed to reorder items.');
    } finally {
      if (safeMovingItemId) {
        setMovingItemIds((prev) => (Array.isArray(prev) ? prev : []).filter((id) => String(id) !== String(safeMovingItemId)));
      }
    }
  }, [fetchMenu, movingItemIds, persistCatalogItem]);

  const reorderItemsWithinSectionByDrop = useCallback(async (sectionId, sourceItemId, targetItemId, position = 'before') => {
    const safeSectionId = normalizeId(sectionId);
    const safeSourceItemId = normalizeId(sourceItemId);
    const safeTargetItemId = normalizeId(targetItemId);
    if (!safeSectionId || !safeSourceItemId || !safeTargetItemId || safeSourceItemId === safeTargetItemId) return;

    const sectionItems = (itemsBySection.get(safeSectionId) || []).slice();
    const reordered = reorderListByKeys(
      sectionItems,
      String(safeSourceItemId),
      String(safeTargetItemId),
      (item) => String(item.id),
      position
    );
    await persistSectionItemOrder(safeSectionId, reordered, safeSourceItemId);
  }, [itemsBySection, persistSectionItemOrder]);

  const moveItemWithinSection = useCallback(async (sectionId, itemId, direction) => {
    const safeSectionId = normalizeId(sectionId);
    const safeItemId = normalizeId(itemId);
    const safeDirection = direction < 0 ? -1 : 1;
    if (!safeSectionId || !safeItemId) return;
    if ((Array.isArray(movingItemIds) ? movingItemIds : []).some((id) => String(id) === String(safeItemId))) return;

    const sectionItems = (itemsBySection.get(safeSectionId) || []).slice();
    const currentIndex = sectionItems.findIndex((item) => String(item.id) === String(safeItemId));
    if (currentIndex === -1) return;
    const targetIndex = currentIndex + safeDirection;
    if (targetIndex < 0 || targetIndex >= sectionItems.length) return;

    const reordered = reorderListByDrop(sectionItems, currentIndex, targetIndex, safeDirection > 0 ? 'after' : 'before');
    await persistSectionItemOrder(safeSectionId, reordered, safeItemId);
  }, [itemsBySection, movingItemIds, persistSectionItemOrder]);

  const moveSelected = async ({ targetType, targetSectionId, targetSectionName }) => {
    const token = localStorage.getItem('token');
    const selectedPersistedSectionIds = selectedSectionKeys
      .map((key) => {
        if (!String(key).startsWith('section:')) return null;
        return normalizeId(String(key).slice('section:'.length));
      })
      .filter(Boolean);
    const selectedPersistedItemIds = selectedItemIds.map((id) => normalizeId(id)).filter(Boolean);
    const payload = moveModal.mode === 'sections'
      ? { targetType, sectionIds: selectedPersistedSectionIds }
      : { targetType, itemIds: selectedPersistedItemIds, targetSectionId, targetSectionName };
    const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}/move`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body || `Move failed (${res.status})`);
    }
    setSelectedItemIds([]);
    setSelectedSectionKeys([]);
    setMoveModal({ open: false, mode: 'items' });
    await fetchMenu();
  };

  const openWidgetPicker = (sectionId) => {
    if (!sectionId) return;
    setWidgetPicker({ open: true, sectionId });
    setSelectedWidgetId(null);
  };

  const closeWidgetPicker = () => {
    setWidgetPicker({ open: false, sectionId: null });
  };

  const addWidgetToSection = async () => {
    if (!widgetPicker.sectionId) return;
    if (!selectedWidgetId) {
      alert('Select a widget first.');
      return;
    }
    const widget = widgetsById.get(String(selectedWidgetId));
    if (!widget) {
      alert('Selected widget not found.');
      return;
    }
    const sectionItems = itemsBySection.get(widgetPicker.sectionId) || [];
    const maxSort = sectionItems.reduce((max, item) => {
      const value = Number(item.sort_order || 0);
      return value > max ? value : max;
    }, 0);
    try {
      await saveItem({
        sectionId: widgetPicker.sectionId,
        name: widget.title || 'Widget',
        itemType: 'widget',
        widgetId: widget.id,
        enabled: true,
        photoAssetIds: [],
        sort_order: maxSort + 1,
      });
      closeWidgetPicker();
    } catch (err) {
      console.error('Failed to add widget', err);
      alert(err.message || 'Failed to add widget.');
    }
  };

  const handleGordonApplied = useCallback(async (job) => {
    if (['import', 'update', 'edit'].includes(job.task) && job.status === 'applied' && (job.task !== 'edit' || job.assets?.length || job.textSources?.length)) {
      setRecentMenuUploadAssetIds([]);
      setRecentMenuTextEntries([]);
      setMenuImportMessage('Reviewed menu changes saved.');
      setGordonInstructions('');
    }
    await fetchMenu();
    await onRefreshAssets?.();
  }, [fetchMenu, onRefreshAssets]);

  const showSetupMessage = !menu?.id || !enabledSections.length;

  return (
    <div className="bg-gray-900 rounded p-4">
      {isMenuCatalog && gordonTask ? <GordonMenuTools
        key={`${siteId}:${areaId}:${gordonTask}`}
        task={gordonTask} apiBase={catalogConfig.apiBase} areaId={areaId} siteId={siteId}
        assetIds={recentMenuUploadAssetIds.map(Number).filter(Boolean)} textEntries={recentMenuTextEntries}
        initialInstructions={gordonInstructions} onClose={() => setGordonTask('')}
        onApplied={handleGordonApplied} onBusy={setMenuImporting}
      /> : null}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-lg font-semibold">{catalogConfig.displayName}</div>
          <div className="text-sm text-gray-400">{catalogConfig.description}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-400"
            onClick={fetchMenu}
            disabled={!siteId || !areaId || loading}
          >
            Refresh
          </button>
          <button
            className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
            onClick={saveSettings}
            disabled={!siteId || !areaId || savingSettings || (!settingsDirty && !sectionGroupingPending)}
            title={sectionGroupingPending ? 'Save to keep sections under each header together.' : settingsDirty ? '' : 'No changes to save.'}
          >
            {savingSettings ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>

      {error && <div className="mb-3 text-red-300 text-sm">{error}</div>}
      {loading && <div className="mb-3 text-gray-300 text-sm">Loading…</div>}

      {!siteId && <div className="text-gray-400">Select a site to configure {catalogConfig.shortName.toLowerCase()}.</div>}
      {!!siteId && !areaId && <div className="text-gray-400">Select an area to configure its {catalogConfig.shortName.toLowerCase()}.</div>}

      {!!siteId && !!areaId && (
        <>
          <input
            ref={uploadMenuInputRef}
            type="file"
            accept={MENU_IMPORT_FILE_ACCEPT}
            multiple
            className="hidden"
            onChange={handleUploadMenuFiles}
          />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="text-xs text-gray-400 mr-2">
              Area: <span className="text-gray-200">{area?.name || `#${areaId}`}</span>
            </div>
            <button
              className={`px-3 py-1 rounded text-white ${view === 'settings' ? 'bg-indigo-600' : 'bg-gray-700'}`}
              onClick={() => setView('settings')}
            >
              {catalogConfig.shortName} Settings
            </button>
            <button
              className={`px-3 py-1 rounded text-white ${view === 'items' ? 'bg-indigo-600' : 'bg-gray-700'} disabled:bg-gray-800 disabled:text-gray-500`}
              onClick={() => setView('items')}
              disabled={!canEditItems}
              title={
                canEditItems
                  ? ''
                  : isSharedCatalogMode
                    ? 'Items come from the selected source products catalog.'
                    : settingsDirty
                      ? 'Save settings to apply section changes.'
                      : `Complete the one-off ${catalogConfig.shortName} Settings step first.`
              }
            >
              Create & Edit
            </button>
            <button
              className={`px-3 py-1 rounded text-white ${view === 'translations' ? 'bg-indigo-600' : 'bg-gray-700'} disabled:bg-gray-800 disabled:text-gray-500`}
              onClick={() => setView('translations')}
              disabled={!canManageTranslations}
              title={
                canManageTranslations
                  ? ''
                  : isSharedCatalogMode
                    ? 'Translations are managed on the source products catalog.'
                    : settingsDirty
                      ? 'Save settings first.'
                      : `Save ${catalogConfig.shortName} Settings first.`
              }
            >
              Manage Translations
            </button>
            {isMenuCatalog && (
              <button
                type="button"
                className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                onClick={() => uploadMenuInputRef.current?.click()}
                disabled={!siteId || menuSourcesUploading || menuImporting}
                title={siteId ? 'Upload menu PDFs, images, CSV files, or text files for Gordon.' : 'Select a site first.'}
              >
                Upload Menu
              </button>
            )}
            {isMenuCatalog && (
              <button
                type="button"
                className={`px-3 py-1 rounded text-white ${menuPastePanelOpen ? 'bg-amber-700' : 'bg-amber-600'} disabled:bg-gray-800 disabled:text-gray-500`}
                onClick={() => {
                  setMenuPastePanelOpen((prev) => !prev);
                  setMenuImportError('');
                }}
                disabled={!siteId}
                title={siteId ? 'Paste a menu image or text source directly from the clipboard.' : 'Select a site first.'}
              >
                Paste Text or Image
              </button>
            )}
            <button
              type="button"
              className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-800 disabled:text-gray-500"
              onClick={() => setQrPreview({ open: true, title: `${catalogConfig.shortName} QR`, url: menuQrUrl })}
              disabled={!menu?.landingKey}
              title={menu?.landingKey ? `Preview ${catalogConfig.shortName} QR` : 'Save Settings first to create the landing key.'}
            >
              QR
            </button>
            {isMenuCatalog && (
              <button
                type="button"
                className="px-3 py-1 rounded bg-emerald-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                onClick={handleImportUploadedMenus}
                disabled={(!pendingMenuImageCount && !pendingMenuTextCount) || menuImporting || menuSourcesUploading}
                title={
                  pendingMenuImageCount || pendingMenuTextCount
                    ? (menuImporting
                      ? 'Gordon is already processing these menu inputs.'
                      : 'Send the queued menu images and text sources to Gordon and build menu items.')
                    : 'Upload or paste menu images, CSV files, or text first.'
                }
              >
                {menuImporting ? 'Gordon Working…' : 'Import Menu Inputs'}
              </button>
            )}
            {isMenuCatalog ? <>
              <button type="button" className="px-3 py-1 rounded bg-emerald-700 text-white disabled:opacity-40" disabled={!canEditItems || menuImporting || menuSourcesUploading} onClick={() => setGordonTask('update')}>Update menu from a new menu</button>
              <button type="button" className="px-3 py-1 rounded bg-amber-700 text-white disabled:opacity-40" disabled={!canEditItems || menuImporting || menuSourcesUploading} onClick={() => setGordonTask('edit')}>Edit Menu with Gordon</button>
              <button type="button" className="px-3 py-1 rounded bg-indigo-700 text-white disabled:opacity-40" disabled={!menu?.id || menuImporting || settingsDirty} onClick={() => setGordonTask('photos')}>Organize Menu Photos</button>
              <button type="button" className="px-3 py-1 rounded bg-gray-700 text-white disabled:opacity-40" disabled={!areaId} onClick={() => setGordonTask('history')}>Gordon Jobs</button>
            </> : null}
            {isMenuCatalog && (pendingMenuImageCount || pendingMenuTextCount || menuPasteDetection || menuImportJob?.id) ? (
              <button
                type="button"
                className="px-3 py-1 rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
                onClick={clearPendingMenuImports}
                disabled={menuImporting || menuSourcesUploading}
              >
                Clear Pending
              </button>
            ) : null}
            {supportsCatalogMonetisation && (
              <label className="ml-auto flex items-center gap-2 rounded border border-gray-800 bg-gray-950 px-3 py-1 text-xs text-gray-300">
                <span className="whitespace-nowrap">Monetisation Mode</span>
                <select
                  className="rounded bg-gray-800 px-2 py-1 text-white border border-gray-700 disabled:bg-gray-900 disabled:text-gray-500"
                  value={effectiveMonetisationMode}
                  onChange={(e) => handleMonetisationModeChange(e.target.value)}
                  disabled={monetisationIsEnforced}
                >
                  {MONETISATION_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="hidden xl:inline text-gray-500">
                  {monetisationIsEnforced
                    ? `${monetisationPolicy?.subscriptionType === 'basic' ? 'Basic' : 'Free'} policy is enforced.`
                    : `Saved: ${MONETISATION_MODE_OPTIONS.find((option) => option.value === savedMonetisationMode)?.label || 'Off'}`}
                </span>
              </label>
            )}
          </div>

          {isMenuCatalog && menuPastePanelOpen && (
            <div className="mb-4 rounded border border-amber-700/60 bg-amber-950/30 p-4">
              <div className="text-sm font-medium text-amber-100">Paste Text or Image</div>
              <div className="mt-1 text-xs text-amber-100/80">
                Paste menu text, copied website content, CSV rows, or an image from your clipboard. Gordon will detect the input type automatically.
              </div>
              <textarea
                ref={menuPasteTargetRef}
                className="mt-3 h-32 w-full rounded border border-amber-800/70 bg-gray-950 px-3 py-2 text-sm text-gray-100 outline-none ring-0 placeholder:text-gray-500"
                placeholder="Click here and paste with Ctrl+V or Cmd+V."
                onPaste={handleMenuPaste}
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-amber-100/80">
                <span>Clipboard images are uploaded. Clipboard text is queued locally for the import step.</span>
                <button
                  type="button"
                  className="rounded bg-gray-800 px-3 py-1 text-gray-100 hover:bg-gray-700"
                  onClick={() => setMenuPastePanelOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {isMenuCatalog && menuSourcesUploading ? <p role="status" className="mb-4 text-sm text-amber-200">Uploading menu PDFs…</p> : null}
          {isMenuCatalog && (menuImportError || menuImportMessage || pendingMenuImageCount > 0 || pendingMenuTextCount > 0 || menuPasteDetection || menuImportJob?.id) && (
            <div className="mb-4 text-sm">
              {menuImportError ? (
                <div className="text-red-300">{menuImportError}</div>
              ) : menuImporting && menuImportJob ? (
                <div className="rounded border border-amber-800/60 bg-amber-950/30 px-3 py-3 text-amber-100">
                  <div className="font-medium">
                    {menuImportJob?.message || 'Gordon is processing the menu inputs.'}
                  </div>
                  <div className="mt-1 text-xs text-amber-100/80">
                    {menuImportJob?.stage || 'Working'}
                    {menuImportJob?.progress?.totalSources
                      ? ` · ${Number(menuImportJob.progress.completedSources || 0)} of ${Number(menuImportJob.progress.totalSources || 0)} source${Number(menuImportJob.progress.totalSources || 0) === 1 ? '' : 's'} complete`
                      : ''}
                    {menuImportJob?.progress?.warningCount
                      ? ` · ${Number(menuImportJob.progress.warningCount)} warning${Number(menuImportJob.progress.warningCount) === 1 ? '' : 's'}`
                      : ''}
                  </div>
                  {menuImportJob?.currentSourceLabel ? (
                    <div className="mt-1 text-xs text-amber-50/80">
                      Current source: {menuImportJob.currentSourceLabel}
                    </div>
                  ) : null}
                </div>
              ) : menuImportMessage ? (
                <div className="text-emerald-300">{menuImportMessage}</div>
              ) : (
                <div className="text-gray-300">
                  {[
                    pendingMenuImageCount
                      ? `${pendingMenuImageCount} uploaded menu PDF/image source${pendingMenuImageCount === 1 ? '' : 's'}`
                      : '',
                    pendingMenuTextCount
                      ? `${pendingMenuTextCount} text source${pendingMenuTextCount === 1 ? '' : 's'}`
                      : '',
                  ].filter(Boolean).join(' and ') || 'Menu input queued.'} ready to import with Gordon.
                  {menuPasteDetection ? ` ${menuPasteDetection}.` : ''}
                </div>
              )}
            </div>
          )}

          {isMenuCatalog && (pendingMenuImageCount || pendingMenuTextCount) ? (
            <label className="mb-4 block text-sm text-gray-200">Instructions for Gordon
              <textarea rows={3} maxLength={6000} value={gordonInstructions} disabled={menuImporting} onChange={(event) => setGordonInstructions(event.target.value)} className="mt-1 w-full rounded border border-gray-700 bg-gray-950 p-3" placeholder="Extract English only. Prices are in thousands of VND: expand 120k to 120,000 vnd; preserve full amounts." />
              <span className="text-xs text-gray-400">Applies to every queued menu source. You'll review the proposal before saving.</span>
            </label>
          ) : null}

          {supportsCatalogMonetisation && (
            <div className="mb-4 rounded border border-gray-800 bg-gray-950 px-3 py-2 text-xs text-gray-400">
              {monetisationIsEnforced
                ? `Monetisation is enforced to ${String(monetisationPolicy?.effectiveMode || 'the package policy')} on ${monetisationPolicy?.subscriptionType === 'basic' ? 'Basic' : 'Free'} subscriptions.`
                : effectiveMonetisationMode === 'custom'
                  ? `Custom landing monetisation active. ${effectiveMonetisationTypes.length} type${effectiveMonetisationTypes.length === 1 ? '' : 's'} selected.`
                  : `${MONETISATION_MODE_OPTIONS.find((option) => option.value === effectiveMonetisationMode)?.description || 'No automatic landing page monetisation.'} Site default: ${MONETISATION_MODE_OPTIONS.find((option) => option.value === siteDefaultMonetisationMode)?.label || 'Off'}.`}
            </div>
          )}

          {supportsCatalogMonetisation && !monetisationIsEnforced && effectiveMonetisationMode === 'custom' && (
            <div className="mb-4 rounded border border-cyan-900/60 bg-cyan-950/20 p-3">
              <div className="text-sm font-medium text-cyan-100">Custom Monetisation Types</div>
              <div className="mt-1 text-xs text-cyan-100/75">
                Pick exactly which monetisation modules this landing page can use.
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {CATALOG_MONETISATION_TYPE_OPTIONS.map((option) => {
                  const checked = effectiveMonetisationTypes.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-start gap-3 rounded border px-3 py-3 text-sm transition ${
                        checked
                          ? 'border-cyan-500/70 bg-cyan-900/30 text-cyan-50'
                          : 'border-gray-800 bg-gray-950 text-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={checked}
                        onChange={() => handleMonetisationTypeToggle(option.value)}
                      />
                      <span className="flex flex-col gap-1">
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-inherit opacity-75">{option.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-2 text-xs text-cyan-100/70">
                Saved custom types: {savedMonetisationMode === 'custom' && savedMonetisationTypes.length
                  ? savedMonetisationTypes.length
                  : 0}
              </div>
            </div>
          )}

          {view === 'settings' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                {showSetupMessage && (
                  <div className="bg-indigo-950/40 border border-indigo-800 rounded p-3 text-sm text-indigo-100">
                    One-off setup: configure your sections, then click <span className="font-semibold">Save Settings</span>. After that, you’ll mostly use Create & Edit.
                  </div>
                )}

                {!showSetupMessage && (
                  <div className="bg-gray-950 rounded p-3 border border-gray-800 text-sm text-gray-200">
                    Sections are already configured. Adjust them here if needed (then Save Settings).
                  </div>
                )}

                <SettingsDisclosurePanel
                  title="Landing Page Header"
                  description="Control the logo, heading text, landing-page colours, and how item photos behave beneath it."
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="text-xs text-gray-400">
                      Logo selection, header copy, and colour theme are grouped here so the main sections editor stays visible.
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-gray-700 text-white"
                        onClick={() => setLogoPickerOpen(true)}
                      >
                        Choose Logo
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                        onClick={() => {
                          setLogoCreativeIdDraft(defaults?.logoCreativeId || null);
                          setSettingsDirty(true);
                        }}
                        disabled={!defaults?.logoCreativeId}
                        title={defaults?.logoCreativeId ? '' : 'No site logo found yet.'}
                      >
                        Use Site Logo
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-gray-700 text-white"
                        onClick={() => {
                          setLogoCreativeIdDraft(null);
                          setSettingsDirty(true);
                        }}
                      >
                        Clear Logo
                      </button>
                    </div>
                  </div>

                  <div className="rounded border border-gray-800 bg-gray-900/60 px-3 py-3">
                    {logoCreativeIdDraft ? (
                      <div className="text-sm text-gray-300">Selected logo: asset #{logoCreativeIdDraft}</div>
                    ) : (
                      <div className="text-sm text-gray-400">No logo selected. The page can still use text only.</div>
                    )}
                    {!!site?.logo_url && (
                      <div className="mt-2 text-xs text-gray-500">
                        Site Details logo is available and used by “Use Site Logo”.
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="mb-2 text-sm font-semibold text-gray-100">Logo Size</div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      {CATALOG_LOGO_SIZE_OPTIONS.map((option) => {
                        const active = displaySettingsDraft?.logoSize === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={`rounded border px-3 py-3 text-left ${
                              active
                                ? 'border-indigo-500 bg-indigo-950/30'
                                : 'border-gray-800 bg-gray-900/60 hover:bg-gray-900'
                            }`}
                            onClick={() => updateDisplaySettingsDraft({ logoSize: option.value })}
                          >
                            <div className="text-sm font-semibold text-white">{option.label}</div>
                            <div className="mt-1 text-xs text-gray-400">{option.description}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="block">
                      <div className="mb-1 flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-300">Header Title</span>
                        <button
                          type="button"
                          className="text-xs text-indigo-300 underline"
                          onClick={() => updateDisplaySettingsDraft({ headerTitle: defaultHeaderTitle })}
                        >
                          Reset to Default
                        </button>
                      </div>
                      <input
                        className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                        value={displaySettingsDraft?.headerTitle || ''}
                        onChange={(e) => updateDisplaySettingsDraft({ headerTitle: e.target.value })}
                        placeholder={defaultHeaderTitle || `${catalogConfig.shortName} heading`}
                      />
                      <div className="mt-1 text-xs text-gray-500">Delete the text if you want to hide the heading.</div>
                    </label>

                    <label className="block">
                      <div className="mb-1 flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-300">Subtitle</span>
                        <button
                          type="button"
                          className="text-xs text-indigo-300 underline"
                          onClick={() => updateDisplaySettingsDraft({ headerSubtitle: defaultHeaderSubtitle })}
                        >
                          Reset to Default
                        </button>
                      </div>
                      <input
                        className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                        value={displaySettingsDraft?.headerSubtitle || ''}
                        onChange={(e) => updateDisplaySettingsDraft({ headerSubtitle: e.target.value })}
                        placeholder={defaultHeaderSubtitle || 'Optional subtitle'}
                      />
                      <div className="mt-1 text-xs text-gray-500">Delete the text if you want to remove the subtitle.</div>
                    </label>
                  </div>

                  <div>
                    <div className="mb-2 text-sm font-semibold text-gray-100">Theme Colours</div>
                    <div className="mb-3 text-xs text-gray-400">
                      Adjust the layered panels and text used across menus, products, and services landing pages.
                    </div>
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                      <CatalogColorField
                        label="Page Background"
                        description="Behind the full landing page."
                        value={displaySettingsDraft?.backgroundColor}
                        defaultValue={DEFAULT_CATALOG_BACKGROUND_COLOR}
                        onChange={(value) => updateDisplaySettingsDraft({ backgroundColor: value })}
                      />
                      <CatalogColorField
                        label="Primary Panel"
                        description="Header and section shells."
                        value={displaySettingsDraft?.surfaceColor}
                        defaultValue={DEFAULT_CATALOG_SURFACE_COLOR}
                        onChange={(value) => updateDisplaySettingsDraft({ surfaceColor: value })}
                      />
                      <CatalogColorField
                        label="2nd Panel"
                        description="Sticky nav, promos, socials, and mid-layer panels."
                        value={displaySettingsDraft?.secondarySurfaceColor}
                        defaultValue={DEFAULT_CATALOG_SECONDARY_SURFACE_COLOR}
                        onChange={(value) => updateDisplaySettingsDraft({ secondarySurfaceColor: value })}
                      />
                      <CatalogColorField
                        label="Inner Card"
                        description="Item cards, inner tiles, controls, and selectors."
                        value={displaySettingsDraft?.tertiarySurfaceColor}
                        defaultValue={DEFAULT_CATALOG_TERTIARY_SURFACE_COLOR}
                        onChange={(value) => updateDisplaySettingsDraft({ tertiarySurfaceColor: value })}
                      />
                      <CatalogColorField
                        label="Heading Text"
                        description="Page titles, section headings, and card titles."
                        value={displaySettingsDraft?.headingColor}
                        defaultValue={DEFAULT_CATALOG_HEADING_COLOR}
                        onChange={(value) => updateDisplaySettingsDraft({ headingColor: value })}
                      />
                      <CatalogColorField
                        label="Body Text"
                        description="Descriptions, subtitles, labels, and neutral copy."
                        value={displaySettingsDraft?.textColor}
                        defaultValue={DEFAULT_CATALOG_TEXT_COLOR}
                        onChange={(value) => updateDisplaySettingsDraft({ textColor: value })}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-sm font-semibold text-gray-100">Photo Display</div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {CATALOG_PHOTO_DISPLAY_OPTIONS.map((option) => {
                        const active = displaySettingsDraft?.photoDisplay === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={`rounded border px-3 py-3 text-left ${
                              active
                                ? 'border-indigo-500 bg-indigo-950/30'
                                : 'border-gray-800 bg-gray-900/60 hover:bg-gray-900'
                            }`}
                            onClick={() => updateDisplaySettingsDraft({ photoDisplay: option.value })}
                          >
                            <div className="text-sm font-semibold text-white">{option.label}</div>
                            <div className="mt-1 text-xs text-gray-400">{option.description}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </SettingsDisclosurePanel>

                {supportsSharedSourceCatalogs && (
                  <div className="bg-gray-950 rounded p-4 border border-gray-800 space-y-4">
                    <div>
                      <div className="text-lg font-semibold">Product Content Source</div>
                      <div className="text-xs text-gray-400">
                        Keep this QR on the current site, but optionally pull products from another products catalog in the same account.
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <button
                        type="button"
                        className={`rounded border px-4 py-4 text-left ${
                          !isSharedCatalogMode
                            ? 'border-indigo-500 bg-indigo-950/30'
                            : 'border-gray-800 bg-gray-900/60 hover:bg-gray-900'
                        }`}
                        onClick={() => handleContentModeChange('native')}
                      >
                        <div className="text-sm font-semibold text-white">This site manages its own products</div>
                        <div className="mt-1 text-xs text-gray-400">
                          Use local sections, items, photos, and translations for this products QR.
                        </div>
                      </button>
                      <button
                        type="button"
                        className={`rounded border px-4 py-4 text-left ${
                          isSharedCatalogMode
                            ? 'border-indigo-500 bg-indigo-950/30'
                            : 'border-gray-800 bg-gray-900/60 hover:bg-gray-900'
                        }`}
                        onClick={() => handleContentModeChange('shared')}
                      >
                        <div className="text-sm font-semibold text-white">Use products from another site</div>
                        <div className="mt-1 text-xs text-gray-400">
                          Keep this site’s QR, branding shell, socials, and campaigns, but mirror the product content from a source catalog.
                        </div>
                      </button>
                    </div>

                    {isSharedCatalogMode && (
                      <div className="space-y-3 rounded border border-gray-800 bg-gray-900/60 px-4 py-4">
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
                          <label className="block">
                            <div className="mb-1 text-xs text-gray-300">Source Products QR</div>
                            <select
                              className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white disabled:bg-gray-900 disabled:text-gray-500"
                              value={sourceCatalogIdDraft || ''}
                              onChange={(e) => handleSourceCatalogChange(e.target.value)}
                              disabled={sourceCatalogsLoading || availableSourceCatalogOptions.length === 0}
                            >
                              <option value="">Choose a source products catalog…</option>
                              {availableSourceCatalogOptions.map((sourceCatalog) => (
                                <option key={`source-catalog-${sourceCatalog.catalogId}`} value={sourceCatalog.catalogId}>
                                  {buildSourceCatalogOptionLabel(sourceCatalog)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="flex items-end">
                            <button
                              type="button"
                              className="rounded bg-gray-700 px-3 py-2 text-sm text-white disabled:bg-gray-800 disabled:text-gray-500"
                              onClick={() => {
                                if (selectedSourceCatalogOption) {
                                  syncSharedCatalogStructureFromSource(selectedSourceCatalogOption);
                                }
                              }}
                              disabled={!selectedSourceCatalogOption}
                              title={selectedSourceCatalogOption ? 'Copy the latest source section structure into this host catalog.' : 'Choose a source catalog first.'}
                            >
                              Refresh From Source
                            </button>
                          </div>
                        </div>

                        {sourceCatalogsLoading && (
                          <div className="text-sm text-gray-300">Loading available products catalogs…</div>
                        )}

                        {sourceCatalogsError && (
                          <div className="text-sm text-red-300">{sourceCatalogsError}</div>
                        )}

                        {!sourceCatalogsLoading && !availableSourceCatalogOptions.length && (
                          <div className="text-sm text-amber-200">
                            No native products catalogs are available in this account yet. Create one on the source site first.
                          </div>
                        )}

                        {!!selectedSourceCatalogLabel && (
                          <div className="rounded border border-cyan-800/70 bg-cyan-950/20 px-3 py-3 text-sm text-cyan-50">
                            Shared mode is active. Product content comes from <span className="font-semibold">{selectedSourceCatalogLabel}</span>.
                          </div>
                        )}

                        {selectedSourceCatalogOption && normalizeCatalogContentMode(selectedSourceCatalogOption.contentMode) === 'shared' && (
                          <div className="rounded border border-amber-800/70 bg-amber-950/20 px-3 py-3 text-sm text-amber-100">
                            This saved source catalog is itself shared. Pick a native products catalog instead to avoid chaining shared sources.
                          </div>
                        )}

                        <div className="rounded border border-gray-800 bg-black/20 px-3 py-3 text-xs text-gray-400">
                          Items and translations are managed on the source catalog. This host QR still controls its own landing key, header branding, socials, and campaign placements.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {isMenuCatalog && (
                  <SettingsDisclosurePanel
                    title="Menu Filters, Icons & Photo Handling"
                    description="Control which food tags this menu uses, how landing-page badges and filters behave, and whether item photos auto-rotate or stay manual."
                  >
                    <MenuTagAvailabilityPicker
                      value={menuFeaturesDraft.enabledTagKeys}
                      items={items}
                      iconSet={menuFeaturesDraft.tagIconSet}
                      customIconUrls={menuFeaturesDraft.customIconUrls}
                      language={activeLanguage}
                      onChange={(enabledTagKeys) => updateMenuFeaturesDraft({ enabledTagKeys })}
                    />
                    <label className="mt-4 flex items-start gap-3 rounded border border-gray-800 bg-gray-900/60 px-3 py-3 text-sm text-gray-200">
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-indigo-500"
                        checked={Boolean(menuFeaturesDraft.showItemTags)}
                        onChange={(e) => updateMenuFeaturesDraft({ showItemTags: e.target.checked })}
                      />
                        <span>
                        <span className="block font-semibold text-white">Show menu item tags on the landing page</span>
                        <span className="mt-1 block text-xs text-gray-400">
                          When enabled, tagged menu items show badge icons on each card and visitors can use filters like vegetarian, hot, or no seafood.
                        </span>
                      </span>
                    </label>
                    <MenuFilterVisibilityPicker
                      value={menuFeaturesDraft.enabledFilterKeys}
                      items={items}
                      enabledTagKeys={menuFeaturesDraft.enabledTagKeys}
                      language={activeLanguage}
                      onChange={(enabledFilterKeys) => updateMenuFeaturesDraft({ enabledFilterKeys })}
                    />
                    <MenuTagIconSetPicker
                      value={menuFeaturesDraft.tagIconSet}
                      customIconUrls={menuFeaturesDraft.customIconUrls}
                      enabledTagKeys={menuFeaturesDraft.enabledTagKeys}
                      language={activeLanguage}
                      onChange={(tagIconSet) => updateMenuFeaturesDraft({ tagIconSet })}
                      onChangeCustomIcons={(customIconUrls) => updateMenuFeaturesDraft({ customIconUrls })}
                    />
                    <div className="mt-4 rounded border border-gray-800 bg-gray-900/60 px-3 py-3">
                      <div className="text-sm font-semibold text-white">Photo Handling</div>
                      <div className="mt-1 text-xs text-gray-400">
                        Default now keeps the first photo still and only loads extra photos when visitors use the arrows.
                      </div>
                      <label className="mt-3 flex items-start gap-3 text-sm text-gray-200">
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-indigo-500"
                          checked={Boolean(menuFeaturesDraft.autoRotatePhotos)}
                          onChange={(e) => updateMenuFeaturesDraft({ autoRotatePhotos: e.target.checked })}
                        />
                        <span>
                          <span className="block font-semibold text-white">Auto-rotate through item photos</span>
                          <span className="mt-1 block text-xs text-gray-400">
                            When enabled, landing pages cycle through each item&apos;s photos automatically. Leave this off to improve load times and keep manual arrow navigation only.
                          </span>
                        </span>
                      </label>
                    </div>
                  </SettingsDisclosurePanel>
                )}

                <SettingsDisclosurePanel
                  title="Sections"
                  description={
                    isSharedCatalogMode
                      ? 'Sections are mirrored from the source catalog so host campaigns and socials still have placement anchors.'
                      : 'Enable, rename, group, and reorder section headers, sections, advertising, and socials in one place.'
                  }
                  defaultOpen
                >
                  <div className="rounded border border-gray-800 bg-gray-950 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="text-lg font-semibold">Sections</div>
                        <div className="text-xs text-gray-400">
                          {isSharedCatalogMode
                            ? 'Sections are mirrored from the source catalog so host campaigns and socials still have placement anchors.'
                            : 'Enable/disable, rename, group, and reorder sections. Save once, then add items.'}
                        </div>
                      </div>
                      {!isSharedCatalogMode && (
                        <button
                          className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                          onClick={() => {
                            const ok = window.confirm('Add the common template? (Starters, Mains, Desserts, Drinks)');
                            if (!ok) return;
                            applyTemplate();
                          }}
                          disabled={sectionsDraft.length > 0}
                        >
                          Add Template
                        </button>
                      )}
                    </div>

                    <div className="mb-4 rounded border border-gray-800 bg-gray-900/60 px-4 py-4"
                      onDropCapture={(event) => {
                        if (draggingSectionHeaderKey || draggingSectionEditorKey) event.preventDefault();
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-white">Section Headers</div>
                          <div className="text-xs text-gray-400">
                            {isSharedCatalogMode
                              ? 'These come from the selected source products catalog and stay read-only here.'
                              : 'Groups like Food or Drinks. Moving a header also moves its sections together. Assign sections using the dropdown below.'}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {sortedSectionHeadersDraft.length} configured
                        </div>
                      </div>

                      {!sortedSectionHeadersDraft.length && (
                        <div className="mt-3 text-sm text-gray-400">
                          {isSharedCatalogMode
                            ? 'The current source catalog has no section headers.'
                            : 'No section headers yet. Add one only if you want grouped jump links on the landing page.'}
                        </div>
                      )}

                      {!!sortedSectionHeadersDraft.length && (
                        <div className="mt-3 space-y-2">
                          {sortedSectionHeadersDraft.map((sectionHeader, index) => {
                            const key = getSectionHeaderDraftKey(sectionHeader);
                            const usageCount = sectionHeaderDraftUsageByKey.get(key) || 0;
                            const isDropTarget = sectionHeaderDropIndicator.key === key;
                            return (
                              <div
                                key={key}
                                className={`flex flex-col gap-2 rounded border border-gray-800 bg-black/20 px-3 py-3 md:flex-row md:items-center ${
                                  isDropTarget
                                    ? (sectionHeaderDropIndicator.position === 'after' ? 'border-b-2 border-b-sky-400' : 'border-t-2 border-t-sky-400')
                                    : ''
                                }`}
                                onDragOver={(event) => {
                                  if (!draggingSectionHeaderKey || isSharedCatalogMode) return;
                                  if (draggingSectionHeaderKey === key) {
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = 'none';
                                    setSectionHeaderDropIndicator({ key: '', position: 'before' });
                                    return;
                                  }
                                  const position = resolveEffectiveDropPosition(
                                    event,
                                    sortedSectionHeadersDraft,
                                    draggingSectionHeaderKey,
                                    key,
                                    (entry) => getSectionHeaderDraftKey(entry)
                                  );
                                  if (!position) {
                                    setSectionHeaderDropIndicator({ key: '', position: 'before' });
                                    return;
                                  }
                                  event.preventDefault();
                                  event.dataTransfer.dropEffect = 'move';
                                  setSectionHeaderDropIndicator({
                                    key,
                                    position,
                                  });
                                }}
                                onDrop={(event) => {
                                  if (!draggingSectionHeaderKey || sectionHeaderDropIndicator.key !== key || isSharedCatalogMode) return;
                                  event.preventDefault();
                                  event.stopPropagation();
                                  reorderSectionHeadersByDrop(draggingSectionHeaderKey, key, sectionHeaderDropIndicator.position);
                                  setDraggingSectionHeaderKey('');
                                  setSectionHeaderDropIndicator({ key: '', position: 'before' });
                                }}
                              >
                                {!isSharedCatalogMode && (
                                  <button
                                    type="button"
                                    className="cursor-grab rounded border border-gray-700 bg-gray-800 px-2 py-1 text-[11px] uppercase tracking-[0.08em] text-gray-300 active:cursor-grabbing"
                                    draggable
                                    onDragStart={(event) => {
                                      event.dataTransfer.effectAllowed = 'move';
                                      event.dataTransfer.setData('text/plain', key);
                                      setDraggingSectionHeaderKey(key);
                                    }}
                                    onDragEnd={() => {
                                      setDraggingSectionHeaderKey('');
                                      setSectionHeaderDropIndicator({ key: '', position: 'before' });
                                    }}
                                  >
                                    Drag
                                  </button>
                                )}
                                {isSharedCatalogMode ? (
                                  <>
                                    <div className="text-xs text-cyan-200/80">
                                      {sectionHeader.enabled ? 'Enabled in source' : 'Disabled in source'}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="rounded border border-gray-800 bg-black/20 px-3 py-2 text-white">
                                        {sectionHeader.name || 'Untitled header'}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <label className="flex items-center gap-2 text-sm text-gray-200">
                                      <input
                                        type="checkbox"
                                        className="accent-indigo-500"
                                        checked={Boolean(sectionHeader.enabled)}
                                        onChange={(e) => updateSectionHeader(key, { enabled: e.target.checked })}
                                      />
                                      Enabled
                                    </label>
                                    <div className="min-w-0 flex-1">
                                      <input
                                        className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                                        value={sectionHeader.name || ''}
                                        onChange={(e) => updateSectionHeader(key, { name: e.target.value })}
                                        placeholder="Section header name"
                                      />
                                    </div>
                                  </>
                                )}
                                <div className="text-xs text-gray-400">
                                  {usageCount} section{usageCount === 1 ? '' : 's'}
                                </div>
                                {!isSharedCatalogMode && (
                                  <div className="flex items-center gap-2">
                                    <button
                                      className="rounded bg-gray-700 px-2 py-1 text-xs text-white disabled:bg-gray-800 disabled:text-gray-500"
                                      onClick={() => moveSectionHeader(key, -1)}
                                      disabled={index === 0}
                                    >
                                      Up
                                    </button>
                                    <button
                                      className="rounded bg-gray-700 px-2 py-1 text-xs text-white disabled:bg-gray-800 disabled:text-gray-500"
                                      onClick={() => moveSectionHeader(key, 1)}
                                      disabled={index === sortedSectionHeadersDraft.length - 1}
                                    >
                                      Down
                                    </button>
                                    <button
                                      className="rounded bg-red-700 px-2 py-1 text-xs text-white"
                                      onClick={() => deleteSectionHeader(key)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {!isSharedCatalogMode && (
                        <div className="mt-3 flex flex-col gap-2 md:flex-row">
                          <input
                            className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                            value={sectionHeaderName}
                            onChange={(e) => setSectionHeaderName(e.target.value)}
                            placeholder="Add a section header (e.g. Food, Drinks, Cakes)"
                          />
                          <button
                            className="rounded bg-indigo-600 px-3 py-2 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                            onClick={addSectionHeader}
                            disabled={!sectionHeaderName.trim()}
                          >
                            Add Header
                          </button>
                        </div>
                      )}
                    </div>

                  {!sectionsDraft.length && (
                    <div className="text-sm text-gray-400 mb-3">
                      {isSharedCatalogMode
                        ? 'Choose a source catalog and refresh from source to copy its sections here.'
                        : 'No sections yet. Add at least one, then click Save Settings.'}
                    </div>
                  )}

                  {!!sectionEditorRows.length && (
                    <div className="mb-3 space-y-3"
                      onDropCapture={(event) => {
                        if (draggingSectionHeaderKey || draggingSectionEditorKey) event.preventDefault();
                      }}
                      onDragLeave={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) {
                          setSectionEditorDropIndicator({ key: '', position: 'before' });
                        }
                      }}
                    >
                      {!isSharedCatalogMode && (
                        <div className="text-sm text-gray-300">
                          Drag or use Up/Down to reorder within a group. Change the dropdown to move a section to another group.
                          Move section headers above to reorder whole groups. Changes take effect after Save Settings.
                        </div>
                      )}
                      {sectionGroupingPending && (
                        <div className="rounded border border-cyan-900 bg-cyan-950/30 px-3 py-2 text-sm text-cyan-100">
                          Sections are shown together under their headers. Save Settings to apply this order to the published menu.
                        </div>
                      )}
                      {isSharedCatalogMode ? (
                        <div className="rounded border border-cyan-900/60 bg-cyan-950/20 px-3 py-2 text-xs text-cyan-50/85">
                          Section names and order are locked to the source catalog. You can still place advertising and socials between these sections below.
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-800 bg-gray-900/60 px-3 py-2">
                          <div className="text-xs text-gray-300">
                            {selectedSectionKeys.length} section{selectedSectionKeys.length === 1 ? '' : 's'} selected
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                              onClick={() => setSelectedSectionKeys(selectableSectionKeys)}
                              disabled={!selectableSectionKeys.length}
                            >
                              Select All
                            </button>
                            <button
                              className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                              onClick={() => setSelectedSectionKeys([])}
                              disabled={!selectedSectionKeys.length}
                            >
                              Clear All
                            </button>
                            <button
                              className="px-2 py-1 bg-indigo-600 text-xs rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
                              onClick={() => setMoveModal({ open: true, mode: 'sections' })}
                              disabled={
                                !selectedSectionKeys.length
                                || settingsDirty
                                || selectedSectionKeys.some((key) => !String(key).startsWith('section:'))
                              }
                              title={settingsDirty ? 'Save section changes before moving sections.' : ''}
                            >
                              Move To
                            </button>
                            <button
                              className="px-2 py-1 bg-red-700 text-xs rounded text-white disabled:bg-red-900 disabled:text-gray-400"
                              onClick={deleteSelectedSections}
                              disabled={!selectedSectionKeys.length}
                            >
                              Delete Selected
                            </button>
                          </div>
                        </div>
                      )}
                      {sectionEditorRows.map((row) => {
                        const rowIsDropTarget = sectionEditorDropIndicator.key === row.key;
                        const rowDropClass = rowIsDropTarget
                          ? (sectionEditorDropIndicator.position === 'after' ? 'border-b-2 border-b-sky-400' : 'border-t-2 border-t-sky-400')
                          : '';
                        const canDragSectionEditorRow = row.kind !== 'section' || !isSharedCatalogMode;
                        const draggingSectionEditorRow = sectionEditorRows.find((entry) => entry.key === draggingSectionEditorKey) || null;
                        const handleSectionEditorDragOver = (event) => {
                          if (!draggingSectionEditorKey) return;
                          if (draggingSectionEditorKey === row.key
                            || (draggingSectionEditorRow?.kind === 'section'
                              && (row.kind !== 'section'
                                || String(draggingSectionEditorRow.section.headerKey || '') !== String(row.section.headerKey || '')))) {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'none';
                            setSectionEditorDropIndicator({ key: '', position: 'before' });
                            return;
                          }
                          const position = draggingSectionEditorRow?.kind === 'section'
                            ? resolveEffectiveDropPosition(
                              event,
                              sortedSectionsDraft,
                              draggingSectionEditorKey,
                              row.key,
                              (entry) => getSectionDraftKey(entry)
                            )
                            : resolveEffectiveDropPosition(
                              event,
                              sectionEditorRows,
                              draggingSectionEditorKey,
                              row.key,
                              (entry) => entry.key
                            );
                          if (!position) {
                            setSectionEditorDropIndicator({ key: '', position: 'before' });
                            return;
                          }
                          event.preventDefault();
                          event.dataTransfer.dropEffect = 'move';
                          setSectionEditorDropIndicator({
                            key: row.key,
                            position,
                          });
                        };
                        const handleSectionEditorDrop = (event) => {
                          if (!draggingSectionEditorKey || sectionEditorDropIndicator.key !== row.key) return;
                          event.preventDefault();
                          event.stopPropagation();
                          reorderSectionEditorRowsByDrop(draggingSectionEditorKey, row.key, sectionEditorDropIndicator.position);
                          setDraggingSectionEditorKey('');
                          setSectionEditorDropIndicator({ key: '', position: 'before' });
                        };
                        if (row.kind === 'promotion') {
                          const isNetworkAd = row.typeId === 'landing_network_ads';
                          const promotionStyle = displaySettingsDraft?.promotionStyles?.[row.typeId]
                            || CATALOG_PROMOTION_STYLE_DEFAULTS[row.typeId];
                          const promotionEmphasis = promotionStyle?.emphasis || 'standard';
                          const maxPosition = isNetworkAd
                            ? Math.max(1, sortedSectionsDraft.length)
                            : Math.max(1, sortedSectionsDraft.length + 1);
                          return (
                            <div
                              key={row.key}
                              className={`flex flex-col gap-3 rounded-2xl border px-4 py-4 ${
                                isNetworkAd
                                  ? 'border-amber-700/70 bg-amber-950/25'
                                  : 'border-violet-800/70 bg-violet-950/25'
                              } ${promotionEmphasis === 'compact' ? 'shadow-none' : ''} ${rowDropClass}`}
                              style={{
                                borderColor: promotionStyle?.accentColor,
                                borderWidth: promotionEmphasis === 'prominent' ? '2px' : '1px',
                                boxShadow: promotionEmphasis === 'prominent'
                                  ? `0 18px 42px -24px ${promotionStyle?.accentColor}`
                                  : undefined,
                              }}
                              onDragOver={handleSectionEditorDragOver}
                              onDrop={handleSectionEditorDrop}
                            >
                              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                                <div className="min-w-0 flex-1">
                                  <div className={`text-xs uppercase tracking-[0.2em] ${isNetworkAd ? 'text-amber-300' : 'text-violet-300'}`}>
                                    {isNetworkAd ? 'Platform inventory • Advertisement' : 'Monetisation component'}
                                  </div>
                                  <div className="mt-1 text-sm font-semibold text-white">
                                    {CATALOG_PROMOTION_LABELS[row.typeId] || row.typeId}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-400">
                                    {isNetworkAd
                                      ? 'Shown only when an eligible administrator assignment exists. It cannot be removed here.'
                                      : 'Editor preview; the public menu renders this position only when eligible content exists.'}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="cursor-grab rounded bg-gray-700 px-2 py-1 text-xs text-white active:cursor-grabbing"
                                    draggable={canDragSectionEditorRow}
                                    onDragStart={() => setDraggingSectionEditorKey(row.key)}
                                    onDragEnd={() => {
                                      setDraggingSectionEditorKey('');
                                      setSectionEditorDropIndicator({ key: '', position: 'before' });
                                    }}
                                  >
                                    Drag
                                  </button>
                                  <button
                                    className="rounded bg-gray-700 px-2 py-1 text-xs text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => movePromotion(row.key, -1)}
                                    disabled={row.position <= 1}
                                  >
                                    Up
                                  </button>
                                  <button
                                    className="rounded bg-gray-700 px-2 py-1 text-xs text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => movePromotion(row.key, 1)}
                                    disabled={row.position >= maxPosition}
                                  >
                                    Down
                                  </button>
                                </div>
                              </div>
                              {renderPromotionAppearanceControls(row.typeId)}
                            </div>
                          );
                        }
                        if (row.kind === 'campaign') {
                          const campaignSection = row.campaignSection;
                          const campaignSectionKey = row.key;
                          const isDynamic = normalizeCampaignSectionMode(campaignSection.mode, 'single') === 'dynamic';
                          const campaignRows = isDynamic
                            ? (Array.isArray(campaignSection.campaignIds) ? campaignSection.campaignIds : [])
                              .map((campaignId) => campaignAdsById.get(String(campaignId)) || null)
                              .filter(Boolean)
                            : [campaignAdsById.get(String(campaignSection.campaignId || '')) || null].filter(Boolean);
                          const previewCampaign = campaignRows[0] || null;
                          return (
                            <div
                              key={campaignSectionKey}
                              className={`flex flex-col gap-2 rounded border border-rose-900/70 bg-rose-950/25 px-3 py-3 ${rowDropClass}`}
                              onDragOver={handleSectionEditorDragOver}
                              onDrop={handleSectionEditorDrop}
                            >
                              <div className="flex flex-col md:flex-row md:items-center gap-3">
                                <div className="flex min-w-0 flex-1 items-start gap-3">
                                  <div className="h-14 w-14 flex-none overflow-hidden rounded border border-rose-900/70 bg-gray-900">
                                    {previewCampaign?.previewImageUrl ? (
                                      <img
                                        src={previewCampaign.previewImageUrl}
                                        alt={previewCampaign.title || 'Campaign preview'}
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="flex h-full items-center justify-center px-2 text-center text-[10px] text-gray-500">
                                        No art
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs uppercase tracking-wide text-rose-200">
                                      {isDynamic ? 'Dynamic Ad Section' : 'Ad Section'}
                                    </div>
                                    {isDynamic ? (
                                      <div className="text-sm text-white">
                                        {campaignRows.length
                                          ? `${campaignRows.length} campaigns selected for round-robin rotation`
                                          : 'Dynamic ad selection is empty.'}
                                      </div>
                                    ) : (
                                      <div className="text-sm text-white">
                                        {previewCampaign?.title || 'Selected campaign unavailable'}
                                      </div>
                                    )}
                                  <div className="text-xs text-rose-100/70">
                                    {previewCampaign?.siteName || 'Campaign ad'}
                                  </div>
                                  <div className="mt-2 text-xs text-rose-100/70">
                                    {campaignSection.showKicker !== false
                                      ? `Landing label: ${normalizeCampaignSectionKickerLabel(campaignSection.kickerLabel)}`
                                      : 'Landing label hidden'}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="cursor-grab rounded bg-gray-700 px-2 py-1 text-xs text-white active:cursor-grabbing"
                                    draggable={canDragSectionEditorRow}
                                    onDragStart={() => setDraggingSectionEditorKey(campaignSectionKey)}
                                    onDragEnd={() => {
                                      setDraggingSectionEditorKey('');
                                      setSectionEditorDropIndicator({ key: '', position: 'before' });
                                    }}
                                  >
                                    Drag
                                  </button>
                                  <button
                                    className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => moveCampaignSection(campaignSectionKey, -1)}
                                    disabled={!canMoveCampaignSection(campaignSectionKey, -1)}
                                  >
                                    Up
                                  </button>
                                  <button
                                    className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => moveCampaignSection(campaignSectionKey, 1)}
                                    disabled={!canMoveCampaignSection(campaignSectionKey, 1)}
                                  >
                                    Down
                                  </button>
                                  <button
                                    className="px-2 py-1 bg-red-700 text-xs rounded text-white"
                                    onClick={() => deleteCampaignSection(campaignSectionKey)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                              {isDynamic && campaignRows.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {campaignRows.map((campaignAd) => (
                                    <div
                                      key={`campaign-section-pill-${campaignSectionKey}-${campaignAd.campaignId}`}
                                      className="rounded-full border border-rose-900/70 bg-black/20 px-3 py-1 text-xs text-rose-100"
                                    >
                                      {campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                                <label className="flex items-center gap-2 text-sm text-rose-50">
                                  <input
                                    type="checkbox"
                                    className="accent-rose-400"
                                    checked={campaignSection.showKicker !== false}
                                    onChange={(e) => updateCampaignSection(campaignSectionKey, {
                                      showKicker: e.target.checked,
                                    })}
                                  />
                                  Show label above campaign
                                </label>
                                <label className="block">
                                  <div className="mb-1 text-xs uppercase tracking-wide text-rose-100/70">
                                    Label Text
                                  </div>
                                  <input
                                    className="w-full rounded border border-rose-900/70 bg-black/20 px-3 py-2 text-sm text-white disabled:cursor-not-allowed disabled:text-gray-500"
                                    value={campaignSection.kickerLabel || ''}
                                    onChange={(e) => updateCampaignSection(campaignSectionKey, {
                                      kickerLabel: e.target.value.slice(0, 80),
                                    })}
                                    placeholder={DEFAULT_CATALOG_CAMPAIGN_SECTION_KICKER_LABEL}
                                    disabled={campaignSection.showKicker === false}
                                  />
                                </label>
                              </div>
                            </div>
                          );
                        }

                        if (row.kind === 'socials') {
                          const socialPosition = resolveCatalogSocialSortOrder(row.socialSection, sortedSectionsDraft.length);
                          const maxSocialPosition = Math.max(1, sortedSectionsDraft.length + 1);
                          const socialPromotionStyle = displaySettingsDraft?.promotionStyles?.socials
                            || CATALOG_PROMOTION_STYLE_DEFAULTS.socials;
                          return (
                            <div
                              key={row.key}
                              className={`flex flex-col gap-3 rounded-2xl border border-cyan-800/70 bg-gradient-to-br from-cyan-950/45 via-sky-950/20 to-emerald-950/20 px-4 py-4 shadow-[0_20px_45px_-30px_rgba(34,211,238,0.7)] ${rowDropClass}`}
                              style={{
                                borderColor: socialPromotionStyle.accentColor,
                                borderWidth: socialPromotionStyle.emphasis === 'prominent' ? '2px' : '1px',
                                boxShadow: socialPromotionStyle.emphasis === 'prominent'
                                  ? `0 18px 42px -24px ${socialPromotionStyle.accentColor}`
                                  : undefined,
                              }}
                              onDragOver={handleSectionEditorDragOver}
                              onDrop={handleSectionEditorDrop}
                            >
                              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs uppercase tracking-[0.24em] text-cyan-200/80">
                                    Socials Section
                                  </div>
                                  <div className="mt-1 text-sm font-semibold text-white">
                                    {row.socialSection?.title || DEFAULT_CATALOG_SOCIAL_SECTION_TITLE}
                                  </div>
                                  <div className="mt-1 text-xs text-cyan-100/70">
                                    {selectedCatalogSocialEntries.length
                                      ? `${selectedCatalogSocialEntries.length} social link${selectedCatalogSocialEntries.length === 1 ? '' : 's'} selected`
                                      : 'Select at least one filled-out social link below to show this section.'}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="cursor-grab rounded bg-gray-700 px-2 py-1 text-xs text-white active:cursor-grabbing"
                                    draggable={canDragSectionEditorRow}
                                    onDragStart={() => setDraggingSectionEditorKey(row.key)}
                                    onDragEnd={() => {
                                      setDraggingSectionEditorKey('');
                                      setSectionEditorDropIndicator({ key: '', position: 'before' });
                                    }}
                                  >
                                    Drag
                                  </button>
                                  <button
                                    className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => moveSocialSection(-1)}
                                    disabled={socialPosition <= 1}
                                  >
                                    Up
                                  </button>
                                  <button
                                    className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => moveSocialSection(1)}
                                    disabled={socialPosition >= maxSocialPosition}
                                  >
                                    Down
                                  </button>
                                </div>
                              </div>
                              {!!selectedCatalogSocialSectionEntries.length && (
                                <div className="flex flex-wrap gap-2">
                                  {selectedCatalogSocialSectionEntries.map((entry) => (
                                    <div
                                      key={`social-section-pill-${entry.key}`}
                                      className={`rounded-full border border-cyan-700/70 bg-black/20 px-3 py-1 text-xs text-cyan-100 bg-gradient-to-r ${entry.accentClass}`}
                                    >
                                      {entry.ctaLabel}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {renderPromotionAppearanceControls('socials')}
                            </div>
                          );
                        }

                        const section = row.section;
                        const key = row.key;
                        const position = sectionDraftPositionByKey.get(key) ?? 0;
                        const previousSection = sortedSectionsDraft[position - 1];
                        const nextSection = sortedSectionsDraft[position + 1];
                        const sameHeading = (neighbor) => neighbor && String(neighbor.headerKey || '') === String(section.headerKey || '');
                        const startsGroup = !sameHeading(previousSection);
                        const sectionHeaderDraft = sectionHeaderDraftByKey.get(String(section.headerKey || '')) || null;
                        return (
                          <div
                            key={key}
                            className={`flex flex-col gap-2 rounded border border-gray-800 bg-gray-900/60 px-3 py-2 md:flex-row md:items-center ${rowDropClass} ${draggingSectionEditorKey === key ? 'opacity-50' : ''}`}
                            onDragOver={handleSectionEditorDragOver}
                            onDrop={handleSectionEditorDrop}
                          >
                            {isSharedCatalogMode ? (
                              <>
                                <div className="text-xs uppercase tracking-wide text-cyan-200/80">
                                  Shared Section
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold text-white">{section.name || 'Untitled section'}</div>
                                  <div className="text-xs text-gray-400">
                                    {sectionHeaderDraft?.name
                                      ? `Section header: ${sectionHeaderDraft.name}`
                                      : 'No section header'}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="cursor-grab rounded border border-gray-700 bg-gray-800 px-2 py-1 text-[11px] uppercase tracking-[0.08em] text-gray-300 active:cursor-grabbing"
                                  draggable={canDragSectionEditorRow}
                                  onDragStart={(event) => {
                                    event.dataTransfer.effectAllowed = 'move';
                                    event.dataTransfer.setData('text/plain', key);
                                    setDraggingSectionEditorKey(key);
                                  }}
                                  onDragEnd={() => {
                                    setDraggingSectionEditorKey('');
                                    setSectionEditorDropIndicator({ key: '', position: 'before' });
                                  }}
                                >
                                  Drag
                                </button>
                                <div className="flex shrink-0 flex-col gap-2">
                                  <label className="flex items-center gap-2 text-sm text-gray-200">
                                    <input
                                      type="checkbox"
                                      className="accent-indigo-500"
                                      checked={selectedSectionKeys.includes(key)}
                                      onChange={() => toggleSectionSelection(key)}
                                    />
                                    Select
                                  </label>
                                  <label className="flex items-center gap-2 text-sm text-gray-200">
                                    <input
                                      type="checkbox"
                                      className="accent-indigo-500"
                                      checked={Boolean(section.enabled)}
                                      onChange={(e) => updateSection(key, { enabled: e.target.checked })}
                                    />
                                    Enabled
                                  </label>
                                </div>
                                <div className="min-w-0 flex-1">
                                  {!!sortedSectionHeadersDraft.length && startsGroup && (
                                    <div className="mb-2 text-sm font-semibold text-cyan-200">
                                      {sectionHeaderDraft?.name || 'No section header'}
                                    </div>
                                  )}
                                  <div className="grid min-w-0 gap-2 xl:grid-cols-[minmax(0,1fr)_180px]">
                                    <input
                                      className="min-w-0 w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-base text-white"
                                      value={section.name || ''}
                                      onChange={(e) => updateSection(key, { name: e.target.value })}
                                      placeholder="Section name"
                                    />
                                    <select
                                      className="min-w-0 w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
                                      value={section.headerKey || ''}
                                      onChange={(e) => updateSection(key, { headerKey: e.target.value })}
                                    >
                                      <option value="">No section header</option>
                                      {sortedSectionHeadersDraft.map((sectionHeader) => {
                                        const sectionHeaderKey = getSectionHeaderDraftKey(sectionHeader);
                                        return (
                                          <option key={sectionHeaderKey} value={sectionHeaderKey}>
                                            {sectionHeader.name || 'Untitled header'}
                                            {!sectionHeader.enabled ? ' (disabled)' : ''}
                                          </option>
                                        );
                                      })}
                                    </select>
                                  </div>
                                </div>
                                <div className="flex shrink-0 flex-col gap-2">
                                  <button
                                    className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => moveSection(key, -1)}
                                    disabled={!sameHeading(previousSection)}
                                  >
                                    Up
                                  </button>
                                  <button
                                    className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                    onClick={() => moveSection(key, 1)}
                                    disabled={!sameHeading(nextSection)}
                                  >
                                    Down
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isSharedCatalogMode && (
                    <div className="flex flex-col md:flex-row gap-2">
                      <input
                        className="flex-1 px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                        value={sectionName}
                        onChange={(e) => setSectionName(e.target.value)}
                        placeholder="Add a new section (e.g. Specials)"
                      />
                      <button
                        className="px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                        onClick={addSection}
                        disabled={!sectionName.trim()}
                      >
                        Add Section
                      </button>
                    </div>
                  )}

                  {supportsCampaignAdvertising && (
                    <div className="mt-4 rounded border border-gray-800 bg-black/10">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                        onClick={() => setAdvertisingOpen((prev) => !prev)}
                      >
                        <div>
                          <div className="text-sm font-semibold text-white">Add Advertising</div>
                          <div className="text-xs text-gray-400">
                            Choose saved campaigns and place them before section headings.
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {advertisingOpen ? 'Hide' : 'Show'}
                        </div>
                      </button>

                      {advertisingOpen && (
                        <div className="border-t border-gray-800 px-4 py-4">
                          {!sortedSectionsDraft.length && (
                            <div className="mb-3 text-sm text-gray-400">
                              Add at least one section before placing advertising.
                            </div>
                          )}

                          <label className="mb-4 flex items-center gap-2 text-sm text-gray-200">
                            <input
                              type="checkbox"
                              checked={campaignAdsDynamicMode}
                              onChange={(e) => setCampaignAdsDynamicMode(e.target.checked)}
                            />
                            Dynamic Ad
                          </label>

                          <div className="mb-4 rounded-2xl border border-gray-800 bg-gray-950/60 px-4 py-3">
                            <label className="flex items-start gap-3 text-sm text-gray-200">
                              <input
                                type="checkbox"
                                className="mt-0.5 accent-indigo-500"
                                checked={campaignSectionKickerEnabled}
                                onChange={(e) => setCampaignSectionKickerEnabled(e.target.checked)}
                              />
                              <span className="block">
                                <span className="block font-semibold text-white">Show label above campaign</span>
                                <span className="mt-1 block text-xs text-gray-400">
                                  Use this for wording like Advertisement, Offer, Voucher, or hide it entirely.
                                </span>
                              </span>
                            </label>

                            <label className="mt-3 block">
                              <div className="mb-1 text-xs uppercase tracking-wide text-gray-400">
                                Label Text
                              </div>
                              <input
                                className="w-full rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white disabled:text-gray-500"
                                value={campaignSectionKickerLabel}
                                onChange={(e) => setCampaignSectionKickerLabel(e.target.value.slice(0, 80))}
                                placeholder={DEFAULT_CATALOG_CAMPAIGN_SECTION_KICKER_LABEL}
                                disabled={!campaignSectionKickerEnabled}
                              />
                            </label>

                            <div className="mt-2 text-xs text-gray-400">
                              {campaignSectionKickerEnabled
                                ? `New campaign sections will show “${normalizeCampaignSectionKickerLabel(campaignSectionKickerLabel)}”.`
                                : 'New campaign sections will hide this label.'}
                            </div>
                          </div>

                          <div className="mb-4 flex flex-wrap gap-3">
                            <input
                              className="flex-1 min-w-[220px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                              placeholder="Search campaigns"
                              value={campaignAdsFilter.q}
                              onChange={(e) => setCampaignAdsFilter({ q: e.target.value })}
                            />
                            <button
                              className="px-3 py-2 rounded bg-gray-800 text-white text-sm disabled:bg-gray-900 disabled:text-gray-500"
                              onClick={loadCampaignAds}
                              disabled={campaignAdsLoading}
                            >
                              {campaignAdsLoading ? 'Loading…' : 'Refresh'}
                            </button>
                            <button
                              className="px-3 py-2 rounded bg-indigo-600 text-white text-sm disabled:bg-indigo-900 disabled:text-gray-400"
                              onClick={addCampaignSection}
                              disabled={!sortedSectionsDraft.length}
                            >
                              {campaignAdsDynamicMode ? 'Add Dynamic Ad Section' : 'Add Ad Section'}
                            </button>
                          </div>

                          {campaignAdsError && (
                            <div className="mb-3 text-sm text-red-300">{campaignAdsError}</div>
                          )}

                          {campaignAdsDynamicMode ? (
                            <div className="mb-3 text-xs text-gray-400">
                              Select multiple campaigns to rotate them round robin on the landing page.
                            </div>
                          ) : (
                            <div className="mb-3 text-xs text-gray-400">
                              Select one saved campaign to insert it as an advertising section.
                            </div>
                          )}

                          {!campaignAdsLoading && filteredCampaignAds.length === 0 && (
                            <div className="mb-3 text-sm text-gray-400">
                              {campaignAds.length === 0
                                ? 'No campaign ads found in this account yet.'
                                : 'No campaign ads match this search.'}
                            </div>
                          )}

                          <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
                            {filteredCampaignAds.map((campaignAd) => {
                              const isSelected = campaignAdsDynamicMode
                                ? selectedCampaignAdIds.some((campaignId) => String(campaignId) === String(campaignAd.campaignId))
                                : String(selectedCampaignAdId || '') === String(campaignAd.campaignId);
                              return (
                                <div
                                  key={campaignAd.campaignId}
                                  role="button"
                                  tabIndex={0}
                                  className={`rounded border px-3 py-3 ${
                                    isSelected
                                      ? 'border-indigo-500 bg-indigo-950/30'
                                      : 'border-gray-800 bg-gray-950/60 hover:bg-gray-900'
                                  }`}
                                  onClick={() => toggleCampaignAdSelection(campaignAd.campaignId)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      toggleCampaignAdSelection(campaignAd.campaignId);
                                    }
                                  }}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="h-16 w-16 flex-none overflow-hidden rounded border border-gray-800 bg-gray-900">
                                      {campaignAd.previewImageUrl ? (
                                        <img
                                          src={campaignAd.previewImageUrl}
                                          alt={campaignAd.title || 'Campaign preview'}
                                          className="h-full w-full object-cover"
                                          loading="lazy"
                                        />
                                      ) : (
                                        <div className="flex h-full items-center justify-center px-2 text-center text-[10px] text-gray-500">
                                          No art
                                        </div>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-sm font-semibold text-white">
                                        {campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                                      </div>
                                      <div className="truncate text-xs text-gray-400">
                                        {campaignAd.siteName || 'Unknown site'}
                                      </div>
                                      <div className="mt-1 text-xs text-gray-500">
                                        {campaignAd.landingKey ? `/lp/c/${campaignAd.landingKey}` : 'No landing page'}
                                      </div>
                                    </div>
                                    <label className="flex items-center gap-2 text-xs text-indigo-300" onClick={(e) => e.stopPropagation()}>
                                      <input type="checkbox" checked={isSelected} readOnly />
                                      {isSelected ? 'Selected' : 'Select'}
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div className="mt-4 rounded border border-gray-800 bg-gray-950/60 p-3">
                            <div className="text-sm font-semibold text-white">Selection Preview</div>
                            <div className="mt-1 text-xs text-gray-400">
                              {campaignAdsDynamicMode
                                ? `${selectedCampaignAds.length} campaign${selectedCampaignAds.length === 1 ? '' : 's'} selected.`
                                : (selectedCampaignAd ? `Selected: ${selectedCampaignAd.title || `Campaign #${selectedCampaignAd.campaignId}`}` : 'No campaign selected.')}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 overflow-hidden rounded-2xl border border-cyan-900/70 bg-gradient-to-br from-cyan-950/35 via-sky-950/10 to-emerald-950/15">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                      onClick={() => setSocialsOpen((prev) => !prev)}
                    >
                      <div>
                        <div className="text-sm font-semibold text-white">Add Socials</div>
                        <div className="text-xs text-cyan-100/70">
                          Pull in the Google Maps, email, and social links already filled out in Site Details.
                        </div>
                      </div>
                      <div className="text-right text-xs text-cyan-100/80">
                        <div>{selectedCatalogSocialEntries.length} section / {selectedCatalogHeaderSocialEntries.length} header</div>
                        <div>{socialsOpen ? 'Hide' : 'Show'}</div>
                      </div>
                    </button>

                    {socialsOpen && (
                      <div className="border-t border-cyan-900/60 px-4 py-4">
                        <label className="mb-4 flex items-start gap-3 rounded-2xl border border-cyan-900/70 bg-black/20 px-4 py-3 text-sm text-cyan-50">
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-cyan-400"
                            checked={Boolean(socialSectionDraft.enabled)}
                            onChange={(e) => updateSocialSectionDraft((current) => ({
                              ...current,
                              enabled: e.target.checked,
                              sortOrder: current.sortOrder || Math.max(1, sortedSectionsDraft.length + 1),
                            }))}
                          />
                          <span>
                            <span className="block font-semibold text-white">Enable socials section</span>
                            <span className="mt-1 block text-xs text-cyan-100/70">
                              When enabled, this section starts at the bottom of the landing page and can be moved in the section list above.
                            </span>
                          </span>
                        </label>

                        <div className="mb-4 rounded-2xl border border-cyan-900/70 bg-cyan-950/20 px-4 py-3 text-xs text-cyan-50/85">
                          <div className="font-semibold text-white">What happens on this page</div>
                          <div className="mt-1">
                            This is now the source of truth for the socials section shown on public {catalogConfig.shortName.toLowerCase()} landing pages. Choose the links here, write the source title and intro copy here, and add any translated socials copy here.
                          </div>
                          <div className="mt-2">
                            Social links still come from <span className="font-semibold text-cyan-50">Settings &gt; Site Details</span>. Manage Translations still controls which landing-page languages are enabled overall, but the socials wording itself is edited here.
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded bg-cyan-500/20 px-3 py-1 text-[11px] font-semibold text-cyan-100 hover:bg-cyan-500/30 disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => {
                                if (canManageTranslations) setView('translations');
                              }}
                              disabled={!canManageTranslations}
                            >
                              Open Manage Translations
                            </button>
                          </div>
                        </div>

                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                          <label className="block">
                            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                              Section Title
                            </span>
                            <input
                              className="w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                              value={socialSectionDraft.title || ''}
                              onChange={(e) => updateSocialSectionDraft((current) => ({
                                ...current,
                                title: e.target.value.slice(0, 120),
                              }))}
                              placeholder={DEFAULT_CATALOG_SOCIAL_SECTION_TITLE}
                            />
                          </label>
                          <label className="block md:col-span-2">
                            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                              Intro Text
                            </span>
                            <textarea
                              className="min-h-[96px] w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                              value={socialSectionDraft.description || ''}
                              onChange={(e) => updateSocialSectionDraft((current) => ({
                                ...current,
                                description: e.target.value.slice(0, 240),
                              }))}
                              placeholder={DEFAULT_CATALOG_SOCIAL_SECTION_DESCRIPTION}
                            />
                          </label>
                        </div>

                        <div className="mb-4 rounded-2xl border border-cyan-900/70 bg-black/20 px-4 py-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="text-sm font-semibold text-white">Socials Translation Copy</div>
                              <div className="mt-1 text-xs text-cyan-100/70">
                                Use this area for the menu, products, or services landing-page socials block. Website Builder socials settings only affect the full customer website templates.
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <button
                                type="button"
                                className="rounded bg-cyan-600 px-3 py-2 text-xs font-semibold text-white disabled:bg-cyan-900/60 disabled:text-cyan-100/40"
                                disabled={!socialTranslationRows.length || !catalogSocialMissingTranslationLanguages.length || socialTranslationState.translating}
                                onClick={translateMissingCatalogSocials}
                              >
                                {socialTranslationState.translating ? 'Translating…' : `Translate Missing Socials${catalogSocialMissingTranslationLanguages.length ? ` (${catalogSocialMissingTranslationLanguages.length})` : ''}`}
                              </button>
                              <div className="rounded-full border border-cyan-900/70 bg-black/20 px-3 py-1 text-xs text-cyan-100/75">
                                {socialTranslationRows.length} translation row{socialTranslationRows.length === 1 ? '' : 's'}
                              </div>
                            </div>
                          </div>
                          {socialTranslationState.error ? (
                            <div className="mt-4 rounded-2xl border border-red-800/70 bg-red-950/20 px-4 py-3 text-sm text-red-100">
                              {socialTranslationState.error}
                            </div>
                          ) : null}
                          <div className="mt-3 text-xs text-cyan-100/65">
                            Add or remove rows here, then click <span className="font-semibold text-cyan-50">Save Settings</span> to persist those changes.
                          </div>

                          {catalogSocialTranslationsReviewNeeded ? (
                            <div className="mt-4 rounded-2xl border border-amber-800/70 bg-amber-950/20 px-4 py-3 text-sm text-amber-100">
                              <div className="font-semibold text-amber-50">Translation review needed</div>
                              <div className="mt-1 text-xs text-amber-100/80">
                                {catalogSocialTranslationsReviewReasons.join(' • ')}. Save settings, then review each socials translation row so the menu-facing copy stays aligned.
                              </div>
                            </div>
                          ) : null}

                          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
                            <div className="space-y-4">
                              <label className="block">
                                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                  Source Language
                                </span>
                                <select
                                  className="w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                                  value={normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en')}
                                  onChange={(e) => handleCatalogSocialSourceLanguageChange(e.target.value)}
                                >
                                  {catalogSocialTranslationLanguageOptions.length ? (
                                    [
                                      {
                                        code: normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en'),
                                        label: getCatalogLanguageLabel(socialSectionDraft.sourceLanguageCode || 'en'),
                                      },
                                      ...catalogSocialTranslationLanguageOptions,
                                    ].filter((option, index, array) => (
                                      array.findIndex((candidate) => sameCatalogLanguageCode(candidate.code, option.code)) === index
                                    )).map((option) => (
                                      <option key={`catalog-social-source-${option.code}`} value={option.code}>{option.label}</option>
                                    ))
                                  ) : (
                                    <option value={normalizeCatalogLanguageCode(socialSectionDraft.sourceLanguageCode, 'en')}>
                                      {getCatalogLanguageLabel(socialSectionDraft.sourceLanguageCode || 'en')}
                                    </option>
                                  )}
                                </select>
                              </label>

                              <div>
                                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                  Suggested Languages
                                </div>
                                {suggestedCatalogSocialTranslationLanguages.length ? (
                                  <div className="flex flex-wrap gap-2">
                                    {suggestedCatalogSocialTranslationLanguages.map((languageCode) => {
                                      const isAdded = socialTranslationRows.some((row) => sameCatalogLanguageCode(row.languageCode, languageCode));
                                      return (
                                        <button
                                          key={`catalog-social-suggested-${languageCode}`}
                                          type="button"
                                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                                            isAdded
                                              ? 'border-cyan-900/70 bg-black/20 text-cyan-100/45'
                                              : 'border-cyan-700/70 bg-cyan-950/30 text-cyan-50 hover:bg-cyan-950/45'
                                          }`}
                                          disabled={isAdded}
                                          onClick={() => addCatalogSocialTranslation(languageCode, { copySource: true })}
                                        >
                                          {isAdded ? `Added: ${getCatalogLanguageLabel(languageCode)}` : `Add ${getCatalogLanguageLabel(languageCode)}`}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="text-xs text-cyan-100/60">
                                    Enable more languages in Manage Translations to get one-click socials translation rows here.
                                  </div>
                                )}
                              </div>

                              <div>
                                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                  Add Language
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <select
                                    className="min-w-[180px] rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                                    value={manualSocialTranslationLanguage}
                                    onChange={(e) => setManualSocialTranslationLanguage(e.target.value)}
                                  >
                                    {catalogSocialTranslationLanguageOptions.length ? (
                                      catalogSocialTranslationLanguageOptions.map((option) => (
                                        <option key={`catalog-social-option-${option.code}`} value={option.code}>{option.label}</option>
                                      ))
                                    ) : (
                                      <option value="">No languages loaded</option>
                                    )}
                                  </select>
                                  <button
                                    type="button"
                                    className="rounded bg-cyan-600 px-3 py-2 text-sm font-semibold text-white disabled:bg-cyan-900/60 disabled:text-cyan-100/40"
                                    disabled={!manualSocialTranslationLanguage || socialTranslationRows.some((row) => sameCatalogLanguageCode(row.languageCode, manualSocialTranslationLanguage))}
                                    onClick={() => addCatalogSocialTranslation(manualSocialTranslationLanguage, { copySource: true })}
                                  >
                                    Add Translation
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="rounded-2xl border border-cyan-900/70 bg-black/20 p-4">
                              {!socialTranslationRows.length ? (
                                <div className="text-sm text-cyan-100/70">
                                  No socials translation rows yet. Add one of the enabled landing languages to localize this section for the public {catalogConfig.shortName.toLowerCase()} page.
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  <div className="flex flex-wrap gap-2">
                                    {socialTranslationRows.map((row) => {
                                      const active = sameCatalogLanguageCode(row.languageCode, activeSocialTranslationLanguage);
                                      return (
                                        <button
                                          key={`catalog-social-row-${row.languageCode}`}
                                          type="button"
                                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                                            active
                                              ? 'border-cyan-400/70 bg-cyan-950/40 text-white'
                                              : 'border-cyan-900/70 bg-black/20 text-cyan-100/75 hover:bg-cyan-950/25'
                                          }`}
                                          onClick={() => setActiveSocialTranslationLanguage(row.languageCode)}
                                        >
                                          {getCatalogLanguageLabel(row.languageCode)}
                                        </button>
                                      );
                                    })}
                                  </div>

                                  {selectedCatalogSocialTranslation ? (
                                    <div className="space-y-3">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="text-sm font-semibold text-white">
                                          Editing {getCatalogLanguageLabel(selectedCatalogSocialTranslation.languageCode)}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          <button
                                            type="button"
                                            className="rounded bg-cyan-500/20 px-3 py-1 text-xs font-semibold text-cyan-100 hover:bg-cyan-500/30"
                                            onClick={() => copyCatalogSocialSourceToTranslation(selectedCatalogSocialTranslation.languageCode)}
                                          >
                                            Copy Source
                                          </button>
                                          <button
                                            type="button"
                                            className="rounded bg-red-500/20 px-3 py-1 text-xs font-semibold text-red-100 hover:bg-red-500/30"
                                            onClick={() => removeCatalogSocialTranslation(selectedCatalogSocialTranslation.languageCode)}
                                          >
                                            Remove Row
                                          </button>
                                        </div>
                                      </div>

                                      <label className="block">
                                        <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                          Translated Title
                                        </span>
                                        <input
                                          className="w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                                          value={selectedCatalogSocialTranslation.title || ''}
                                          onChange={(e) => updateCatalogSocialTranslation(selectedCatalogSocialTranslation.languageCode, {
                                            title: e.target.value.slice(0, 120),
                                          })}
                                          placeholder={socialSectionDraft.title || DEFAULT_CATALOG_SOCIAL_SECTION_TITLE}
                                        />
                                      </label>

                                      <label className="block">
                                        <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                          Translated Intro Text
                                        </span>
                                        <textarea
                                          className="min-h-[96px] w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                                          value={selectedCatalogSocialTranslation.description || ''}
                                          onChange={(e) => updateCatalogSocialTranslation(selectedCatalogSocialTranslation.languageCode, {
                                            description: e.target.value.slice(0, 240),
                                          })}
                                          placeholder={socialSectionDraft.description || DEFAULT_CATALOG_SOCIAL_SECTION_DESCRIPTION}
                                        />
                                      </label>

                                      {selectedCatalogSocialSectionEntries.length ? (
                                        <div>
                                          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                            Translated Link Titles
                                          </div>
                                          <div className="grid grid-cols-1 gap-3">
                                            {selectedCatalogSocialSectionEntries.map((entry) => (
                                              <label
                                                key={`catalog-social-translation-title-${selectedCatalogSocialTranslation.languageCode}-${entry.key}`}
                                                className="block rounded-2xl border border-cyan-900/70 bg-black/20 px-4 py-3"
                                              >
                                                <span className="mb-2 block text-sm font-semibold text-white">
                                                  {entry.label}
                                                </span>
                                                <input
                                                  className="w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                                                  value={selectedCatalogSocialTranslation.ctaLabels?.[entry.key] ?? ''}
                                                  onChange={(e) => updateCatalogSocialTranslation(selectedCatalogSocialTranslation.languageCode, (currentRow) => ({
                                                    ctaLabels: {
                                                      ...(currentRow.ctaLabels || {}),
                                                      [entry.key]: e.target.value.slice(0, 120),
                                                    },
                                                  }))}
                                                  placeholder={socialSectionDraft.ctaLabels?.[entry.key] || getCatalogSocialDefaultCtaLabel(entry.key, entry.ctaLabel)}
                                                />
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-xs text-cyan-100/60">
                                          Select at least one socials link above to add translated link titles here.
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <label className="mb-4 flex items-start gap-3 rounded-2xl border border-cyan-900/70 bg-black/20 px-4 py-3 text-sm text-cyan-50">
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-cyan-400"
                            checked={Boolean(socialSectionDraft.showInHeader)}
                            onChange={(e) => updateSocialSectionDraft((current) => ({
                              ...current,
                              showInHeader: e.target.checked,
                              headerPosition: normalizeCatalogSocialHeaderPosition(current.headerPosition),
                            }))}
                          />
                          <span>
                            <span className="block font-semibold text-white">Enable Socials in Header</span>
                            <span className="mt-1 block text-xs text-cyan-100/70">
                              Adds small square social icons next to the language control in the top header, using the links selected in the header picker below.
                            </span>
                          </span>
                        </label>

                        {socialSectionDraft.showInHeader && (
                          <label className="mb-4 block">
                            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                              Header Positioning
                            </span>
                            <select
                              className="w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                              value={normalizeCatalogSocialHeaderPosition(socialSectionDraft.headerPosition)}
                              onChange={(e) => updateSocialSectionDraft((current) => ({
                                ...current,
                                headerPosition: normalizeCatalogSocialHeaderPosition(e.target.value),
                              }))}
                            >
                              {CATALOG_SOCIAL_HEADER_POSITION_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <span className="mt-2 block text-xs text-cyan-100/70">
                              {CATALOG_SOCIAL_HEADER_POSITION_OPTIONS.find(
                                (option) => option.value === normalizeCatalogSocialHeaderPosition(socialSectionDraft.headerPosition)
                              )?.description || ''}
                            </span>
                          </label>
                        )}

                        {missingSelectedCatalogSocialCount > 0 && (
                          <div className="mb-4 rounded-2xl border border-amber-800/70 bg-amber-950/20 px-4 py-3 text-xs text-amber-100">
                            {missingSelectedCatalogSocialCount} saved link{missingSelectedCatalogSocialCount === 1 ? '' : 's'} {missingSelectedCatalogSocialCount === 1 ? 'is' : 'are'} no longer filled out in Site Details, so {missingSelectedCatalogSocialCount === 1 ? 'it will not' : 'they will not'} appear until {missingSelectedCatalogSocialCount === 1 ? 'that URL is added back' : 'those URLs are added back'}.
                          </div>
                        )}

                        {socialSectionDraft.showInHeader && missingSelectedCatalogHeaderSocialCount > 0 && (
                          <div className="mb-4 rounded-2xl border border-amber-800/70 bg-amber-950/20 px-4 py-3 text-xs text-amber-100">
                            {missingSelectedCatalogHeaderSocialCount} saved header link{missingSelectedCatalogHeaderSocialCount === 1 ? '' : 's'} {missingSelectedCatalogHeaderSocialCount === 1 ? 'is' : 'are'} no longer filled out in Site Details, so {missingSelectedCatalogHeaderSocialCount === 1 ? 'it will not' : 'they will not'} appear until {missingSelectedCatalogHeaderSocialCount === 1 ? 'that URL is added back' : 'those URLs are added back'}.
                          </div>
                        )}

                        {!availableCatalogSocialEntries.length ? (
                          <div className="rounded-2xl border border-dashed border-cyan-900/70 bg-black/10 px-4 py-6 text-sm text-cyan-100/70">
                            No Google Maps, email, or social links are filled out for this site yet.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                Socials Section Links
                              </div>
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                {availableCatalogSocialSectionEntries.map((entry) => {
                                  const checked = socialSectionDraft.linkKeys.includes(entry.key);
                                  return (
                                    <label
                                      key={`catalog-social-entry-${entry.key}`}
                                      className={`group flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                                        checked
                                          ? 'border-cyan-400/60 bg-cyan-950/30 shadow-[0_18px_40px_-28px_rgba(34,211,238,0.85)]'
                                          : 'border-cyan-900/60 bg-black/10 hover:border-cyan-700/70 hover:bg-cyan-950/20'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-1 accent-cyan-400"
                                        checked={checked}
                                        onChange={() => toggleCatalogSocialLink(entry.key)}
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className={`inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br text-[11px] font-semibold text-white shadow-inner ${entry.accentClass}`}>
                                            {entry.iconLabel}
                                          </span>
                                          <div className="min-w-0">
                                            <div className="text-sm font-semibold text-white">{entry.ctaLabel}</div>
                                            <div className="truncate text-xs text-cyan-100/70">
                                              {entry.hostLabel || entry.label}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>

                            {selectedCatalogSocialSectionEntries.length ? (
                              <div>
                                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                  Section Link Titles
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                  {selectedCatalogSocialSectionEntries.map((entry) => (
                                    <label
                                      key={`catalog-social-title-${entry.key}`}
                                      className="block rounded-2xl border border-cyan-900/70 bg-black/20 px-4 py-3"
                                    >
                                      <span className="mb-2 block text-sm font-semibold text-white">
                                        {entry.label}
                                      </span>
                                      <input
                                        className="w-full rounded-2xl border border-cyan-900/70 bg-black/30 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
                                        value={socialSectionDraft.ctaLabels?.[entry.key] ?? entry.ctaLabel}
                                        onChange={(e) => updateSocialSectionDraft((current) => ({
                                          ...current,
                                          ctaLabels: {
                                            ...(current.ctaLabels || {}),
                                            [entry.key]: e.target.value.slice(0, 120),
                                          },
                                        }))}
                                        placeholder={getCatalogSocialDefaultCtaLabel(entry.key, entry.ctaLabel)}
                                      />
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ) : null}

                            {socialSectionDraft.showInHeader && (
                              <div>
                                <div className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100/70">
                                  Header Icon Links
                                </div>
                                <div className="mb-3 rounded-2xl border border-cyan-900/70 bg-black/20 px-4 py-3 text-xs text-cyan-100/70">
                                  Choose the icons that should appear in the top header. These do not need to match the socials section selection.
                                </div>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                  {availableCatalogSocialEntries.map((entry) => {
                                    const checked = socialSectionDraft.headerLinkKeys.includes(entry.key);
                                    return (
                                      <label
                                        key={`catalog-header-social-entry-${entry.key}`}
                                        className={`group flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                                          checked
                                            ? 'border-sky-400/60 bg-sky-950/30 shadow-[0_18px_40px_-28px_rgba(56,189,248,0.85)]'
                                            : 'border-cyan-900/60 bg-black/10 hover:border-sky-700/70 hover:bg-sky-950/20'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          className="mt-1 accent-sky-400"
                                          checked={checked}
                                          onChange={() => toggleCatalogHeaderSocialLink(entry.key)}
                                        />
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className={`inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br text-[11px] font-semibold text-white shadow-inner ${entry.accentClass}`}>
                                              {entry.iconLabel}
                                            </span>
                                            <div className="min-w-0">
                                              <div className="text-sm font-semibold text-white">{entry.ctaLabel}</div>
                                              <div className="truncate text-xs text-cyan-100/70">
                                                {entry.hostLabel || entry.label}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-4 rounded-2xl border border-cyan-900/70 bg-black/20 px-4 py-3">
                          <div className="text-sm font-semibold text-white">Selection Preview</div>
                          <div className="mt-1 text-xs text-cyan-100/70">
                            {selectedCatalogSocialEntries.length || selectedCatalogHeaderSocialEntries.length
                              ? `${selectedCatalogSocialEntries.length} link${selectedCatalogSocialEntries.length === 1 ? '' : 's'} ${socialSectionDraft.enabled ? 'will be shown in the socials section' : 'are ready for the socials section'} and ${selectedCatalogHeaderSocialEntries.length} icon${selectedCatalogHeaderSocialEntries.length === 1 ? '' : 's'} ${socialSectionDraft.showInHeader ? 'will appear in the header' : 'are ready for the header'}.`
                              : 'No socials selected yet.'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                </SettingsDisclosurePanel>

              </div>
              <div className="space-y-4">
                <SelectedAssetPreview asset={selectedLogoAsset} />

                <div className="bg-gray-950 rounded p-4 border border-gray-800">
                  <div className="text-lg font-semibold mb-2">Landing Style Summary</div>
                  <div className="space-y-2 text-sm text-gray-300">
                    <div>
                      <span className="text-gray-500">Logo:</span>{' '}
                      {CATALOG_LOGO_SIZE_OPTIONS.find((option) => option.value === displaySettingsDraft?.logoSize)?.label || 'Normal'}
                    </div>
                    <div>
                      <span className="text-gray-500">Title:</span>{' '}
                      {String(displaySettingsDraft?.headerTitle || '').trim() || <span className="text-gray-500">Hidden</span>}
                    </div>
                    <div>
                      <span className="text-gray-500">Subtitle:</span>{' '}
                      {String(displaySettingsDraft?.headerSubtitle || '').trim() || <span className="text-gray-500">Hidden</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Page Background:</span>
                      <span
                        className="inline-block h-4 w-4 rounded-full border border-white/10"
                        style={{ backgroundColor: normalizeHexColor(displaySettingsDraft?.backgroundColor, DEFAULT_CATALOG_BACKGROUND_COLOR) }}
                      />
                      <span>{normalizeHexColor(displaySettingsDraft?.backgroundColor, DEFAULT_CATALOG_BACKGROUND_COLOR)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Primary Panel:</span>
                      <span
                        className="inline-block h-4 w-4 rounded-full border border-white/10"
                        style={{ backgroundColor: normalizeHexColor(displaySettingsDraft?.surfaceColor, DEFAULT_CATALOG_SURFACE_COLOR) }}
                      />
                      <span>{normalizeHexColor(displaySettingsDraft?.surfaceColor, DEFAULT_CATALOG_SURFACE_COLOR)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">2nd Panel:</span>
                      <span
                        className="inline-block h-4 w-4 rounded-full border border-white/10"
                        style={{ backgroundColor: normalizeHexColor(displaySettingsDraft?.secondarySurfaceColor, DEFAULT_CATALOG_SECONDARY_SURFACE_COLOR) }}
                      />
                      <span>{normalizeHexColor(displaySettingsDraft?.secondarySurfaceColor, DEFAULT_CATALOG_SECONDARY_SURFACE_COLOR)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Inner Card:</span>
                      <span
                        className="inline-block h-4 w-4 rounded-full border border-white/10"
                        style={{ backgroundColor: normalizeHexColor(displaySettingsDraft?.tertiarySurfaceColor, DEFAULT_CATALOG_TERTIARY_SURFACE_COLOR) }}
                      />
                      <span>{normalizeHexColor(displaySettingsDraft?.tertiarySurfaceColor, DEFAULT_CATALOG_TERTIARY_SURFACE_COLOR)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Heading Text:</span>
                      <span
                        className="inline-block h-4 w-4 rounded-full border border-white/10"
                        style={{ backgroundColor: normalizeHexColor(displaySettingsDraft?.headingColor, DEFAULT_CATALOG_HEADING_COLOR) }}
                      />
                      <span>{normalizeHexColor(displaySettingsDraft?.headingColor, DEFAULT_CATALOG_HEADING_COLOR)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Body Text:</span>
                      <span
                        className="inline-block h-4 w-4 rounded-full border border-white/10"
                        style={{ backgroundColor: normalizeHexColor(displaySettingsDraft?.textColor, DEFAULT_CATALOG_TEXT_COLOR) }}
                      />
                      <span>{normalizeHexColor(displaySettingsDraft?.textColor, DEFAULT_CATALOG_TEXT_COLOR)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Photos:</span>{' '}
                      {CATALOG_PHOTO_DISPLAY_OPTIONS.find((option) => option.value === displaySettingsDraft?.photoDisplay)?.label || 'Fill Frame'}
                    </div>
                    <div>
                      <span className="text-gray-500">Socials:</span>{' '}
                      {(socialSectionDraft.enabled && selectedCatalogSocialEntries.length) || (socialSectionDraft.showInHeader && selectedCatalogHeaderSocialEntries.length)
                        ? `${selectedCatalogSocialEntries.length} section / ${selectedCatalogHeaderSocialEntries.length} header`
                        : <span className="text-gray-500">Disabled</span>}
                    </div>
                    <div>
                      <span className="text-gray-500">Section Headers:</span>{' '}
                      {sortedSectionHeadersDraft.length
                        ? `${sortedSectionHeadersDraft.length} configured`
                        : <span className="text-gray-500">Not used</span>}
                    </div>
                    {isMenuCatalog && (
                      <div>
                        <span className="text-gray-500">Tag Icons:</span>{' '}
                        {menuFeaturesDraft.showItemTags
                          ? (MENU_TAG_ICON_SET_OPTIONS.find((option) => option.key === menuFeaturesDraft.tagIconSet)?.label || 'Emoji')
                          : <span className="text-gray-500">Disabled</span>}
                      </div>
                    )}
                    {isMenuCatalog && (
                      <div>
                        <span className="text-gray-500">Visible Filters:</span>{' '}
                        {menuFeaturesDraft.enabledFilterKeys.length
                          ? `${menuFeaturesDraft.enabledFilterKeys.length} selected`
                          : <span className="text-gray-500">Hidden</span>}
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Photo Rotation:</span>{' '}
                      {menuFeaturesDraft.autoRotatePhotos ? 'Auto' : 'Manual Only'}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="text-lg font-semibold mb-2">QR Codes</div>
                <div className="text-sm text-gray-400 mb-3">{catalogConfig.shortName} + {catalogConfig.unitPlural.toLowerCase()} QR codes (optional jump params).</div>

                  <button
                    className="w-full px-3 py-2 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                    onClick={() => setQrPreview({ open: true, title: `${catalogConfig.shortName} QR`, url: menuQrUrl })}
                    disabled={!menu?.landingKey}
                    title={menu?.landingKey ? '' : 'Save Settings first to create the landing key.'}
                  >
                    Preview {catalogConfig.shortName} QR
                  </button>

                  <button
                    type="button"
                    className="mt-2 w-full px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-gray-800 disabled:text-gray-500"
                    onClick={handleDownloadCatalogQr}
                    disabled={!menu?.landingKey || downloadingCatalogQr}
                    title={menu?.landingKey ? '' : 'Save Settings first to create the landing key.'}
                  >
                    {downloadingCatalogQr ? 'Downloading…' : 'Download QR'}
                  </button>

                  <div className="mt-4 border-t border-gray-800 pt-4">
                    <div className="text-sm text-gray-300 font-semibold mb-2">{catalogConfig.unitLabel} QR (optional params)</div>
                    <label className="block mb-2">
                      <div className="text-xs text-gray-300 mb-1">{catalogConfig.unitLabel} Number</div>
                      <input
                        className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                        value={tableNumber}
                        onChange={(e) => setTableNumber(e.target.value)}
                        placeholder="e.g. 12"
                      />
                    </label>

                    <label className="block mb-2">
                      <div className="text-xs text-gray-300 mb-1">{catalogConfig.unitLabel} Label (optional)</div>
                      <input
                        className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                        value={tableLabel}
                        onChange={(e) => setTableLabel(e.target.value)}
                        placeholder="e.g. By the front entrance"
                      />
                    </label>

                    <label className="block mb-2">
                      <div className="text-xs text-gray-300 mb-1">Jump to Section (optional)</div>
                      <select
                        className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                        value={tableJumpSectionId}
                        onChange={(e) => setTableJumpSectionId(e.target.value)}
                      >
                        <option value="">None</option>
                        {enabledSections.map((section) => (
                          <option key={section.id} value={section.id}>{section.name}</option>
                        ))}
                      </select>
                    </label>

                    <label className="block mb-3">
                      <div className="text-xs text-gray-300 mb-1">Jump to Item (optional)</div>
                      <select
                        className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                        value={tableJumpItemId}
                        onChange={(e) => setTableJumpItemId(e.target.value)}
                        disabled={!items.length}
                      >
                        <option value="">None</option>
                        {items.map((item) => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                    </label>

                    <button
                      className="w-full px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                      onClick={() => setQrPreview({ open: true, title: `${catalogConfig.unitLabel} QR`, url: tableQrUrl })}
                      disabled={!menu?.landingKey}
                    >
                      Preview {catalogConfig.unitLabel} QR
                    </button>
                  </div>

                  <div className="mt-4 border-t border-gray-800 pt-4">
                    <div className="text-sm text-gray-300 font-semibold mb-2">{catalogConfig.unitPlural} (bulk)</div>
                    <div className="text-xs text-gray-400 mb-3">
                      Set the total number of {catalogConfig.unitPlural.toLowerCase()} and optionally add labels. Print a QR sheet for {catalogConfig.unitPlural.toLowerCase()} 1…N.
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="block">
                        <div className="text-xs text-gray-300 mb-1">Total {catalogConfig.unitPlural}</div>
                        <input
                          className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                          value={tableCountDraft}
                          onChange={(e) => {
                            setTableCountDraft(e.target.value);
                            setSettingsDirty(true);
                          }}
                          placeholder="e.g. 12"
                        />
                      </label>
                      <div className="flex items-end gap-2">
                        <button
                          className="flex-1 px-3 py-2 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                          disabled={!menu?.landingKey || !tableCountValue}
                          title={menu?.landingKey ? '' : 'Save Settings first to create the landing key.'}
                          onClick={() => setBulkQrPreview({ open: true, title: `${catalogConfig.unitLabel} QR Codes`, entries: bulkTableEntries })}
                        >
                          Preview / Print {catalogConfig.unitLabel} QRs
                        </button>
                      </div>
                    </div>

                    {!!tableCountValue && (
                      <div className="mt-3 max-h-[320px] overflow-auto rounded border border-gray-800">
                        <table className="min-w-full text-sm">
                          <thead className="sticky top-0 bg-gray-950">
                            <tr className="text-left text-gray-300">
                              <th className="py-2 px-3 w-[120px]">{catalogConfig.unitLabel}</th>
                              <th className="py-2 px-3">Label (optional)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: tableCountValue }, (_, idx) => {
                              const number = idx + 1;
                              const key = String(number);
                              const value = String(tableLabelsDraft?.[key] || '');
                              return (
                                <tr key={key} className="border-t border-gray-800">
                                  <td className="py-2 px-3 text-gray-200">#{number}</td>
                                  <td className="py-2 px-3">
                                    <input
                                      className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                                      value={value}
                                      onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setTableLabelsDraft((prev) => ({ ...(prev || {}), [key]: nextValue }));
                                        setSettingsDirty(true);
                                      }}
                                      placeholder={`Label for ${catalogConfig.unitLabel.toLowerCase()} ${number}`}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {view === 'items' && (
            <div className="space-y-4">
              <div className="bg-gray-950 rounded p-3 border border-gray-800 text-sm text-gray-200">
                Sections are set up. Add items below.
              </div>

              {enabledLanguageOptions.length > 1 && (
                <div className="bg-gray-950 rounded p-3 border border-gray-800 flex flex-wrap items-center gap-3">
                  <div className="text-sm text-gray-200 font-semibold">Language</div>
                  <select
                    className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                    value={activeLanguage}
                    onChange={(e) => setActiveLanguage(e.target.value)}
                  >
                    {enabledLanguageOptions.map((option) => (
                      <option key={option.code} value={option.code}>{option.label}</option>
                    ))}
                  </select>
                  {isTranslationMode && (
                    <div className="text-xs text-gray-400">
                      Editing translations only. Switch to English to add items, prices, or photos.
                    </div>
                  )}
                </div>
              )}

              {languageData.error && isTranslationMode && (
                <div className="text-sm text-red-300 bg-red-900/20 border border-red-800 rounded p-3">
                  {languageData.error}
                </div>
              )}

              {languageData.loading && isTranslationMode && (
                <div className="text-sm text-gray-300">Loading translations…</div>
              )}

              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-gray-400">
                  {itemLabelPlural} are grouped by section. Each item can also have its own QR code.
                </div>
                <button
                  className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                  onClick={() => setEditor({ open: true, item: null })}
                  disabled={!canAddItems}
                  title={
                    isTranslationMode
                      ? 'Switch to English to add new items.'
                      : !enabledSections.length
                        ? `Add and enable a section in ${catalogConfig.shortName} Settings first.`
                        : ''
                  }
                >
                  Add {itemLabel}
                </button>
              </div>

              {!!enabledSections.length && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-800 bg-gray-950 px-3 py-2">
                  <div className="text-xs text-gray-300">
                    {selectedItemIds.length} selected from {allItemIds.length} {itemLabelPlural.toLowerCase()}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                      onClick={() => setOpenSectionIds(enabledSections.map((section) => String(section.id)))}
                    >
                      Open All
                    </button>
                    <button
                      className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                      onClick={() => setOpenSectionIds([])}
                    >
                      Close All
                    </button>
                    <button
                      className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                      onClick={() => setSelectedItemIds(allItemIds)}
                      disabled={!allItemIds.length || isTranslationMode}
                    >
                      Select All
                    </button>
                    <button
                      className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                      onClick={() => setSelectedItemIds([])}
                      disabled={!selectedItemIds.length}
                    >
                      Clear All
                    </button>
                    <button
                      className="px-2 py-1 bg-indigo-600 text-xs rounded text-white disabled:bg-indigo-900 disabled:text-gray-400"
                      onClick={() => setMoveModal({ open: true, mode: 'items' })}
                      disabled={!selectedItemIds.length || isTranslationMode}
                      title={isTranslationMode ? 'Switch to English to move items.' : ''}
                    >
                      Move To
                    </button>
                    <button
                      className="px-2 py-1 bg-red-700 text-xs rounded text-white disabled:bg-red-900 disabled:text-gray-400"
                      onClick={deleteSelectedItems}
                      disabled={!selectedItemIds.length || isTranslationMode}
                      title={isTranslationMode ? 'Switch to English to delete items.' : ''}
                    >
                      Delete Selected
                    </button>
                  </div>
                </div>
              )}

              {!!settingsDirty && (
                <div className="text-sm text-yellow-200 bg-yellow-900/20 border border-yellow-800 rounded p-3">
                  You have unsaved section changes. Save Settings before editing items.
                </div>
              )}

              {!enabledSections.length && (
                <div className="text-gray-400">Enable at least one section in {catalogConfig.shortName} Settings.</div>
              )}

              {!!enabledSections.length && (
                <div className="space-y-3">
                  {enabledSections.map((section) => {
                    const sectionHeader = getSectionHeaderForSection(section);
                    const sectionHeaderName = getSectionHeaderDisplayName(sectionHeader);
                    const missingSectionHeaderTranslation = Boolean(
                      sectionHeader
                      && isTranslationMode
                      && !sectionHeaderTranslationMap.get(String(sectionHeader.id))?.name
                    );
                    const sectionItems = itemsBySection.get(section.id) || [];
                    const sectionItemIds = sectionItems.map((item) => item.id).filter(Boolean);
                    const selectedInSection = sectionItemIds.filter((id) => selectedItemIds.some((selectedId) => String(selectedId) === String(id)));
                    const sectionOpen = openSectionIds.includes(String(section.id));
                    return (
                      <div key={section.id} className="bg-gray-950 rounded p-4 border border-gray-800">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <button
                            type="button"
                            className="min-w-0 flex flex-1 items-center gap-3 text-left"
                            onClick={() => {
                              setOpenSectionIds((prev) => (
                                prev.includes(String(section.id))
                                  ? prev.filter((id) => id !== String(section.id))
                                  : [...prev, String(section.id)]
                              ));
                            }}
                          >
                            <span className="flex h-7 w-7 flex-none items-center justify-center rounded bg-gray-800 text-sm text-gray-200">
                              {sectionOpen ? '−' : '+'}
                            </span>
                            <span className="min-w-0">
                              {!!sectionHeaderName && (
                                <span className="mb-1 inline-flex max-w-full items-center gap-2 rounded-full border border-cyan-800/70 bg-cyan-950/30 px-2 py-1 text-[11px] font-medium text-cyan-100">
                                  <span className="truncate">{sectionHeaderName}</span>
                                  {missingSectionHeaderTranslation && (
                                    <span className="text-yellow-300">Missing translation</span>
                                  )}
                                </span>
                              )}
                              <span className="block truncate text-lg font-semibold">{getSectionDisplayName(section)}</span>
                              <span className="block text-xs text-gray-400">
                                {sectionItems.length ? `${sectionItems.length} item${sectionItems.length === 1 ? '' : 's'}` : 'No items yet.'}
                                {selectedInSection.length ? ` · ${selectedInSection.length} selected` : ''}
                              </span>
                            </span>
                          </button>
                          <div className="flex flex-wrap items-center gap-2">
                            {!!sectionItems.length && !isTranslationMode && (
                              <>
                                <button
                                  className="px-2 py-1 rounded bg-gray-700 text-xs text-white"
                                  onClick={() => {
                                    setSelectedItemIds((prev) => Array.from(new Set([...(prev || []), ...sectionItemIds])));
                                    setOpenSectionIds((prev) => (prev.includes(String(section.id)) ? prev : [...prev, String(section.id)]));
                                  }}
                                >
                                  Select Section
                                </button>
                                <button
                                  className="px-2 py-1 rounded bg-gray-700 text-xs text-white disabled:bg-gray-800 disabled:text-gray-500"
                                  onClick={() => {
                                    const sectionSet = new Set(sectionItemIds.map((id) => String(id)));
                                    setSelectedItemIds((prev) => (prev || []).filter((id) => !sectionSet.has(String(id))));
                                  }}
                                  disabled={!selectedInSection.length}
                                >
                                  Clear Section
                                </button>
                              </>
                            )}
                            {isTranslationMode && (
                              <>
                                {!!sectionHeader && (
                                  <button
                                    className="px-3 py-1 rounded bg-gray-700 text-white"
                                    onClick={() => setSectionHeaderTranslationEditor({ open: true, sectionHeader })}
                                  >
                                    Edit {activeLanguageLabel} Header
                                  </button>
                                )}
                                <button
                                  className="px-3 py-1 rounded bg-gray-700 text-white"
                                  onClick={() => setSectionTranslationEditor({ open: true, section })}
                                >
                                  Edit {activeLanguageLabel} Name
                                </button>
                              </>
                            )}
                            <button
                              className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                              onClick={() => {
                                setOpenSectionIds((prev) => (prev.includes(String(section.id)) ? prev : [...prev, String(section.id)]));
                                setEditor({ open: true, item: { sectionId: section.id } });
                              }}
                              disabled={isTranslationMode}
                              title={isTranslationMode ? 'Switch to English to add new items.' : ''}
                            >
                              Add
                            </button>
                            {isAdmin && (
                              <button
                                className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                                onClick={() => openWidgetPicker(section.id)}
                                disabled={isTranslationMode}
                                title={isTranslationMode ? 'Switch to English to add widgets.' : ''}
                              >
                                Widget
                              </button>
                            )}
                          </div>
                        </div>

                        {sectionOpen && !sectionItems.length && (
                          <div className="text-sm text-gray-400">Add your first item to this section.</div>
                        )}

                        {sectionOpen && !!sectionItems.length && (
                          <div className="mt-3 overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-300">
                                  <th className="py-2 pr-3 w-16">Order</th>
                                  <th className="py-2 pr-3 w-10">Select</th>
                                  <th className="py-2 pr-3">Item</th>
                                  <th className="py-2 pr-3">Price</th>
                                  <th className="py-2 pr-3">Photos</th>
                                  <th className="py-2 pr-3">Artwork</th>
                                  <th className="py-2 pr-3">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sectionItems.map((item, itemIndex) => {
                                  const isWidget = item.itemType === 'widget';
                                  const widgetInfo = isWidget ? widgetsById.get(String(item.widgetId || '')) : null;
                                  const translation = getItemTranslation(item);
                                  const displayName = isWidget
                                    ? (widgetInfo?.title || item.name || 'Widget')
                                    : isTranslationMode
                                      ? (translation?.name || item.name)
                                      : item.name;
                                  const displaySubtitle = isWidget
                                    ? (
                                      widgetInfo?.partner
                                        ? `${widgetInfo.partner}${widgetInfo.widget_type ? ` · ${widgetInfo.widget_type}` : ''}`
                                        : (item.subtitle || '')
                                    )
                                    : isTranslationMode
                                      ? (translation?.subtitle || item.subtitle)
                                      : item.subtitle;
                                  const missingTranslation = !isWidget && isTranslationMode && !translation?.name;
                                  const selected = selectedItemIds.some((id) => String(id) === String(item.id));
                                  const isMoving = movingItemIds.some((id) => String(id) === String(item.id));
                                  const isDropTarget = itemDropIndicator.sectionId === String(section.id)
                                    && itemDropIndicator.itemId === String(item.id);
                                  return (
                                    <tr
                                      key={item.id}
                                      className={`border-t border-gray-800 ${selected ? 'bg-indigo-950/20' : ''} ${
                                        isDropTarget
                                          ? (itemDropIndicator.position === 'after' ? 'border-b-2 border-b-sky-400' : 'border-t-2 border-t-sky-400')
                                          : ''
                                      }`}
                                      onDragOver={(event) => {
                                        if (
                                          !draggingItemState.itemId
                                          || String(draggingItemState.itemId) === String(item.id)
                                          || String(draggingItemState.sectionId || '') !== String(section.id)
                                        ) {
                                          return;
                                        }
                                        event.preventDefault();
                                        setItemDropIndicator({
                                          sectionId: String(section.id),
                                          itemId: String(item.id),
                                          position: resolveDropPosition(event),
                                        });
                                      }}
                                      onDrop={async (event) => {
                                        if (
                                          !draggingItemState.itemId
                                          || String(draggingItemState.itemId) === String(item.id)
                                          || String(draggingItemState.sectionId || '') !== String(section.id)
                                        ) {
                                          return;
                                        }
                                        event.preventDefault();
                                        await reorderItemsWithinSectionByDrop(
                                          section.id,
                                          draggingItemState.itemId,
                                          item.id,
                                          resolveDropPosition(event)
                                        );
                                        setDraggingItemState({ sectionId: null, itemId: null });
                                        setItemDropIndicator({ sectionId: null, itemId: null, position: 'before' });
                                      }}
                                    >
                                      <td className="py-2 pr-3 align-top">
                                        <button
                                          type="button"
                                          className="cursor-grab rounded border border-gray-700 bg-gray-800 px-2 py-1 text-[11px] uppercase tracking-[0.08em] text-gray-300 active:cursor-grabbing disabled:cursor-not-allowed disabled:text-gray-500"
                                          draggable={!isTranslationMode && !isMoving}
                                          onDragStart={() => setDraggingItemState({ sectionId: String(section.id), itemId: String(item.id) })}
                                          onDragEnd={() => {
                                            setDraggingItemState({ sectionId: null, itemId: null });
                                            setItemDropIndicator({ sectionId: null, itemId: null, position: 'before' });
                                          }}
                                          disabled={isTranslationMode || isMoving}
                                          title={isTranslationMode ? 'Switch to English to reorder items.' : 'Drag to reorder within this section.'}
                                        >
                                          Drag
                                        </button>
                                      </td>
                                      <td className="py-2 pr-3 align-top">
                                        <input
                                          type="checkbox"
                                          className="accent-indigo-500"
                                          checked={selected}
                                          disabled={isTranslationMode}
                                          onChange={() => toggleItemSelection(item.id)}
                                          aria-label={`Select ${displayName || 'item'}`}
                                        />
                                      </td>
                                      <td className="py-2 pr-3">
                                        <div className="font-semibold">{displayName}</div>
                                        {!!displaySubtitle && <div className="text-xs text-gray-400">{displaySubtitle}</div>}
                                        {!isWidget && <MenuItemTagBadges tags={item.tags} spiciness={item.spiciness} language={activeLanguage} />}
                                        {missingTranslation && (
                                          <div className="text-[11px] text-yellow-300">Missing translation (showing English)</div>
                                        )}
                                      </td>
                                    <td className="py-2 pr-3 text-gray-200">
                                      {isWidget ? <span className="text-gray-400">Widget</span> : (item.priceText || <span className="text-gray-500">—</span>)}
                                    </td>
                                    <td className="py-2 pr-3 text-gray-200">
                                      {isWidget ? <span className="text-gray-500">—</span> : ((Array.isArray(item.photoAssetIds) ? item.photoAssetIds : []).length || 0)}
                                    </td>
                                    <td className="py-2 pr-3">
                                      {isWidget ? (
                                        <span className="text-gray-500">—</span>
                                      ) : (
                                        (() => {
                                          const ids = Array.isArray(item.photoAssetIds) ? item.photoAssetIds : [];
                                          if (!ids.length) return <span className="text-gray-500">—</span>;
                                          return (
                                            <PhotoThumbPager
                                              assetIds={ids}
                                              imageAssetById={imageAssetById}
                                              size={36}
                                              className="w-[152px]"
                                              titlePrefix="Photo"
                                            />
                                          );
                                        })()
                                      )}
                                    </td>
                                    <td className="py-2 pr-3">
                                      <div className="flex flex-wrap gap-2 justify-end">
                                        <button
                                          className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                          disabled={isTranslationMode || isMoving || itemIndex === 0}
                                          title={isTranslationMode ? 'Switch to English to reorder items.' : ''}
                                          onClick={() => moveItemWithinSection(section.id, item.id, -1)}
                                        >
                                          Up
                                        </button>
                                        <button
                                          className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                          disabled={isTranslationMode || isMoving || itemIndex === sectionItems.length - 1}
                                          title={isTranslationMode ? 'Switch to English to reorder items.' : ''}
                                          onClick={() => moveItemWithinSection(section.id, item.id, 1)}
                                        >
                                          Down
                                        </button>
                                        {isAdmin && isWidget && widgetInfo && (
                                          <button
                                            className="px-2 py-1 bg-indigo-600 text-xs rounded text-white"
                                            onClick={() => onEditWidget?.(widgetInfo.id)}
                                          >
                                            Edit
                                          </button>
                                        )}
                                        {!isWidget && (
                                          <button
                                            className="px-2 py-1 bg-indigo-600 text-xs rounded text-white"
                                            onClick={() => setEditor({ open: true, item })}
                                          >
                                            Edit
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="px-2 py-1 bg-red-700 text-xs rounded text-white disabled:bg-red-900 disabled:text-gray-400"
                                          disabled={isTranslationMode || isMoving}
                                          title={isTranslationMode ? 'Switch to English to delete items.' : `Delete ${displayName}`}
                                          aria-label={`Delete ${displayName}`}
                                          onClick={() => deleteItems([item.id], `Delete "${displayName}"?`)}
                                        >
                                          Delete
                                        </button>
                                        <button
                                          className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                          disabled={!menu?.landingKey}
                                          onClick={() => {
                                            const url = buildCatalogLandingUrl(catalogConfig, menu?.landingKey, { itemId: item.id, sectionId: item.sectionId });
                                            setQrPreview({ open: true, title: `Item QR: ${displayName}`, url });
                                          }}
                                        >
                                          QR
                                        </button>
                                        <button
                                          className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                                          disabled={isTranslationMode}
                                          onClick={() => toggleItemSelection(item.id)}
                                        >
                                          {selected ? 'Selected' : 'Select'}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {view === 'translations' && (
            <div className="space-y-4">
              <div className="bg-cyan-950/30 rounded p-3 border border-cyan-900/70 text-sm text-cyan-50">
                <div className="font-semibold text-white">What this page controls</div>
                <div className="mt-1 text-cyan-100/75">
                  Manage Translations sets the languages and updates section, item, product, or service translations. Socials copy for the public {catalogConfig.shortName.toLowerCase()} landing pages is now managed in {catalogConfig.shortName} Settings under Add Socials.
                </div>
                <button
                  type="button"
                  className="mt-3 rounded bg-cyan-500/20 px-3 py-1 text-xs font-semibold text-cyan-100 hover:bg-cyan-500/30"
                  onClick={() => {
                    setSocialsOpen(true);
                    setView('settings');
                  }}
                >
                  Open Add Socials
                </button>
              </div>

              <div className="bg-gray-950 rounded p-3 border border-gray-800 text-sm text-gray-200">
                Update Missing checks what is actually pending. If section headers are the only missing or outdated translations and this option is on, it will send a section-header-only refresh. Otherwise it will update the wider batch too.
              </div>

              {translationsState.error && (
                <div className="text-sm text-red-300 bg-red-900/20 border border-red-800 rounded p-3">
                  {translationsState.error}
                </div>
              )}

              <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-lg font-semibold">Languages</div>
                    <div className="text-xs text-gray-400">English is always available. Enable the languages you want to keep fully translated.</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1 rounded bg-gray-700 text-white"
                      onClick={() => fetchTranslationsStatus({ keepJob: true })}
                      disabled={translationsState.loading}
                    >
                      Refresh
                    </button>
                    <button
                      className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                      disabled={translationsState.saving || translationsState.loading}
                      onClick={async () => {
                        const token = localStorage.getItem('token');
                        setTranslationsState((prev) => ({ ...prev, saving: true, error: '' }));
                        try {
                          const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}/translations`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({ enabledLanguages: translationsState.enabledLanguages }),
                          });
                          if (!res.ok) {
                            const body = await res.text().catch(() => '');
                            throw new Error(body || `Save failed (${res.status})`);
                          }
                          await fetchTranslationsStatus({ keepJob: true });
                          alert('Languages saved.');
                        } catch (err) {
                          console.error('Failed to save languages', err);
                          setTranslationsState((prev) => ({ ...prev, error: err.message || 'Failed to save languages.' }));
                        } finally {
                          setTranslationsState((prev) => ({ ...prev, saving: false }));
                        }
                      }}
                    >
                      {translationsState.saving ? 'Saving…' : 'Save Languages'}
                    </button>
                  </div>
                </div>

                {translationsState.loading && <div className="text-sm text-gray-300">Loading…</div>}

                {!translationsState.loading && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {(translationsState.availableLanguages || []).map((lang) => {
                      const code = String(lang.code || '').trim();
                      const enabled = translationsState.enabledLanguages.includes(code);
                      const statsRow = (translationsState.stats || []).find((s) => String(s.code) === code) || null;
                      const disabled = code === 'en';
                      return (
                        <label key={code} className="flex items-center justify-between gap-3 bg-gray-900/60 border border-gray-800 rounded px-3 py-2">
                          <div className="min-w-0">
                            <div className="text-sm text-gray-100 font-semibold truncate">{lang.label || code}</div>
                            <div className="text-xs text-gray-400 truncate">{code}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            {statsRow && code !== 'en' && (
                              <div className="text-xs text-gray-300">
                                <div>
                                  Pending: {statsRow.pendingSectionHeaders || 0} hdr, {statsRow.pendingSections || 0} sec, {statsRow.pendingItems || 0} items
                                </div>
                                {((statsRow.outdatedManualSectionHeaders || 0) + (statsRow.outdatedManualSections || 0) + (statsRow.outdatedManualItems || 0)) > 0 && (
                                  <div className="text-gray-400">
                                    Manual stale: {statsRow.outdatedManualSectionHeaders || 0} hdr, {statsRow.outdatedManualSections || 0} sec, {statsRow.outdatedManualItems || 0} items
                                  </div>
                                )}
                              </div>
                            )}
                            <input
                              type="checkbox"
                              className="accent-indigo-500"
                              checked={enabled}
                              disabled={disabled}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setTranslationsState((prev) => {
                                  const current = Array.isArray(prev.enabledLanguages) ? prev.enabledLanguages : ['en'];
                                  const next = new Set(current);
                                  next.add('en');
                                  if (checked) next.add(code);
                                  else if (code !== 'en') next.delete(code);
                                  return { ...prev, enabledLanguages: Array.from(next.values()).sort(compareLanguageCodesForDisplay) };
                                });
                              }}
                            />
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="text-lg font-semibold mb-3">Translation Options</div>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 rounded border border-gray-800 bg-gray-900/60 px-3 py-3 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-indigo-500"
                      checked={translationOptions.maintainTitles}
                      onChange={(e) => setTranslationOptions((prev) => ({
                        ...prev,
                        maintainTitles: e.target.checked,
                      }))}
                    />
                    <span>
                      <span className="block font-semibold text-white">Maintain titles when translating</span>
                      <span className="mt-1 block text-xs text-gray-400">
                        Keeps item, product, and service names in the original language while translating subtitles and descriptions.
                      </span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 rounded border border-gray-800 bg-gray-900/60 px-3 py-3 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-indigo-500"
                      checked={translationOptions.translateSectionHeaders}
                      onChange={(e) => setTranslationOptions((prev) => ({
                        ...prev,
                        translateSectionHeaders: e.target.checked,
                      }))}
                    />
                    <span>
                      <span className="block font-semibold text-white">Translate section headers</span>
                      <span className="mt-1 block text-xs text-gray-400">
                        On by default. If section headers are the only pending translations, Update Missing will send a section-header-only batch. Otherwise they are added to the normal section and item translation run.
                      </span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 rounded border border-gray-800 bg-gray-900/60 px-3 py-3 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-indigo-500"
                      checked={translationOptions.includeManualEdits}
                      onChange={(e) => setTranslationOptions((prev) => ({
                        ...prev,
                        includeManualEdits: e.target.checked,
                      }))}
                    />
                    <span>
                      <span className="block font-semibold text-white">Overwrite manually edited translations</span>
                      <span className="mt-1 block text-xs text-gray-400">
                        Off by default. When enabled, the batch run will also refresh translations that were edited by hand after the English source changed.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="bg-gray-950 rounded p-4 border border-gray-800">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold">Update Missing / Outdated Translations</div>
                    <div className="text-xs text-gray-400">
                      Refreshes any missing or outdated translated section headers, section names, and ${itemLabelPlural.toLowerCase()} for the enabled languages, based on the options above. When only headers are pending, the system automatically uses the header-only route.
                    </div>
                  </div>
                  <button
                    className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                    disabled={translationsState.starting || Boolean(translationsState.runningJobId)}
                    onClick={async () => {
                      const token = localStorage.getItem('token');
                      setTranslationsState((prev) => ({ ...prev, starting: true, error: '' }));
                      try {
                        const previewRes = await fetch(`${catalogConfig.apiBase}/area/${areaId}/translations/update-missing`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({
                            confirm: false,
                            maintainTitles: translationOptions.maintainTitles,
                            translateSectionHeaders: translationOptions.translateSectionHeaders,
                            includeManualEdits: translationOptions.includeManualEdits,
                          }),
                        });
                        const preview = await previewRes.json().catch(() => ({}));
                        if (!previewRes.ok) {
                          throw new Error(preview?.error || `Start failed (${previewRes.status})`);
                        }
                        if (!preview?.needsConfirm) {
                          alert(preview?.message || 'No translations needed.');
                          await fetchTranslationsStatus({ keepJob: true });
                          return;
                        }

                        const estimate = preview.estimate || null;
                        if (estimate?.requestEstimate) {
                          const ok = window.confirm(
                            preview?.sectionHeadersOnly
                              ? `This will run ~${estimate.requestEstimate} translation requests to update any missing or outdated section headers selected by the options above.\n\nContinue?`
                              : `This will run ~${estimate.requestEstimate} translation requests to update any missing or outdated section headers, section names, and ${itemLabelPlural.toLowerCase()} selected by the options above.\n\nContinue?`
                          );
                          if (!ok) return;
                        }

                        const res = await fetch(`${catalogConfig.apiBase}/area/${areaId}/translations/update-missing`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({
                            confirm: true,
                            maintainTitles: translationOptions.maintainTitles,
                            translateSectionHeaders: translationOptions.translateSectionHeaders,
                            includeManualEdits: translationOptions.includeManualEdits,
                          }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) {
                          throw new Error(data?.error || `Start failed (${res.status})`);
                        }
                        if (!data?.jobId) {
                          alert(data?.message || 'No translations needed.');
                          await fetchTranslationsStatus({ keepJob: true });
                          return;
                        }

                        setTranslationsState((prev) => ({
                          ...prev,
                          jobId: data.jobId,
                          runningJobId: data.jobId,
                          estimate: data.estimate || estimate,
                        }));
                        await fetchTranslationsStatus({ keepJob: true });
                      } catch (err) {
                        console.error('Failed to start translation job', err);
                        setTranslationsState((prev) => ({ ...prev, error: err.message || 'Failed to start translation job.' }));
                      } finally {
                        setTranslationsState((prev) => ({ ...prev, starting: false }));
                      }
                    }}
                  >
                    {translationsState.runningJobId ? 'Running…' : translationsState.starting ? 'Starting…' : 'Update Missing'}
                  </button>
                </div>

                {!!translationsState.runningJobId && (
                  <div className="mt-3 text-sm text-gray-200">
                    <div className="text-xs text-gray-400">Job: {translationsState.runningJobId}</div>
                  </div>
                )}

                {!!translationsState.job && (
                  <div className="mt-3 bg-gray-900/60 border border-gray-800 rounded p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold">
                        Status: <span className="text-gray-100">{translationsState.job.status}</span>
                      </div>
                      <button
                        className="px-3 py-1 rounded bg-gray-700 text-white"
                        onClick={() => fetchTranslationJob(translationsState.job.id)}
                      >
                        Refresh Job
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-gray-300">
                      Current language: {translationsState.job.currentLanguage || '—'}
                    </div>
                    <div className="mt-2 text-xs text-gray-300">
                      Requests: {translationsState.job.progress?.completedRequests || 0} / {translationsState.job.progress?.requestEstimate || 0}
                    </div>
                    <div className="mt-2 text-xs text-gray-300">
                      Languages done: {translationsState.job.progress?.completedLanguages || 0} / {(translationsState.job.summary?.perLanguage || []).length || 0}
                    </div>
                    <div className="mt-2 text-xs text-gray-300">
                      Saved: {translationsState.job.summary?.translatedSectionHeaders || 0} headers, {translationsState.job.summary?.translatedSections || 0} sections, {translationsState.job.summary?.translatedItems || 0} items
                    </div>
                    <div className="mt-2 text-xs text-gray-300">
                      Options: {translationsState.job.summary?.sectionHeadersOnly
                        ? 'section headers only'
                        : `titles ${translationsState.job.summary?.maintainTitles === false ? 'translated' : 'maintained'}, section headers ${translationsState.job.summary?.translateSectionHeaders ? 'included' : 'skipped'}, manual edits ${translationsState.job.summary?.includeManualEdits ? 'overwritten' : 'preserved'}`}
                    </div>
                    {translationsState.job.error && (
                      <div className="mt-2 text-xs text-red-300">{translationsState.job.error}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {logoPickerOpen && (
        <LogoPickerModal
          siteId={siteId}
          initialSelectedId={logoCreativeIdDraft || defaults?.logoCreativeId || null}
          onClose={() => setLogoPickerOpen(false)}
          onConfirm={(selectedId) => {
            setLogoCreativeIdDraft(selectedId);
            setSettingsDirty(true);
            setLogoPickerOpen(false);
          }}
        />
      )}

      {editor.open && (
        <MenuItemEditorModal
          siteId={siteId}
          apiBase={catalogConfig.apiBase}
          itemLabel={itemLabel}
          supportsItemTags={isMenuCatalog}
          availableTagKeys={menuFeaturesDraft.enabledTagKeys}
          assets={assets}
          sections={enabledSections}
          getSectionLabel={getSectionDisplayName}
          initialItem={editor.item}
          translation={isTranslationMode && editor.item?.id ? itemTranslationMap.get(String(editor.item.id)) : null}
          language={activeLanguage}
          languageLabel={activeLanguageLabel}
          onClose={() => setEditor({ open: false, item: null })}
          onSave={async (draft) => {
            try {
              await saveItem(draft);
              alert('Saved.');
            } catch (err) {
              console.error('Failed to save item', err);
              alert(err.message || 'Failed to save item.');
            }
          }}
          onSaveTranslation={async (draft) => {
            try {
              await saveItemTranslation(draft);
              alert('Saved.');
            } catch (err) {
              console.error('Failed to save translation', err);
              alert(err.message || 'Failed to save translation.');
            }
          }}
          onRefreshAssets={onRefreshAssets}
        />
      )}

      {isAdmin && widgetPicker.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closeWidgetPicker}
        >
          <div
            className="w-full max-w-3xl rounded-lg border border-gray-800 bg-gray-950 p-4 text-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div>
                <div className="text-lg font-semibold">Select Widget</div>
                <div className="text-xs text-gray-400">Add a widget to this section.</div>
              </div>
              <button className="text-gray-400 hover:text-white text-xl" onClick={closeWidgetPicker}>
                ×
              </button>
            </div>

            <div className="flex flex-wrap gap-3 mb-4">
              <input
                className="flex-1 min-w-[200px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                placeholder="Search widgets"
                value={widgetFilter.q}
                onChange={(e) => setWidgetFilter((prev) => ({ ...prev, q: e.target.value }))}
              />
              <select
                className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={widgetFilter.status}
                onChange={(e) => setWidgetFilter((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">All statuses</option>
                <option value="draft">draft</option>
                <option value="active">active</option>
                <option value="archived">archived</option>
              </select>
              <select
                className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                value={widgetFilter.partner}
                onChange={(e) => setWidgetFilter((prev) => ({ ...prev, partner: e.target.value }))}
              >
                <option value="all">All partners</option>
                <option value="GETYOURGUIDE">GetYourGuide</option>
                <option value="TRAVELPAYOUTS">Travelpayouts</option>
              </select>
              <button
                className="px-3 py-2 rounded bg-gray-800 text-white text-sm"
                onClick={loadWidgets}
                disabled={widgetsLoading}
              >
                {widgetsLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {widgetTagSuggestions.length > 0 && (
              <div className="mb-4">
                <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Filter Tags</div>
                <TagFilter
                  value={widgetFilter.tags}
                  suggestions={widgetTagSuggestions}
                  onChange={(tags) => setWidgetFilter((prev) => ({ ...prev, tags }))}
                />
              </div>
            )}

            {widgetsError && (
              <div className="text-sm text-red-300 mb-3">{widgetsError}</div>
            )}

            {widgetsLoading && <div className="text-gray-400">Loading widgets...</div>}
            {!widgetsLoading && filteredWidgets.length === 0 && (
              <div className="text-gray-400">No widgets found.</div>
            )}

            <div className="space-y-3 max-h-[45vh] overflow-auto pr-1">
              {filteredWidgets.map((widget) => {
                const isSelected = String(widget.id) === String(selectedWidgetId || '');
                return (
                  <div
                    key={widget.id}
                    className={`w-full text-left rounded border px-3 py-2 ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-950/40'
                        : 'border-gray-800 bg-gray-950/60 hover:bg-gray-800/60'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => setSelectedWidgetId(widget.id)}
                      >
                        <div className="text-sm text-gray-400">#{widget.id}</div>
                        <div className="text-base font-semibold">{widget.title}</div>
                        <div className="text-xs text-gray-400">
                          {widget.partner}
                          {widget.widget_type ? ` · ${widget.widget_type}` : ''}
                        </div>
                        <div className="text-xs text-gray-400">{widget.status}</div>
                      </button>
                      <div className="flex items-center gap-2">
                        {isSelected && (
                          <span className="text-xs text-indigo-300">Selected</span>
                        )}
                        <button
                          type="button"
                          className="rounded bg-indigo-700 px-2 py-1 text-xs text-white"
                          onClick={() => onEditWidget?.(widget.id)}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                    {Array.isArray(widget.tags) && widget.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {widget.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 rounded-full bg-gray-800 text-xs text-gray-200"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                className="px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                onClick={addWidgetToSection}
                disabled={!selectedWidgetId}
              >
                Add Widget
              </button>
              <button
                className="px-3 py-2 rounded bg-gray-700 text-white"
                onClick={closeWidgetPicker}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {sectionTranslationEditor.open && sectionTranslationEditor.section && (
        <SectionTranslationModal
          title="Edit Section Translation"
          entityLabel="Section"
          englishName={sectionTranslationEditor.section.name || ''}
          languageLabel={activeLanguageLabel}
          initialName={sectionTranslationMap.get(String(sectionTranslationEditor.section.id))?.name || ''}
          onClose={() => setSectionTranslationEditor({ open: false, section: null })}
          onSave={async (name) => {
            try {
              await saveSectionTranslation({ sectionId: sectionTranslationEditor.section.id, name });
              alert('Saved.');
              setSectionTranslationEditor({ open: false, section: null });
            } catch (err) {
              console.error('Failed to save section translation', err);
              throw err;
            }
          }}
        />
      )}

      {sectionHeaderTranslationEditor.open && sectionHeaderTranslationEditor.sectionHeader && (
        <SectionTranslationModal
          title="Edit Section Header Translation"
          entityLabel="Section Header"
          englishName={sectionHeaderTranslationEditor.sectionHeader.name || ''}
          languageLabel={activeLanguageLabel}
          initialName={sectionHeaderTranslationMap.get(String(sectionHeaderTranslationEditor.sectionHeader.id))?.name || ''}
          onClose={() => setSectionHeaderTranslationEditor({ open: false, sectionHeader: null })}
          onSave={async (name) => {
            try {
              await saveSectionHeaderTranslation({ sectionHeaderId: sectionHeaderTranslationEditor.sectionHeader.id, name });
              alert('Saved.');
              setSectionHeaderTranslationEditor({ open: false, sectionHeader: null });
            } catch (err) {
              console.error('Failed to save section header translation', err);
              throw err;
            }
          }}
        />
      )}

      {qrPreview.open && (
        <QrPreviewModal
          title={qrPreview.title}
          url={qrPreview.url}
          onClose={() => setQrPreview({ open: false, title: '', url: '' })}
        />
      )}

      {bulkQrPreview.open && (
        <BulkQrPreviewModal
          title={bulkQrPreview.title}
          entries={bulkQrPreview.entries}
          onClose={() => setBulkQrPreview({ open: false, title: '', entries: [] })}
        />
      )}

      <MoveCatalogEntriesModal
        open={moveModal.open}
        mode={moveModal.mode}
        sourceType={catalogConfig.catalogType}
        areaId={areaId}
        itemCount={selectedItemIds.length}
        sectionCount={selectedSectionKeys.length}
        onClose={() => setMoveModal({ open: false, mode: 'items' })}
        onConfirm={moveSelected}
      />
    </div>
  );
}
